<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="official-docs-page">
    <section class="od-hero">
      <div class="od-hero__top">
        <div class="od-hero__identity">
          <span class="od-hero__icon" aria-hidden="true"><v-icon size="25">mdi-bank-outline</v-icon></span>
          <div>
            <span class="od-eyebrow">Generator i baza urzędów</span>
            <h1>Dokumenty urzędowe</h1>
            <p>Przygotuj komplet z danymi z systemu i udostępnij go kierowcy lub właścicielowi bez przesyłania plików ręcznie.</p>
          </div>
        </div>
        <button type="button" class="od-button od-button--ghost" :disabled="catalogLoading || operationBusy" @click="loadOfficialCatalog">
          <v-icon size="18" :class="{ 'is-spinning': catalogLoading }">mdi-refresh</v-icon>
          {{ catalogLoading ? 'Odświeżam…' : 'Odśwież katalog' }}
        </button>
      </div>

      <div v-if="catalogError" class="od-catalog-alert">
        <v-icon size="18">mdi-cloud-alert-outline</v-icon>
        <span><strong>Katalog formularzy jest niedostępny</strong><small>{{ catalogError }} Generowanie druków miejskich jest zablokowane, żeby nie użyć starej wersji.</small></span>
        <button type="button" @click="loadOfficialCatalog">Spróbuj ponownie</button>
      </div>
    </section>

    <nav class="od-workspace-tabs" aria-label="Obszary dokumentów urzędowych">
      <button
        type="button"
        aria-controls="od-workspace-generator"
        :aria-pressed="activeWorkspace === 'generator'"
        :class="{ 'is-active': activeWorkspace === 'generator' }"
        @click="setWorkspace('generator')"
      >
        <span><v-icon size="20">mdi-file-document-edit-outline</v-icon></span>
        <span class="od-workspace-tab__copy"><strong>Utwórz dokumenty</strong><small>Krok po kroku: miasto → sprawa → dane</small></span>
        <em>{{ wizardStep === 0 ? '—' : wizardStep === 5 ? 'gotowe' : 'krok ' + wizardStep + '/4' }}</em>
      </button>
      <button
        type="button"
        aria-controls="od-workspace-history"
        :aria-pressed="activeWorkspace === 'history'"
        :class="{ 'is-active': activeWorkspace === 'history' }"
        @click="setWorkspace('history')"
      >
        <span><v-icon size="20">mdi-history</v-icon></span>
        <span class="od-workspace-tab__copy"><strong>Historia</strong><small>Wygenerowane dokumenty</small></span>
        <em>{{ docsLoading ? '—' : docs.length }}</em>
      </button>
      <button
        type="button"
        aria-controls="od-workspace-registry"
        :aria-pressed="activeWorkspace === 'registry'"
        :class="{ 'is-active': activeWorkspace === 'registry' }"
        @click="setWorkspace('registry')"
      >
        <span><v-icon size="20">mdi-city-variant-outline</v-icon></span>
        <span class="od-workspace-tab__copy"><strong>Baza urzędów</strong><small>Miasta, BIP i druki</small></span>
        <em>{{ cityRegistry.length }}</em>
      </button>
    </nav>

    <div v-show="activeWorkspace === 'generator'" id="od-workspace-generator" class="odw">
      <!-- EKRAN STARTOWY -->
      <section v-if="wizardStep === 0" class="od-panel odw-start">
        <span class="odw-start__icon"><v-icon size="34">mdi-file-document-multiple-outline</v-icon></span>
        <h2>Przygotuj dokumenty do urzędu</h2>
        <p>Cztery proste kroki: wybierasz miasto, wskazujesz sprawę, wpisujesz kierowcę lub auto i generujesz gotowe druki. Na końcu możesz dodać pełnomocnictwo, wysłać komplet e-mailem albo wydrukować.</p>
        <div class="odw-start__actions">
          <button type="button" class="od-button od-button--primary odw-start__cta" @click="startWizard">
            <v-icon size="20">mdi-plus-circle-outline</v-icon>Utwórz dokumenty
          </button>
          <button type="button" class="od-button od-button--ghost" @click="setWorkspace('history')">
            <v-icon size="18">mdi-history</v-icon>Zobacz wygenerowane
          </button>
        </div>
      </section>

      <template v-else>
        <!-- PASEK KROKÓW -->
        <nav class="odw-progress" aria-label="Kroki kreatora">
          <button
            v-for="step in wizardSteps"
            :key="step.value"
            type="button"
            :disabled="operationBusy || wizardStep === 5 || step.value > wizardMaxStep"
            :aria-current="wizardStep === step.value ? 'step' : null"
            :class="{ 'is-active': wizardStep === step.value, 'is-done': wizardStep > step.value }"
            @click="goWizardStep(step.value)"
          >
            <span>{{ step.value }}</span>{{ step.label }}
          </button>
          <button type="button" class="odw-progress__cancel" :disabled="operationBusy" @click="resetWizard(0)"><v-icon size="16">mdi-close</v-icon>Anuluj</button>
        </nav>

        <!-- KROK 1: MIASTO -->
        <section v-if="wizardStep === 1" class="od-panel odw-step">
          <header class="odw-step__head">
            <h2>Wybierz miasto</h2>
            <p>Urząd, adres i dostępne druki ustawią się automatycznie.</p>
          </header>
          <v-text-field v-model="wizardCitySearch" placeholder="Szukaj miasta…" prepend-inner-icon="mdi-magnify" clearable outlined dense hide-details class="od-control odw-city-search" />
          <div class="odw-city-grid">
            <button
              v-for="c in wizardCityCards"
              :key="c.city"
              type="button"
              :class="{ 'is-active': !wizardManualCity && selectedRegistryCity === c.city }"
              @click="selectWizardCity(c.city)"
            >
              <span>{{ cityInitials(c.city) }}</span>
              <strong>{{ c.city }}</strong>
              <small>{{ c.office }}</small>
              <em v-if="(c.procedures || []).length || (c.cityTemplates || []).length"><v-icon size="13">mdi-auto-fix</v-icon>druki wypełniane automatycznie</em>
            </button>
          </div>
          <p v-if="!wizardCityCards.length" class="odw-help" role="status">Nie znaleziono miasta. Zmień wyszukiwanie lub wpisz urząd ręcznie poniżej.</p>
          <div class="odw-manual">
            <button type="button" class="odw-manual__toggle" @click="wizardManualCity = !wizardManualCity">
              <v-icon size="16">{{ wizardManualCity ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              Innego miasta nie ma na liście? Wpisz urząd ręcznie
            </button>
            <div v-if="wizardManualCity" class="od-form-grid od-form-grid--office odw-manual__grid">
              <div class="od-field-block od-field-block--wide"><label class="od-field-label">Nazwa urzędu <b>*</b></label><v-text-field v-model="form.officeName" placeholder="Urząd miasta — właściwy wydział" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Miasto <b>*</b></label><v-text-field v-model="form.officeCity" placeholder="Miasto" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Adres urzędu</label><v-text-field v-model="form.officeAddress" placeholder="Ulica i numer" outlined dense hide-details class="od-control" /></div>
              <div class="odw-manual__go">
                <button type="button" class="od-button od-button--primary" :disabled="!builderStepTwoReady" @click="confirmManualOffice">Dalej<v-icon size="17">mdi-arrow-right</v-icon></button>
              </div>
            </div>
          </div>
        </section>

        <!-- KROK 2: SPRAWA -->
        <section v-if="wizardStep === 2" class="od-panel odw-step">
          <header class="odw-step__head">
            <h2>Co chcesz zrobić?</h2>
            <p>{{ form.officeCity }} · {{ form.officeName }}</p>
          </header>
          <div class="odw-intent-grid">
            <button
              v-for="intent in wizardIntentOptions"
              :key="intent.value"
              type="button"
              :class="{ 'is-active': wizardIntent === intent.value }"
              @click="selectWizardIntent(intent.value)"
            >
              <span :class="`is-${intent.tone}`"><v-icon size="24">{{ intent.icon }}</v-icon></span>
              <strong>{{ intent.text }}</strong>
              <small>{{ intent.description }}</small>
            </button>
          </div>
          <div class="odw-step__nav">
            <button type="button" class="od-button od-button--ghost" @click="goWizardStep(1)"><v-icon size="17">mdi-arrow-left</v-icon>Wstecz</button>
          </div>
        </section>

        <!-- KROK 3: DANE -->
        <section v-if="wizardStep === 3" class="od-panel odw-step">
          <header class="odw-step__head">
            <h2>{{ wizardIntentLabel }}</h2>
            <p>{{ form.officeCity }} — pola z gwiazdką są wymagane. Pozostałe dane uzupełnij zgodnie z drukiem urzędu.</p>
          </header>

          <div v-if="wizardIntent !== 'NEW_LICENSE'" class="od-form-grid od-form-grid--office odw-block">
            <div class="od-field-block"><label class="od-field-label">Numer licencji TAXI <b>*</b></label><v-text-field v-model="form.licenseNumber" placeholder="np. 1234/2026" prepend-inner-icon="mdi-card-account-details-outline" outlined dense hide-details class="od-control" /><p v-if="licenseHint" class="od-license-hint" :class="licenseHintClass">{{ licenseHint }}</p></div>
            <!-- Wypisy wydaje się do pojazdów — przy zgłaszaniu samych kierowców to pole tylko myli. -->
            <div v-if="wizardNeedsVehicle && !isRemoval" class="od-field-block"><label class="od-field-label">Liczba wypisów</label><v-text-field v-model="form.copiesCount" type="number" min="1" outlined dense hide-details class="od-control" /></div>
          </div>
          <div v-else class="od-form-grid od-form-grid--office odw-block">
            <div class="od-field-block"><label class="od-field-label">Obszar licencji <b>*</b></label><v-text-field v-model="form.licenseArea" placeholder="np. Gmina - miasto Grudziądz" outlined dense hide-details class="od-control" /></div>
            <div class="od-field-block"><label class="od-field-label">Okres licencji</label><v-select v-model="form.licensePeriod" :items="licensePeriodOptions" outlined dense hide-details class="od-control" /></div>
            <div v-if="needsLicenseYears" class="od-field-block"><label class="od-field-label">Liczba lat licencji <b>*</b></label><v-text-field v-model="form.licenseYears" type="number" min="2" max="50" placeholder="np. 50" outlined dense hide-details class="od-control" /><span class="od-field-help">Ten urząd wpisuje liczbę lat (2–50), nie przedział.</span></div>
            <div class="od-field-block"><label class="od-field-label">Liczba wypisów</label><v-text-field v-model="form.copiesCount" type="number" min="1" outlined dense hide-details class="od-control" /></div>
          </div>

          <div v-if="!cityTemplates.length && !isRemoval" class="odw-help" role="note">Dla tej sprawy powstanie podanie uniwersalne. Sprawdź w bazie urzędów lub BIP, czy urząd wymaga własnego formularza.</div>
          <div v-if="isRemoval" class="odw-help" role="note">Wpisz dane kierowców lub pojazdów, które chcesz wykreślić. Powstanie wniosek i wykaz wskazanych osób lub pojazdów do wykreślenia. Wykaz ma układ uniwersalny — oryginalne załączniki urzędu są dostępne w bazie urzędów lub BIP.</div>
          <div v-if="isLicenseChange || isRemoval" class="od-field-block">
            <label class="od-field-label">Opis zmiany <b>*</b></label>
            <v-textarea v-model="form.changeDescription" placeholder="Opisz zmianę; przy wykreśleniu podaj imię i nazwisko lub numer rejestracyjny." rows="3" auto-grow outlined hide-details class="od-control" />
          </div>
          <div v-if="isRemoval" class="od-form-grid">
            <div v-if="wizardIntent === 'REMOVE_DRIVER'" class="od-field-block"><label class="od-field-label">Kierowców pozostających po zmianie <b>*</b></label><v-text-field v-model="form.driversTotalAfterChange" type="number" min="0" outlined dense hide-details class="od-control" /></div>
            <div v-if="wizardIntent === 'REMOVE_VEHICLE'" class="od-field-block"><label class="od-field-label">Pojazdów pozostających po zmianie <b>*</b></label><v-text-field v-model="form.vehiclesTotalAfterChange" type="number" min="0" outlined dense hide-details class="od-control" /></div>
          </div>
          <!-- KIEROWCA -->
          <div v-if="showDriverFields" class="odw-block odw-block--framed">
            <header class="odw-block__head"><span class="is-blue"><v-icon size="19">mdi-card-account-details-outline</v-icon></span><div><strong>{{ isRemoval ? 'Kierowcy do wykreślenia' : 'Kierowca' }}</strong><small>Wybierz osobę z systemu — imię i nazwisko wpisze się samo, a komplet pojawi się też w jej aplikacji BTP.</small></div></header>
            <div class="od-field-block">
              <label class="od-field-label">Kierowca z systemu</label>
              <v-autocomplete
                v-model="form.person"
                :items="personOptions"
                :loading="personLoading"
                :search-input.sync="personSearch"
                item-text="text"
                return-object
                placeholder="Wpisz co najmniej 2 znaki nazwiska"
                prepend-inner-icon="mdi-account-search-outline"
                no-data-text="Wpisz nazwisko, aby wyszukać osobę"
                outlined dense no-filter clearable hide-details
                class="od-control"
              />
            </div>
            <p v-if="personSearchError" class="odw-help" role="alert">{{ personSearchError }}</p>
            <div v-if="form.person" class="od-form-grid od-form-grid--reputation">
              <div class="od-field-block od-field-block--wide"><label class="od-field-label">Numer prawa jazdy</label><v-text-field v-model="form.driverLicenseNumber" outlined dense hide-details class="od-control" /></div>
              <details class="od-driver-details od-field-block--full"><summary>Dane dodatkowe kierowcy — PESEL, badania, terminy</summary><div class="od-form-grid od-form-grid--reputation">
              <div class="od-field-block"><label class="od-field-label">PESEL kierowcy</label><v-text-field v-model="form.driverPesel" maxlength="11" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Data urodzenia</label><v-text-field v-model="form.driverBirthDate" type="date" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Prawo jazdy ważne do</label><v-text-field v-model="form.driverLicenseValidTo" type="date" :disabled="form.driverLicenseIndefinite" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block od-field-block--checkbox"><v-checkbox v-model="form.driverLicenseIndefinite" dense hide-details label="Prawo jazdy bezterminowe" class="mt-0 pt-0" @change="form.driverLicenseIndefinite && (form.driverLicenseValidTo = '')" /></div>
              <div class="od-field-block"><label class="od-field-label">Badania lekarskie do</label><v-text-field v-model="form.driverMedicalValidTo" type="date" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Nr orzeczenia lekarskiego</label><v-text-field v-model="form.driverMedicalCertificateNumber" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Psychologiczne do</label><v-text-field v-model="form.driverPsychologicalValidTo" type="date" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Nr orzeczenia psychologicznego</label><v-text-field v-model="form.driverPsychologicalCertificateNumber" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Data wydania KRK</label><v-text-field v-model="form.driverCriminalRecordDate" type="date" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block od-field-block--wide"><label class="od-field-label">Pochodzenie kierowcy</label><v-select v-model="form.driverIsEuCitizen" :items="driverOriginOptions" outlined dense hide-details class="od-control" /></div>
              </div></details>
            </div>
            <div class="od-repeat-section">
              <header>
                <div><strong>{{ form.person ? 'Dodatkowi kierowcy' : 'Kierowcy wpisywani ręcznie' }}</strong><small>Limit wykazu tego urzędu: {{ driverCapacity }}.</small></div>
                <button type="button" class="od-repeat-add" :disabled="additionalDrivers.length >= driverCapacity - (form.person ? 1 : 0)" @click="addDriverRow"><v-icon size="15">mdi-plus</v-icon>Dodaj kierowcę</button>
              </header>
              <div v-for="(driver, index) in additionalDrivers" :key="'driver-' + index" class="od-repeat-card">
                <div class="od-repeat-card__title"><strong>Kierowca {{ index + (form.person ? 2 : 1) }}</strong><button type="button" @click="removeDriverRow(index)"><v-icon size="15">mdi-delete-outline</v-icon>Usuń</button></div>
                <div class="od-additional-vehicle-source">
                  <v-btn-toggle v-model="driver.source" mandatory dense class="od-vehicle-source-toggle">
                    <v-btn value="FLEET"><v-icon left size="16">mdi-account-search-outline</v-icon>Z systemu</v-btn>
                    <v-btn value="MANUAL"><v-icon left size="16">mdi-pencil-outline</v-icon>Ręcznie</v-btn>
                  </v-btn-toggle>
                  <v-autocomplete
                    v-if="driver.source === 'FLEET'"
                    v-model="driver.driverId"
                    :items="driver.options"
                    :loading="driver.loading"
                    :search-input.sync="driver.search"
                    item-text="text"
                    item-value="id"
                    placeholder="Wpisz co najmniej 2 znaki nazwiska"
                    prepend-inner-icon="mdi-account-search-outline"
                    no-data-text="Wpisz nazwisko, aby wyszukać osobę"
                    outlined dense hide-details clearable no-filter
                    class="od-control"
                    @update:search-input="searchDriverRow(driver, $event)"
                    @change="applyFleetDriver($event, driver)"
                  />
                </div>
                <div class="od-form-grid od-form-grid--reputation">
                  <div class="od-field-block od-field-block--wide"><label class="od-field-label">Imię i nazwisko <b>*</b></label><v-text-field v-model="driver.name" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block od-field-block--wide"><label class="od-field-label">Numer prawa jazdy</label><v-text-field v-model="driver.drivingLicenseNumber" outlined dense hide-details class="od-control" /></div>
                  <details class="od-driver-details od-field-block--full"><summary>Dane dodatkowe kierowcy — PESEL, badania, terminy</summary><div class="od-form-grid od-form-grid--reputation">
                  <div class="od-field-block"><label class="od-field-label">PESEL</label><v-text-field v-model="driver.pesel" maxlength="11" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Data urodzenia</label><v-text-field v-model="driver.birthDate" type="date" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Prawo jazdy ważne do</label><v-text-field v-model="driver.drivingLicenseValidTo" type="date" :disabled="driver.drivingLicenseIndefinite" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block od-field-block--checkbox"><v-checkbox v-model="driver.drivingLicenseIndefinite" dense hide-details label="Prawo jazdy bezterminowe" class="mt-0 pt-0" @change="driver.drivingLicenseIndefinite && (driver.drivingLicenseValidTo = '')" /></div>
                  <div class="od-field-block"><label class="od-field-label">Badania lekarskie do</label><v-text-field v-model="driver.medicalValidTo" type="date" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Nr orzeczenia lekarskiego</label><v-text-field v-model="driver.medicalCertificateNumber" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Psychologiczne do</label><v-text-field v-model="driver.psychologicalValidTo" type="date" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Nr orzeczenia psychologicznego</label><v-text-field v-model="driver.psychologicalCertificateNumber" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Data wydania KRK</label><v-text-field v-model="driver.criminalRecordDate" type="date" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block od-field-block--wide"><label class="od-field-label">Pochodzenie kierowcy</label><v-select v-model="driver.isEuCitizen" :items="driverOriginOptions" outlined dense hide-details class="od-control" /></div>
                  </div></details>
                </div>
              </div>
            </div>
          </div>

          <!-- AUTO -->
          <div v-if="showVehicleFields" class="odw-block odw-block--framed">
            <header class="odw-block__head"><span class="is-green"><v-icon size="19">mdi-car-info</v-icon></span><div><strong>{{ isRemoval ? 'Pojazdy do wykreślenia' : 'Pojazd' }}</strong><small>Dane auta trafią do wniosku i wykazu pojazdów.</small></div></header>
            <div v-if="wizardIntent === 'ADD_VEHICLE' && !isGrudziadz" class="od-change-vehicle-toggle">
              <v-switch v-model="form.vehicleIsReplacement" inset hide-details color="success" label="Zmiana pojazdu zamiast dopisania kolejnego" />
            </div>
            <div class="od-vehicle-source-box">
              <div class="od-vehicle-source-box__head">
                <strong>Skąd pobrać dane auta?</strong>
                <small>Wybierz samochód z floty albo wpisz jego dane samodzielnie.</small>
              </div>
              <v-btn-toggle v-model="form.vehicleSource" mandatory dense class="od-vehicle-source-toggle">
                <v-btn value="FLEET"><v-icon left size="17">mdi-car-multiple</v-icon>Nasza flota</v-btn>
                <v-btn value="DRIVER"><v-icon left size="17">mdi-car-key</v-icon>Auto kierowcy</v-btn>
                <v-btn value="MANUAL"><v-icon left size="17">mdi-pencil-outline</v-icon>Wpisz ręcznie</v-btn>
              </v-btn-toggle>
              <v-autocomplete
                v-if="form.vehicleSource !== 'MANUAL'"
                v-model="form.fleetVehicleId"
                :items="opcjeAut(form.vehicleSource)"
                :loading="fleetVehiclesLoading"
                item-text="text"
                item-value="id"
                :label="form.vehicleSource === 'DRIVER' ? 'Wybierz auto kierowcy' : 'Wybierz auto z floty'"
                :placeholder="form.vehicleSource === 'DRIVER' ? 'Szukaj po nazwisku kierowcy, marce lub numerze' : 'Szukaj po marce, modelu lub numerze rejestracyjnym'"
                prepend-inner-icon="mdi-magnify"
                :no-data-text="form.vehicleSource === 'DRIVER' ? 'Brak pasujących aut kierowców' : 'Brak pasujących aut we flocie'"
                outlined dense hide-details clearable
                class="od-control od-vehicle-source-select"
                @change="applyFleetVehicle($event)"
              />
              <button v-if="fleetVehiclesError" type="button" class="od-inline-retry" @click="loadFleetVehicles">{{ fleetVehiclesError }} Spróbuj ponownie</button>
              <small v-else-if="form.vehicleSource !== 'MANUAL' && form.fleetVehicleId" class="od-vehicle-source-note">Dane skopiowane. Możesz je niżej uzupełnić lub poprawić dla tego wniosku.</small>
            </div>
            <div class="od-form-grid od-form-grid--vehicle">
              <div class="od-field-block"><label class="od-field-label">Marka <b>*</b></label><v-text-field v-model="form.vehicleBrand" placeholder="np. Toyota" prepend-inner-icon="mdi-car-outline" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Model</label><v-text-field v-model="form.vehicleModel" placeholder="np. Corolla" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Numer rejestracyjny <b>*</b></label><v-text-field v-model="form.vehiclePlate" placeholder="np. CB 1234A" outlined dense hide-details class="od-control od-control--uppercase" /></div>
              <div class="od-field-block od-field-block--wide"><label class="od-field-label">VIN <b>*</b></label><v-text-field v-model="form.vehicleVin" placeholder="17 znaków" maxlength="17" outlined dense hide-details class="od-control od-control--uppercase" /></div>
              <div class="od-field-block"><label class="od-field-label">Rok produkcji</label><v-text-field v-model="form.vehicleYear" type="number" placeholder="RRRR" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Liczba miejsc</label><v-text-field v-model="form.vehicleSeats" type="number" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Tytuł prawny</label><v-select v-model="form.legalTitle" :items="legalTitleOptions" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Pierwsza rejestracja</label><v-text-field v-model="form.firstRegDate" type="date" outlined dense hide-details class="od-control" /></div>
            </div>
            <div class="od-repeat-section">
              <header>
                <div><strong>Dodatkowe pojazdy</strong><small>Limit wykazu tego urzędu: {{ vehicleCapacity }}.</small></div>
                <button type="button" class="od-repeat-add" :disabled="additionalVehicles.length >= vehicleCapacity - 1" @click="addVehicleRow"><v-icon size="15">mdi-plus</v-icon>Dodaj pojazd</button>
              </header>
              <div v-for="(vehicle, index) in additionalVehicles" :key="'vehicle-' + index" class="od-repeat-card">
                <div class="od-repeat-card__title"><strong>Pojazd {{ index + 2 }}</strong><button type="button" @click="removeVehicleRow(index)"><v-icon size="15">mdi-delete-outline</v-icon>Usuń</button></div>
                <div class="od-additional-vehicle-source">
                  <v-btn-toggle v-model="vehicle.source" mandatory dense class="od-vehicle-source-toggle">
                    <v-btn value="FLEET"><v-icon left size="16">mdi-car-multiple</v-icon>Flota</v-btn>
                    <v-btn value="DRIVER"><v-icon left size="16">mdi-car-key</v-icon>Kierowcy</v-btn>
                    <v-btn value="MANUAL"><v-icon left size="16">mdi-pencil-outline</v-icon>Ręcznie</v-btn>
                  </v-btn-toggle>
                  <v-autocomplete
                    v-if="vehicle.source !== 'MANUAL'"
                    v-model="vehicle.fleetVehicleId"
                    :items="opcjeAut(vehicle.source)"
                    :loading="fleetVehiclesLoading"
                    item-text="text"
                    item-value="id"
                    :label="vehicle.source === 'DRIVER' ? 'Wybierz auto kierowcy' : 'Wybierz auto z floty'"
                    :no-data-text="vehicle.source === 'DRIVER' ? 'Brak pasujących aut kierowców' : 'Brak pasujących aut we flocie'"
                    outlined dense hide-details clearable
                    class="od-control"
                    @change="applyFleetVehicle($event, vehicle)"
                  />
                </div>
                <div class="od-form-grid od-form-grid--vehicle">
                  <div class="od-field-block"><label class="od-field-label">Marka</label><v-text-field v-model="vehicle.brand" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Model</label><v-text-field v-model="vehicle.model" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Numer rejestracyjny</label><v-text-field v-model="vehicle.plate" outlined dense hide-details class="od-control od-control--uppercase" /></div>
                  <div class="od-field-block od-field-block--wide"><label class="od-field-label">VIN</label><v-text-field v-model="vehicle.vin" maxlength="17" outlined dense hide-details class="od-control od-control--uppercase" /></div>
                  <div class="od-field-block"><label class="od-field-label">Rok produkcji</label><v-text-field v-model="vehicle.year" type="number" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Pierwsza rejestracja</label><v-text-field v-model="vehicle.firstRegDate" type="date" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Liczba miejsc</label><v-text-field v-model="vehicle.seats" type="number" outlined dense hide-details class="od-control" /></div>
                  <div class="od-field-block"><label class="od-field-label">Tytuł prawny</label><v-select v-model="vehicle.legalTitle" :items="legalTitleOptions" outlined dense hide-details class="od-control" /></div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="wizardIntent === 'NEW_LICENSE'" class="odw-block">
            <v-checkbox v-model="form.hasDrivers" label="Dołącz kierowców do nowej licencji" hide-details />
            <v-checkbox v-model="form.ownerDrives" label="Przedsiębiorca jeździ osobiście" hide-details />
          </div>

          <!-- WYMAGANIA URZĘDU -->
          <div v-if="cityTemplates.length || conditionalTemplates.length" class="odw-block odw-block--framed">
            <header class="odw-block__head"><span class="is-amber"><v-icon size="19">mdi-bank-outline</v-icon></span><div><strong>Wymagane przez ten urząd</strong><small>Druki {{ form.officeCity }} są wypełniane automatycznie — te pola muszą być uzupełnione.</small></div></header>
            <!-- Druki w komplecie: obowiązkowe zawsze, warunkowe (wykaz kierowców,
                 wykaz pojazdów…) z przełącznikiem. Do 19.08.2026 warunkowych nie
                 dało się włączyć nigdzie, więc wykazy nie trafiały do kompletu. -->
            <div class="odw-forms-list">
              <div v-for="tpl in procedureTemplates" :key="tpl.type" class="odw-forms-list__row">
                <v-checkbox
                  v-if="tpl.conditional"
                  :input-value="!!form.conditionalForms[tpl.type]"
                  dense hide-details class="mt-0 pt-0"
                  @change="setConditionalForm(tpl.type, $event)"
                />
                <v-icon v-else size="18" color="#6f8bff" class="odw-forms-list__lock">mdi-check-circle</v-icon>
                <span class="odw-forms-list__name">{{ tpl.name }}</span>
                <small v-if="!tpl.conditional" class="odw-forms-list__tag">wymagany</small>
                <small v-else-if="tpl.conditionLabel" class="odw-forms-list__tag">{{ tpl.conditionLabel }}</small>
              </div>
            </div>
            <div class="od-form-grid od-form-grid--city-extra">
              <div class="od-field-block"><label class="od-field-label">Telefon firmy</label><v-text-field v-model="form.companyPhone" outlined dense hide-details class="od-control" /></div>
              <!-- Opłata dotyczy wypisów; przy zgłaszaniu samych kierowców urząd jej nie pobiera. -->
              <div v-if="showVehicleFields || isGrudziadz" class="od-field-block">
                <label class="od-field-label">Łączna opłata</label>
                <v-text-field v-model="form.feeAmount" placeholder="Kwota z tabeli urzędu (także 0)" suffix="zł" outlined dense hide-details class="od-control" />

              </div>
              <div v-if="!isRemoval && (isLicenseChange || wizardNeedsVehicle || wizardNeedsDriver)" class="od-field-block">
                <label class="od-field-label">Kierowców po zmianie</label>
                <v-text-field v-model="form.driversTotalAfterChange" type="number" min="0" placeholder="cała flota po zgłoszeniu" outlined dense hide-details class="od-control" />
              </div>
              <div v-if="!isRemoval && (isLicenseChange || wizardNeedsVehicle)" class="od-field-block">
                <label class="od-field-label">Pojazdów po zmianie</label>
                <v-text-field v-model="form.vehiclesTotalAfterChange" type="number" min="0" placeholder="cała flota po zgłoszeniu" outlined dense hide-details class="od-control" />
              </div>
              <div v-if="wizardIntent === 'NEW_LICENSE'" class="od-field-block"><label class="od-field-label">Liczba zaświadczeń KRK</label><v-text-field v-model="form.criminalRecordCertificatesCount" type="number" min="1" outlined dense hide-details class="od-control" /></div>

            </div>
            <div v-if="needsDeclarantDetails" class="odw-declarant">
              <strong class="odw-declarant__title">Osoba składająca oświadczenie o dobrej reputacji</strong>
              <div class="od-form-grid od-form-grid--reputation">
                <div class="od-field-block od-field-block--wide"><label class="od-field-label">Imię i nazwisko <b>*</b></label><v-text-field v-model="form.declarantName" outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block"><label class="od-field-label">PESEL <b>*</b></label><v-text-field v-model="form.declarantPesel" maxlength="11" outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block"><label class="od-field-label">Data urodzenia</label><v-text-field v-model="form.declarantBirthDate" type="date" outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block"><label class="od-field-label">Stanowisko</label><v-select v-model="form.declarantPosition" :items="declarantPositionOptions" clearable outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block od-field-block--wide"><label class="od-field-label">Ulica <b>*</b></label><v-text-field v-model="form.declarantStreet" placeholder="ulica i numer" outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block od-field-block--wide"><label class="od-field-label">Kod i miejscowość <b>*</b></label><v-text-field v-model="form.declarantCityLine" placeholder="86-300 Grudziądz" outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block od-field-block--wide"><label class="od-field-label">Dokument tożsamości <b>*</b></label><v-text-field v-model="form.declarantDocument" placeholder="np. dowód osobisty ABC123456" outlined dense hide-details class="od-control" /></div>
              </div>
            </div>
          </div>

          <div v-if="supplementalFields.length" class="odw-block odw-block--framed">
            <header class="odw-block__head"><div><strong>Dodatkowe dane wymagane przez urząd</strong><small>Pola dopasowane do wybranych druków.</small></div></header>
            <div class="od-form-grid">
              <div v-for="field in supplementalFields" :key="field.key" class="od-field-block">
                <label class="od-field-label">{{ field.label }} <b>*</b></label>
                <v-text-field v-model="form[field.key]" outlined dense hide-details class="od-control" />
              </div>
            </div>
          </div>
          <div class="odw-block odw-block--framed">
            <h3>Dane firmy / wnioskodawcy</h3>
            <p class="odw-help-text">Domyślne dane BTP. Możesz je zmienić dla przygotowywanego kompletu dokumentów.</p>
            <div class="od-form-grid">
              <div class="od-field-block"><label class="od-field-label">Nazwa firmy <b>*</b></label><v-text-field v-model="form.applicantName" maxlength="200" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">NIP <b>*</b></label><v-text-field v-model="form.applicantNip" maxlength="10" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block od-field-block--wide"><label class="od-field-label">Ulica i numer <b>*</b></label><v-text-field v-model="form.applicantAddress" maxlength="200" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Kod pocztowy <b>*</b></label><v-text-field v-model="form.applicantZipCode" maxlength="6" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">Miejscowość <b>*</b></label><v-text-field v-model="form.applicantCity" maxlength="100" outlined dense hide-details class="od-control" /></div>
            </div>
          </div>
          <details v-if="cityTemplates.length" class="odw-block odw-block--framed">
            <summary>Dane firmy do formularzy — KRS, REGON, e-mail</summary>
            <p class="odw-help-text">Dodatkowe dane firmy wymagane na wybranych drukach.</p>
            <div class="od-form-grid">
              <div class="od-field-block"><label class="od-field-label">Numer KRS</label><v-text-field v-model="form.companyKrs" maxlength="10" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block"><label class="od-field-label">REGON</label><v-text-field v-model="form.companyRegon" maxlength="14" outlined dense hide-details class="od-control" /></div>
              <div class="od-field-block od-field-block--wide"><label class="od-field-label">E-mail firmy</label><v-text-field v-model="form.companyEmail" type="email" outlined dense hide-details class="od-control" /></div>
            </div>
          </details>
          <details v-if="cityTemplates.length" class="odw-block odw-block--framed">
            <summary>Załączniki i oświadczenia na drukach</summary>
            <p class="odw-help-text">Druki generowane w komplecie są zaznaczone automatycznie. Pozostałe załączniki zaznacz, jeśli je dołączasz.</p>
            <div class="od-attachment-grid">
              <v-checkbox v-for="option in attachmentOptions" :key="option.value" :input-value="form.attachmentFlags[option.value] || automaticAttachmentFlags[option.value]" :disabled="automaticAttachmentFlags[option.value]" :label="option.text" @change="$set(form.attachmentFlags, option.value, $event)" dense hide-details />
              <v-checkbox v-model="form.consentPersonalData" label="Zgoda na przetwarzanie danych osobowych" dense hide-details />
              <v-checkbox v-model="form.consentContact" label="Zgoda na kontakt" dense hide-details />
              <v-checkbox v-model="form.vehicleTechnicalInspection" label="Pojazd ma ważne badanie techniczne" dense hide-details />
              <v-checkbox v-model="form.vehicleHasTaximeter" label="Pojazd wyposażony w taksometr" dense hide-details />
              <v-checkbox v-model="form.vehicleUsesApp" label="Rozliczanie przejazdów aplikacją" dense hide-details />
            </div>
          </details>
          <div v-if="pendingReadinessItems.length" class="odw-help" role="status">
            <strong>Do uzupełnienia przed generowaniem:</strong>
            <ul><li v-for="item in pendingReadinessItems" :key="item.key">{{ item.value }}</li></ul>
          </div>
          <div class="odw-step__nav">
            <button type="button" class="od-button od-button--ghost" @click="goWizardStep(2)"><v-icon size="17">mdi-arrow-left</v-icon>Wstecz</button>
            <button type="button" class="od-button od-button--primary" @click="goWizardStep(4)">Dalej: podsumowanie<v-icon size="17">mdi-arrow-right</v-icon></button>
          </div>
        </section>

        <!-- KROK 4: PODSUMOWANIE I GENEROWANIE -->
        <section v-if="wizardStep === 4" class="od-panel odw-step">
          <header class="odw-step__head">
            <h2>Sprawdź i wygeneruj</h2>
            <p>Zobacz, co znajdzie się w komplecie, zanim klikniesz „Generuj".</p>
          </header>
          <div class="odw-summary-grid">
            <div class="odw-summary-fact"><small>Urząd</small><strong>{{ form.officeName }}</strong><span>{{ form.officeCity }}</span></div>
            <div class="odw-summary-fact"><small>Sprawa</small><strong>{{ wizardIntentLabel }}</strong><span v-if="form.licenseNumber">Licencja {{ form.licenseNumber }}</span></div>
            <div v-if="showDriverFields" class="odw-summary-fact"><small>Kierowcy</small><strong>{{ wizardDriversSummary }}</strong></div>
            <div v-if="showVehicleFields" class="odw-summary-fact"><small>Pojazdy</small><strong>{{ wizardVehiclesSummary }}</strong></div>
          </div>
          <div class="od-package odw-package">
            <span class="od-summary-label">Pliki w komplecie</span>
            <div v-for="item in includedDocuments" :key="item.key" class="od-package__item">
              <span :class="`is-${item.tone}`"><v-icon size="18">{{ item.icon }}</v-icon></span>
              <div><strong>{{ item.name }}</strong><small>{{ item.hint }}</small></div>
            </div>
          </div>
          <ul v-if="pendingReadinessItems.length" class="od-readiness-list odw-pending">
            <li v-for="item in pendingReadinessItems" :key="item.key">
              <span><v-icon size="16">mdi-alert-circle-outline</v-icon></span>
              <div><strong>{{ item.label }}</strong><small>{{ item.value }}</small></div>
            </li>
          </ul>
          <div class="odw-step__nav">
            <button type="button" class="od-button od-button--ghost" @click="goWizardStep(3)"><v-icon size="17">mdi-arrow-left</v-icon>Wstecz</button>
            <button type="button" class="od-button od-button--ghost" :disabled="!canGenerate || previewing || generating" @click="preview">
              <v-icon size="18" :class="{ 'is-spinning': previewing }">{{ previewing ? 'mdi-loading' : 'mdi-eye-outline' }}</v-icon>{{ previewing ? 'Tworzę podgląd…' : 'Podgląd PDF' }}
            </button>
            <button type="button" class="od-button od-button--primary" :disabled="!canGenerate || previewing || generating" @click="generate">
              <v-icon size="18" :class="{ 'is-spinning': generating }">{{ generating ? 'mdi-loading' : 'mdi-file-check-outline' }}</v-icon>{{ generating ? 'Generuję…' : 'Generuj dokumenty' }}
            </button>
          </div>
        </section>

        <!-- KROK 5: GOTOWE -->
        <section v-if="wizardStep === 5" class="od-panel odw-step">
          <header class="odw-step__head odw-step__head--done">
            <span class="odw-done-icon"><v-icon size="26">mdi-check-circle-outline</v-icon></span>
            <h2>Dokumenty gotowe</h2>
            <p>Komplet jest zapisany w historii. Otwórz i wydrukuj pliki albo wyślij je e-mailem osobie, która pójdzie do urzędu.</p>
          </header>

          <div class="odw-done-list">
            <div v-for="doc in generatedDocs" :key="doc.id" class="odw-done-item">
              <span :class="`is-${documentTone(doc.type)}`"><v-icon size="18">{{ documentTypeIcon(doc.type) }}</v-icon></span>
              <div><strong>{{ documentTypeLabel(doc) }}</strong><small>{{ doc.number || '' }}</small></div>
              <button type="button" @click="openPdf(doc.id)"><v-icon size="16">mdi-open-in-new</v-icon>Otwórz</button>
              <button v-if="doc.type !== 'URZAD_DRUK'" type="button" :disabled="!!printingId" @click="printDoc(doc)"><v-icon size="16" :class="{ 'is-spinning': printingId === doc.id }">{{ printingId === doc.id ? 'mdi-loading' : 'mdi-printer-outline' }}</v-icon>Drukuj</button>
            </div>
          </div>

          <!-- PEŁNOMOCNICTWO -->
          <div class="odw-block odw-block--framed">
            <header class="odw-block__head odw-block__head--toggle" role="button" tabindex="0" :aria-expanded="attorneyOpen" @click="attorneyOpen = !attorneyOpen" @keydown.enter="attorneyOpen = !attorneyOpen" @keydown.space.prevent="attorneyOpen = !attorneyOpen">
              <span class="is-violet"><v-icon size="19">mdi-account-tie-outline</v-icon></span>
              <div><strong>Dodaj pełnomocnictwo</strong><small>Upoważnij osobę, która pójdzie do urzędu w imieniu firmy.</small></div>
              <v-icon size="20">{{ attorneyOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </header>
            <div v-if="attorneyOpen" class="odw-attorney">
              <div class="od-form-grid od-form-grid--attorney">
                <div class="od-field-block od-field-block--wide"><label class="od-field-label">Pełnomocnik — imię i nazwisko <b>*</b></label><v-text-field v-model="form.attorneyName" placeholder="Osoba idąca do urzędu" prepend-inner-icon="mdi-account-outline" outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block"><label class="od-field-label">PESEL pełnomocnika <b>*</b></label><v-text-field v-model="form.attorneyPesel" maxlength="11" outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block od-field-block--wide"><label class="od-field-label">Adres pełnomocnika</label><v-text-field v-model="form.attorneyAddress" outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block od-field-block--wide"><label class="od-field-label">Mocodawca / reprezentant firmy <b>*</b></label><v-text-field v-model="form.principalRep" placeholder="Imię, nazwisko i funkcja" outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block"><label class="od-field-label">Numer dokumentu</label><v-text-field v-model="form.attorneyDocument" placeholder="Opcjonalnie" outlined dense hide-details class="od-control" /></div>
                <div class="od-field-block"><label class="od-field-label">Ważne do</label><v-text-field v-model="form.validUntil" type="date" outlined dense hide-details class="od-control" /></div>
              </div>
              <div class="od-scope-grid">
                <label :class="{ 'is-active': form.scopeSubmit }"><v-checkbox v-model="form.scopeSubmit" dense hide-details color="#6f8bff" /><span><strong>Złożenie wniosku</strong><small>Przekazanie kompletu do urzędu</small></span></label>
                <label :class="{ 'is-active': form.scopeCollect }"><v-checkbox v-model="form.scopeCollect" dense hide-details color="#6f8bff" /><span><strong>Odbiór dokumentów</strong><small>Wypisy, decyzje i potwierdzenia</small></span></label>
                <label :class="{ 'is-active': form.scopeStatements }"><v-checkbox v-model="form.scopeStatements" dense hide-details color="#6f8bff" /><span><strong>Oświadczenia i braki</strong><small>Uzupełnianie braków formalnych</small></span></label>
              </div>
              <button type="button" class="od-button od-button--primary" :disabled="attorneyGenerating || !attorneyFormValid" @click="generateAttorney">
                <v-icon size="18" :class="{ 'is-spinning': attorneyGenerating }">{{ attorneyGenerating ? 'mdi-loading' : 'mdi-file-sign' }}</v-icon>{{ attorneyGenerating ? 'Generuję…' : 'Generuj pełnomocnictwo' }}
              </button>
            </div>
          </div>

          <div class="odw-final-actions">
            <button type="button" class="od-button od-button--primary" @click="openEmailDialog"><v-icon size="18">mdi-email-arrow-right-outline</v-icon>Wyślij e-mailem</button>
            <button type="button" class="od-button od-button--ghost" :disabled="!!printingId || !printableDocs.length" @click="printAll"><v-icon size="18">mdi-printer-outline</v-icon>Drukuj komplet</button>
            <button type="button" class="od-button od-button--ghost" :disabled="operationBusy" @click="resetWizard(1)"><v-icon size="18">mdi-plus</v-icon>Nowy komplet</button>
          </div>
        </section>
      </template>

      <!-- DIALOG E-MAIL -->
      <v-dialog v-model="emailDialog" :persistent="emailSending" max-width="560" content-class="odw-dialog">
        <div class="odw-dialog__card">
          <header><span><v-icon size="19">mdi-email-arrow-right-outline</v-icon></span><div><small>Wysyłka kompletu</small><h3>Wyślij dokumenty e-mailem</h3></div><button type="button" aria-label="Zamknij" :disabled="emailSending" @click="emailDialog = false"><v-icon size="19">mdi-close</v-icon></button></header>
          <div class="odw-dialog__body">
            <div class="od-field-block"><label class="od-field-label">Adres e-mail <b>*</b></label><v-text-field v-model="emailForm.email" type="email" placeholder="osoba@firma.pl" outlined dense hide-details class="od-control" /></div>
            <div class="od-field-block"><label class="od-field-label">Imię i nazwisko odbiorcy</label><v-text-field v-model="emailForm.name" maxlength="120" outlined dense hide-details class="od-control" /></div>
            <div class="od-field-block"><label class="od-field-label">Temat</label><v-text-field v-model="emailForm.subject" maxlength="200" outlined dense hide-details class="od-control" /></div>
            <div class="od-field-block"><label class="od-field-label">Wiadomość</label><v-textarea v-model="emailForm.message" maxlength="4000" rows="4" auto-grow outlined dense hide-details class="od-control" /></div>
            <small class="odw-dialog__hint"><v-icon size="14">mdi-paperclip</v-icon>Załączniki: {{ generatedDocs.length }} plików z tego kompletu.</small>
          </div>
          <footer>
            <button type="button" class="od-button od-button--ghost" :disabled="emailSending" @click="emailDialog = false">Anuluj</button>
            <button type="button" class="od-button od-button--primary" :disabled="emailSending || !emailFormValid" @click="sendPackageEmail">
              <v-icon size="17" :class="{ 'is-spinning': emailSending }">{{ emailSending ? 'mdi-loading' : 'mdi-send-outline' }}</v-icon>{{ emailSending ? 'Wysyłam…' : 'Wyślij' }}
            </button>
          </footer>
        </div>
      </v-dialog>
    </div>

    <section v-show="activeWorkspace === 'history'" id="od-workspace-history" class="od-panel od-history">
      <header class="od-history__head">
        <div class="od-section-head od-section-head--plain">
          <span class="od-section-icon is-green"><v-icon size="21">mdi-history</v-icon></span>
          <div><span class="od-section-eyebrow">Ostatnia aktywność</span><h2>Wygenerowane dokumenty</h2><p>Otwórz dokument lub sprawdź, komu i do jakiego urzędu został przypisany.</p></div>
        </div>
        <div class="od-history__filters">
          <v-text-field v-model="docsSearch" placeholder="Szukaj po numerze, osobie lub urzędzie" prepend-inner-icon="mdi-magnify" clearable outlined dense hide-details class="od-control od-history-search" />
          <v-select v-model="docsTypeFilter" :items="docsTypeOptions" outlined dense hide-details class="od-control od-history-type" />
          <button type="button" class="od-refresh-action" :disabled="docsLoading" aria-label="Odśwież historię" @click="loadDocs">
            <v-icon size="18" :class="{ 'is-spinning': docsLoading }">mdi-refresh</v-icon>
            <span>{{ docsLoading ? 'Odświeżam…' : 'Odśwież historię' }}</span>
          </button>
        </div>
      </header>

      <v-progress-linear v-if="docsLoading" indeterminate height="2" color="#6f8bff" />
      <div v-if="docsError && !docsLoading" class="od-inline-state od-inline-state--error">
        <span><v-icon size="22">mdi-alert-circle-outline</v-icon></span><div><strong>Nie udało się pobrać historii</strong><p>{{ docsError }}</p></div><button type="button" @click="loadDocs">Spróbuj ponownie</button>
      </div>
      <div v-else-if="!docsLoading && filteredDocs.length === 0" class="od-empty-state">
        <span><v-icon size="30">mdi-file-search-outline</v-icon></span>
        <div><strong>{{ docs.length ? 'Brak wyników dla wybranych filtrów' : 'Nie wygenerowano jeszcze dokumentów' }}</strong><p>{{ docs.length ? 'Zmień wyszukiwanie lub typ dokumentu.' : 'Pierwszy przygotowany komplet pojawi się w tym miejscu.' }}</p></div>
      </div>

      <template v-else-if="!docsLoading">
        <div v-if="$vuetify.breakpoint.smAndDown" class="od-history-mobile">
          <article v-for="item in filteredDocs" :key="item.id" class="od-doc-card">
            <header><span class="od-doc-type" :class="`is-${documentTone(item.type)}`"><v-icon size="15">{{ documentTypeIcon(item.type) }}</v-icon>{{ documentTypeLabel(item) }}</span><time>{{ formatDateTime(item.createdAt) }}</time></header>
            <strong class="od-doc-number">{{ item.number || 'Bez numeru' }}</strong>
            <div class="od-doc-facts"><span><v-icon size="16">mdi-account-outline</v-icon>{{ documentPerson(item) }}</span><span><v-icon size="16">mdi-office-building-marker-outline</v-icon>{{ documentOffice(item) }}</span></div>
            <button type="button" @click="openPdf(item.id)"><v-icon size="17">{{ item.type === 'URZAD_DRUK' ? 'mdi-download' : 'mdi-file-pdf-box' }}</v-icon>{{ item.type === 'URZAD_DRUK' ? 'Pobierz druk' : 'Otwórz PDF' }}</button>
            <button v-if="mozeEdytowac(item)" type="button" @click="popraw(item)"><v-icon size="17">mdi-pencil-outline</v-icon>Popraw</button>
          </article>
        </div>
        <v-data-table v-else :headers="docHeaders" :items="filteredDocs" :items-per-page="10" class="od-data-table" no-data-text="Brak dokumentów">
          <template v-slot:item.createdAt="{ item }"><div class="od-date-cell"><strong>{{ formatDate(item.createdAt) }}</strong><small>{{ formatTime(item.createdAt) }}</small></div></template>
          <template v-slot:item.type="{ item }"><span class="od-doc-type" :class="`is-${documentTone(item.type)}`"><v-icon size="15">{{ documentTypeIcon(item.type) }}</v-icon>{{ documentTypeLabel(item) }}</span></template>
          <template v-slot:item.person="{ item }"><div class="od-person-cell"><span>{{ personInitials(documentPerson(item)) }}</span><strong>{{ documentPerson(item) }}</strong></div></template>
          <template v-slot:item.office="{ item }"><span class="od-office-cell"><v-icon size="16">mdi-map-marker-outline</v-icon>{{ documentOffice(item) }}</span></template>
          <template v-slot:item.actions="{ item }">
            <button type="button" class="od-table-action" @click="openPdf(item.id)"><v-icon size="16">{{ item.type === 'URZAD_DRUK' ? 'mdi-download' : 'mdi-file-pdf-box' }}</v-icon>{{ item.type === 'URZAD_DRUK' ? 'Pobierz' : 'Otwórz' }}</button>
            <button
              v-if="mozeEdytowac(item)" type="button" class="od-table-action od-table-action--edit"
              title="Wczytaj ten wniosek do kreatora i popraw" @click="popraw(item)"
            ><v-icon size="16">mdi-pencil-outline</v-icon>Popraw</button>
          </template>
        </v-data-table>
      </template>
    </section>

    <section v-show="activeWorkspace === 'registry'" id="od-workspace-registry" ref="officeRegistry" class="od-panel od-registry" tabindex="-1">
      <header class="od-registry__head">
        <div class="od-section-head od-section-head--plain">
          <span class="od-section-icon is-violet"><v-icon size="21">mdi-map-search-outline</v-icon></span>
          <div><span class="od-section-eyebrow">Zweryfikowane źródła</span><h2>Baza urzędów i formularzy</h2><p>Oficjalne karty usług BIP oraz druki dostępne bezpośrednio w systemie.</p></div>
        </div>
        <v-text-field v-model="registrySearch" placeholder="Szukaj miasta lub urzędu" prepend-inner-icon="mdi-magnify" clearable outlined dense hide-details class="od-control od-registry-search" />
      </header>

      <div class="od-registry-note"><v-icon size="19">mdi-shield-check-outline</v-icon><p>Przed wizytą sprawdź aktualne wymagania w karcie BIP. Urzędy mogą zmieniać formularze, opłaty i adresy obsługi.<strong v-if="officialCatalogVerifiedAt"> Katalog zweryfikowany: {{ officialCatalogVerifiedAt }}.</strong></p></div>

      <div v-if="filteredRegistry.length" class="od-registry-grid">
        <article v-for="c in filteredRegistry" :key="c.city" :class="{ 'is-selected': selectedRegistryCity === c.city }">
          <header><span>{{ cityInitials(c.city) }}</span><div><small>Urząd właściwy</small><h3>{{ c.city }}</h3></div><v-icon v-if="selectedRegistryCity === c.city" size="20">mdi-check-circle</v-icon></header>
          <strong>{{ c.office }}</strong>
          <p>{{ c.note }}</p>
          <div class="od-registry-card__meta"><span><v-icon size="15">mdi-web</v-icon>Karta BIP</span><span><v-icon size="15">mdi-file-outline</v-icon>{{ (c.forms || []).length }} {{ (c.forms || []).length === 1 ? 'druk' : 'druków' }}</span><span v-if="(c.cityTemplates || []).length || (c.procedures || []).length"><v-icon size="15">mdi-auto-fix</v-icon>auto-druk</span></div>
          <footer>
            <button type="button" class="od-use-office" @click="useRegistryCity(c.city)"><v-icon size="16">mdi-check</v-icon>{{ selectedRegistryCity === c.city ? 'Wybrano' : 'Użyj urzędu' }}</button>
            <a :href="c.card" target="_blank" rel="noopener noreferrer"><v-icon size="16">mdi-open-in-new</v-icon>BIP</a>
            <button v-for="f in c.forms || []" :key="f.fileId || f.type" type="button" class="od-download-form" :title="f.statusNote || f.name" @click="downloadStoredForm(f)"><v-icon size="16">mdi-download</v-icon>{{ f.name }}</button>
          </footer>
        </article>
      </div>
      <div v-else class="od-empty-state od-empty-state--compact"><span><v-icon size="28">mdi-map-marker-off-outline</v-icon></span><div><strong>Nie znaleziono urzędu</strong><p>Spróbuj wpisać inną nazwę miasta lub fragment nazwy wydziału.</p></div></div>
    </section>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
const axios = require('axios');

// Baza urzędów największych miast — oficjalne karty usług i druki z BIP.
// UWAGA: urzędy potrafią zmieniać adresy — przy wejściu do nowego miasta
// sprawdzić link i ewentualnie zaktualizować wpis.
const CITY_REGISTRY = [
  { city: 'Warszawa', office: 'Urząd m.st. Warszawy — Biuro Administracji i Spraw Obywatelskich', card: 'https://warszawa19115.pl/-/licencja-na-wykonywanie-krajowego-transportu-drogowego-przewoz-osob-taksowka', note: 'własny formularz „TW”', forms: [{ name: 'AO-09-05 Wniosek o licencję TAXI (TU).pdf', fileId: 'be5c1704-5629-47c2-a791-dec130564ce4' }] },
  { city: 'Kraków', office: 'Urząd Miasta Krakowa — Wydział Ewidencji Pojazdów i Kierowców', card: 'https://www.bip.krakow.pl/uslugi/KM-34', note: 'procedury KM-34 (licencja) / KM-38 (zmiana, wypisy)' },
  { city: 'Łódź', office: 'Urząd Miasta Łodzi', card: 'https://lckm.uml.lodz.pl/procedura/KA-01470', note: 'procedura KA-01470, także przez ePUAP', forms: [{ name: 'Dopisanie pojazdów TAXI — wypisy (DOC)', fileId: 'e504cf05-a0a6-4bcb-8bf3-95333e6999e7' }] },
  { city: 'Wrocław', office: 'Urząd Miejski Wrocławia', card: 'https://bip.um.wroc.pl/sprawa-do-zalatwienia/8632/licencja-na-wykonywanie-krajowego-transportu-drogowego-w-zakresie-przewozu-osob-taksowka', note: 'własny wniosek o wypis/wypisy', forms: [{ name: 'Wniosek o wydanie wypisu z licencji (PDF)', fileId: 'f74ddf18-5e92-4dca-bdde-df7121e2cab9' }] },
  { city: 'Poznań', office: 'Urząd Miasta Poznania', card: 'https://bip.poznan.pl/public/bip/druki.html?co=show&dr_id=5581&type=pdf', note: 'druk „Zgłoszenie dodatkowego pojazdu — TAXI” (PDF)', forms: [{ name: 'Zgłoszenie dodatkowego pojazdu TAXI (PDF)', fileId: '4f080583-1c79-4677-ac2c-38b68ac0f81f' }] },
  { city: 'Gdańsk', office: 'Urząd Miejski w Gdańsku — Wydział Komunikacji', card: 'https://bip.gdansk.pl/urzad-miejski/wydzial-komunikacji/procedury/Licencja-na-wykonywanie-krajowego-transportu-drogowego-w-zakresie-przewozu-osob-taksowka,a,44953', note: 'formularz „Wniosek o dodatkowe wypisy, wtórniki, wymianę wypisów”', forms: [{ name: 'Wniosek o dodatkowe wypisy (DOCX)', fileId: '01770363-8ff1-4a9c-8c9c-f6f3dbc8d408' }] },
  { city: 'Szczecin', office: 'Urząd Miasta Szczecin — Biuro Obsługi Interesantów', card: 'https://eurzad.szczecin.pl/chapter_51120.asp?soid=90BC8A9E3F6E9E1E605D029C70FAB67A', note: 'e-Urząd: „Licencje taxi na terenie Gminy Miasta Szczecin”' },
  { city: 'Bydgoszcz', office: 'Urząd Miasta Bydgoszczy — Wydział Uprawnień Komunikacyjnych (Grudziądzka 9-15)', card: 'https://um.bydgoszcz.pl/sprawa/175/licencja-na-wykonywanie-krajowego-transportu-drogowego-taksowka-zgloszenie-zmiany-zawieszenie-dzialalnosci-zrzeczenie-sie-licencji/', note: 'karta UMB175; mamy AUTO-WYPEŁNIANY druk zgłoszenia zmian + wykaz pojazdów (zał. 3)', cityTemplates: [{ type: 'BYDGOSZCZ_TAXI_ZMIANY', name: 'Bydgoszcz — zgłoszenie zmian do licencji' }, { type: 'BYDGOSZCZ_TAXI_WYKAZ', name: 'Bydgoszcz — wykaz pojazdów (zał. 3)' }], forms: [{ name: 'Wniosek TAXI z załącznikami (PDF)', fileId: '875b99cd-82ca-4d55-abe3-160556747a07' }, { name: 'Zgłoszenie zmian do licencji 2026 (PDF)', fileId: '2fa48426-96fc-4bc9-a865-f09e5b5d0790' }] },
  {
    city: 'Grudziądz',
    office: 'Urząd Miejski w Grudziądzu — Wydział Transportu (Piłsudskiego 51)',
    card: 'https://transport.grudziadz.pl/licencje/',
    note: 'cztery aktualne druki TAXI z grudnia 2025; komplet nowej licencji i zmiany licencji uzupełniany automatycznie',
    procedures: [
      {
        value: 'NEW_LICENSE',
        text: 'Nowa licencja TAXI',
        description: 'Wniosek, dobra reputacja i wykaz pojazdów',
        templates: [
          { type: 'GRUDZIADZ_TAXI_WNIOSEK', name: 'Grudziądz — wniosek o licencję TAXI' },
          { type: 'GRUDZIADZ_TAXI_REPUTACJA', name: 'Grudziądz — oświadczenie o dobrej reputacji' },
          { type: 'GRUDZIADZ_TAXI_WYKAZ', name: 'Grudziądz — wykaz pojazdów' },
        ],
      },
      {
        value: 'LICENSE_CHANGE',
        text: 'Zmiana licencji',
        description: 'Wniosek o zmianę; wykaz pojazdów tylko gdy potrzebny',
        templates: [
          { type: 'GRUDZIADZ_TAXI_ZMIANA', name: 'Grudziądz — zmiana licencji TAXI' },
          { type: 'GRUDZIADZ_TAXI_WYKAZ', name: 'Grudziądz — wykaz pojazdów' },
        ],
      },
    ],
    forms: [
      { type: 'GRUDZIADZ_TAXI_WNIOSEK', name: 'Wniosek o licencję TAXI (DOCX)', fileName: 'wniosek-licencja-taxi.docx' },
      { type: 'GRUDZIADZ_TAXI_REPUTACJA', name: 'Oświadczenie o dobrej reputacji (DOCX)', fileName: 'oswiadczenie-dobra-reputacja.docx' },
      { type: 'GRUDZIADZ_TAXI_WYKAZ', name: 'Wykaz pojazdów (DOCX)', fileName: 'wykaz-pojazdow.docx' },
      { type: 'GRUDZIADZ_TAXI_ZMIANA', name: 'Zmiana licencji TAXI (DOCX)', fileName: 'zmiana-licencji-taxi.docx' },
    ],
  },
  { city: 'Lublin', office: 'Urząd Miasta Lublin — Wydział Komunikacji (Czechowska 19A)', card: 'https://bip.lublin.eu/e-urzad/opisy-uslug/wydzial-komunikacji/komunikacjatransport/udzielenie-licencji-na-wykonywanie-transportu-drogowego-taksowka,9,22958,2.html', note: 'formularz KM-018-01', forms: [{ name: 'Wniosek KM-018-01 (PDF)', fileId: '0ce87fec-5d9e-4d48-b59c-e46621572026' }] },
  { city: 'Białystok', office: 'Urząd Miejski w Białymstoku', card: 'https://www.bip.bialystok.pl/urzad_miejski/poradnik_interesanta/jak_zalatwic_sprawe_wyszukiwarka_uslug/udzielenie-zmiana-tresci-dodatkowe-wypisy-z-licencji-na-wykonywanie-krajowego-transportu-drogowego-w-zakresie-przewozu-osob-taksowka.html', note: 'karta: udzielenie / zmiana / dodatkowe wypisy', forms: [{ name: 'Wniosek taxi (DOCX)', fileId: 'c4ee5b35-1044-4bfa-8297-cc4f03ca4ba9' }] },
  { city: 'Katowice', office: 'Urząd Miasta Katowice (Młyńska 4)', card: 'https://bip.katowice.eu/UrzadMiasta/KatalogUslug/KatalogUslug.aspx?idu=190', note: 'katalog usług, poz. 190' },
  { city: 'Gdynia', office: 'Urząd Miasta Gdyni — licencje i zezwolenia (al. Piłsudskiego 52/54, pok. 220)', card: 'https://nasze.miasto.gdynia.pl/cu/Service/getServiceCard/id,150,cid,79.html', note: 'karta usługi: wydanie/zmiana/wtórnik licencji taxi' },
  { city: 'Częstochowa', office: 'Urząd Miasta Częstochowy — Wydział Inżynierii Miejskiej i Kontroli (Śląska 11/13)', card: 'https://bip.czestochowa.pl/sprawa-do-zalatwienia/1167195/licencja-na-wykonywanie-krajowego-transportu-drogowego-w-zakresie-przewozu-osob-taksowka-imik004', note: 'karta IMiK004', forms: [{ name: 'Wniosek F-IMiK0041 (PDF)', fileId: '48d20ea5-9440-4666-a806-ee4c53eb3c07' }] },
  { city: 'Radom', office: 'Urząd Miejski w Radomiu', card: 'https://bip.radom.pl/ra/zalatw-sprawe/transport-drogowy-i-zez/24876,Licencja-na-wykonywanie-transportu-drogowego-taksowka-na-terenie-miasta-Radomia.html', note: 'własny wniosek o wydanie wypisu', forms: [{ name: 'Wniosek o wydanie wypisu (PDF)', fileId: '88cebea7-e6c0-4239-9dc6-e60ed77e20d0' }] },
  { city: 'Toruń', office: 'Urząd Miasta Torunia — Wydział Komunikacji i Działalności Gospodarczej (Legionów 220)', card: 'https://bip.torun.pl/sprawa-do-zalatwienia/27556/wkidg-1-26-wydanie-licencji-na-wykonywanie-krajowego-transportu-drogowego-w-zakresie-przewozu-osob-taksowka-na-obszarze-gminy-miasta-torun', note: 'karta WKiDG 1.26', forms: [{ name: 'Wniosek o licencję taxi (PDF)', fileId: '33d7d940-c9f6-40e5-890a-24802f3e4408' }, { name: 'Zmiana danych w licencji (PDF)', fileId: 'de070444-4dc6-480f-b69e-d8c3eb274848' }] },
  { city: 'Rzeszów', office: 'Urząd Miasta Rzeszowa', card: 'https://bip.erzeszow.pl/323-transport-drogowy/4143-wniosek-o-wydanie-dodatkowego-wypisu-lub-wypisow-z-licencji-na-wykonywanie-krajowego-transportu-drogowego-w-zakresie-przewozu-osob-taksowka-oraz-zgloszenie-dodatkowego-kierowcy.html', note: 'osobny wniosek o dodatkowy wypis + zgłoszenie kierowcy (PDF na BIP)', forms: [{ name: 'Wniosek o udzielenie/zmianę licencji (PDF)', fileId: '58c58c7f-a8e2-4a32-b561-cac2210d3b83' }] },
  { city: 'Kielce', office: 'Urząd Miasta Kielce — Wydział Komunikacji', card: 'https://bipum.kielce.eu/urzad-miasta-kielce/sposoby-przyjmowania-i-zalatwiania-spraw/komunikacja/komunikacja-transport/udzielenie-licencji-na-taksowke-osobowa.html', note: 'Karta Usług nr 94 + załącznik-wniosek', forms: [{ name: 'Wniosek o licencję taxi — zał. 1 (DOCX)', fileId: 'd6f77cb4-678d-4d85-95da-82ad6924f0f6' }] },
  { city: 'Gliwice', office: 'Urząd Miejski w Gliwicach', card: 'https://bip.gliwice.eu/wypis-do-licencji-taxi', note: 'osobna karta „Wypis do licencji taxi”', forms: [{ name: 'Wniosek o wypis KM-107 (PDF)', fileId: '7c2262f9-de7d-43e2-bdab-935c22d6b176' }] },
];

// Zdania, ktore kreator sam wpisuje w „Zakres zmiany". Na druku bydgoskim dokladnie
// to samo mowia kratki w pkt III, wiec rubryka „inne" ma przy nich zostac pusta —
// tester zglaszal ten zbedny dopisek trzy razy (01.09, 10.09 i 18.09.2026).
// Tekst napisany przez operatora niesie juz cos, czego kratki nie pokazuja: drukujemy.
const OPISY_PODSTAWIANE_PRZEZ_KREATOR = [
  'Dopisanie pojazdu do licencji i wydanie wypisu.',
  'Zgłoszenie kierowcy do licencji.',
  'Dopisanie pojazdu i zgłoszenie kierowcy do licencji.',
  'Wykreślenie wskazanych osób lub pojazdów z licencji — dane w załączonym wykazie.',
];

export default {
  name: 'OfficialDocumentsPage',
  data() {
    return {
      activeWorkspace: 'generator',
      builderStep: 1,
      activeDetailSection: 'vehicle',
      // Kreator liniowy: 0 = ekran startowy, 1 miasto, 2 sprawa, 3 dane,
      // 4 podsumowanie, 5 gotowe dokumenty.
      initialForm: null,
      wizardStep: 0,
      wizardMaxStep: 0,
      wizardIntent: '',
      wizardManualCity: false,
      wizardCitySearch: '',
      generatedDocs: [],
      attorneyOpen: false,
      attorneyGenerating: false,
      printingId: null,
      emailDialog: false,
      emailSending: false,
      emailForm: { email: '', name: '', subject: '', message: '' },
      wizardSteps: [
        { value: 1, label: 'Miasto' },
        { value: 2, label: 'Sprawa' },
        { value: 3, label: 'Dane' },
        { value: 4, label: 'Generuj' },
      ],
      wizardIntentOptions: [
        { value: 'NEW_LICENSE', text: 'Nowa licencja', description: 'Wniosek o licencję TAXI z wykazem pojazdów', icon: 'mdi-license', tone: 'violet' },
        { value: 'ADD_DRIVER', text: 'Dodaj kierowcę', description: 'Zgłoszenie kierowcy do istniejącej licencji', icon: 'mdi-account-plus-outline', tone: 'blue' },
        { value: 'ADD_VEHICLE', text: 'Dodaj auto', description: 'Dopisanie pojazdu do licencji i wypis', icon: 'mdi-car-arrow-right', tone: 'green' },
        { value: 'ADD_BOTH', text: 'Dodaj kierowcę i auto', description: 'Zgłoszenie kierowcy oraz dopisanie pojazdu', icon: 'mdi-car-multiple', tone: 'amber' },
        { value: 'REMOVE_DRIVER', text: 'Wykreśl kierowcę', description: 'Zgłoszenie zmiany — kierowca przestaje jeździć na licencji', icon: 'mdi-account-minus-outline', tone: 'red' },
        { value: 'REMOVE_VEHICLE', text: 'Wykreśl auto', description: 'Zgłoszenie zmiany — pojazd wycofany z licencji, zwrot wypisu', icon: 'mdi-car-off', tone: 'red' },
      ],
      docKinds: [
        {
          text: 'Komplet dokumentów urzędowych + pełnomocnictwo',
          shortText: 'Pełny komplet',
          description: 'Druki wybranej sprawy, pełnomocnictwo i dostępne załączniki',
          icon: 'mdi-file-document-multiple-outline',
          value: 'BOTH',
        },
        {
          text: 'Wniosek i druki wybranej sprawy',
          shortText: 'Druki urzędu',
          description: 'Formularze wymagane dla sprawy, bez pełnomocnictwa',
          icon: 'mdi-car-info',
          value: 'WNIOSEK',
        },
        {
          text: 'Pełnomocnictwo do urzędu',
          shortText: 'Pełnomocnictwo',
          description: 'Upoważnienie wybranej osoby do działania',
          icon: 'mdi-account-tie-outline',
          value: 'PELNOMOCNICTWO',
        },
      ],
      legalTitleOptions: ['umowa najmu', 'własność', 'współwłasność', 'umowa użyczenia', 'leasing'],
      licensePeriodOptions: [
        { text: '2–15 lat', value: '2-15' },
        { text: '16–30 lat', value: '16-30' },
        { text: '31–50 lat', value: '31-50' },
      ],
      companyLegalFormOptions: [
        'fundacja',
        'spółdzielnia',
        'spółka z ograniczoną odpowiedzialnością',
        'spółka akcyjna',
        'spółka jawna',
        'spółka komandytowa',
        'spółka komandytowo-akcyjna',
        'spółka partnerska',
        'jednoosobowa działalność gospodarcza',
        'inne',
      ],
      companyRepresentationBodyOptions: [
        'Zarząd',
        'Wspólnicy reprezentujący spółkę',
        'Rada Dyrektorów',
      ],
      entityTypeOptions: [
        { text: 'Osoba prawna / spółka', value: 'LEGAL' },
        { text: 'Osoba fizyczna / JDG', value: 'PHYSICAL' },
      ],
      companyRegistryOptions: [
        { text: 'KRS', value: 'KRS' },
        { text: 'CEIDG', value: 'CEIDG' },
      ],
      driverOriginOptions: [
        { text: 'Polska / Unia Europejska', value: true },
        { text: 'Poza Unią Europejską', value: false },
      ],
      declarantPositionOptions: [
        'Członek Zarządu',
        'Dyrektor generalny',
        'Prezes Zarządu',
        'Prokurent',
        'Wiceprezes Zarządu',
        'Właściciel (działalność gospodarcza)',
        'Wspólnik (spółki osobowe)',
      ],
      provinceOptions: [
        'dolnośląskie', 'kujawsko-pomorskie', 'lubelskie', 'lubuskie',
        'łódzkie', 'małopolskie', 'mazowieckie', 'opolskie', 'podkarpackie',
        'podlaskie', 'pomorskie', 'śląskie', 'świętokrzyskie',
        'warmińsko-mazurskie', 'wielkopolskie', 'zachodniopomorskie',
      ],
      attachmentOptions: [
        { value: 'attachmentGoodRepute', text: 'Oświadczenie o dobrej reputacji' },
        { value: 'attachmentVehiclesList', text: 'Wykaz pojazdów' },
        { value: 'attachmentDriversList', text: 'Wykaz kierowców' },
        { value: 'attachmentDrivingLicense', text: 'Kopia prawa jazdy' },
        { value: 'attachmentMedical', text: 'Badania lekarskie' },
        { value: 'attachmentPsychological', text: 'Badania psychologiczne' },
        { value: 'attachmentCriminalRecord', text: 'Zaświadczenie o niekaralności' },
        { value: 'attachmentRegistrationDocument', text: 'Dowód rejestracyjny' },
        { value: 'attachmentLegalTitle', text: 'Tytuł prawny do pojazdu' },
        { value: 'attachmentFeeProof', text: 'Potwierdzenie opłaty' },
        { value: 'attachmentPhoto', text: 'Zdjęcie' },
      ],
      changeScopeOptions: [
        { value: 'changeCompanyName', text: 'Nazwa firmy', description: 'Nazwa, nazwisko lub forma prawna' },
        { value: 'changeCompanyAddress', text: 'Adres firmy', description: 'Siedziba albo adres zamieszkania' },
        { value: 'changeManagement', text: 'Zarząd / wspólnicy', description: 'Zmiana osób reprezentujących firmę' },
        { value: 'changeTransportManager', text: 'Zarządzający transportem', description: 'Zmiana osoby zarządzającej' },
        { value: 'changeBaseAddress', text: 'Baza eksploatacyjna', description: 'Zmiana adresu bazy' },
        { value: 'changeFinancialStanding', text: 'Sytuacja finansowa', description: 'Aktualizacja danych finansowych' },
        { value: 'changeDriver', text: 'Kierowca', description: 'Zgłoszenie albo wykreślenie kierowcy' },
      ],
      form: {
        docKind: 'BOTH',
        recipientMode: 'PERSON',
        officialProcedure: '',
        person: null,
        officeName: '',
        officeAddress: '',
        officeCity: '',
        licenseNumber: '',
        licenseArea: '',
        licensePeriod: '2-15',
        licenseYears: '',
        copiesCount: 1,
        criminalRecordCertificatesCount: 1,
        // Nazwa REJESTROWA — urzad wymaga jej na wniosku i pelnomocnictwie
        // (Katarzyna z urzedu przez K. Sobkowskiego, 28.09.2026). Ta sama
        // wartosc siedzi w `Company.name`, ktore nadpisuje to pole po wybraniu
        // firmy z systemu — obie musza byc identyczne, bo wczesniej rozjazd
        // sprawial, ze staranniejsze wypelnienie dawalo GORSZY wynik.
        applicantName: 'BTP sp. z o.o.',
        applicantNip: '9671441888',
        applicantAddress: 'ul. Makowa 13',
        applicantZipCode: '85-361',
        applicantCity: 'Bydgoszcz',
        companyPhone: '52 516 11 12',
        companyKrs: '',
        companyRegon: '',
        companyEmail: '',
        companyLegalForm: 'spółka z ograniczoną odpowiedzialnością',
        entityType: 'LEGAL',
        companyRegistry: 'KRS',
        companyProvince: 'kujawsko-pomorskie',
        companyRepresentationBody: 'Zarząd',
        companyRepresentationMethod: '',
        vehicleSource: 'FLEET',
        fleetVehicleId: null,
        vehicleBrand: '',
        vehicleModel: '',
        vehiclePlate: '',
        vehicleVin: '',
        vehicleYear: '',
        vehicleSeats: '5',
        vehicleDmc: '',
        vehicleTechnicalInspection: true,
        vehicleHasTaximeter: false,
        vehicleUsesApp: true,
        vehiclePurpose: 'samochód osobowy / TAXI',
        vehicleIsReplacement: false,
        changeIncludesVehicle: true,
        vehiclesAfter: '',
        legalTitle: 'umowa najmu',
        firstRegDate: '',
        feeAmount: '',
        changeDescription: '',
        procedureChangedExtract: false,
        procedureDuplicateExtract: false,
        changeFlags: {
          changeCompanyName: false,
          changeCompanyAddress: false,
          changeManagement: false,
          changeTransportManager: false,
          changeBaseAddress: false,
          changeFinancialStanding: false,
          changeDriver: false,
        },
        declarantName: '',
        declarantPesel: '',
        declarantBirthDate: '',
        declarantBirthPlace: '',
        declarantStreet: '',
        declarantCityLine: '',
        declarantDocument: '',
        declarantPosition: 'Prezes Zarządu',
        declarantEmail: '',
        driverPesel: '',
        driverBirthDate: '',
        driverLicenseNumber: '',
        driverLicenseValidTo: '',
        driverLicenseIndefinite: false,
        driverMedicalValidTo: '',
        driverMedicalCertificateNumber: '',
        driverPsychologicalValidTo: '',
        driverPsychologicalCertificateNumber: '',
        driversTotalAfterChange: '',
        vehiclesTotalAfterChange: '',
        driverCriminalRecordDate: '',
        driverIsEuCitizen: true,
        hasDrivers: false,
        ownerDrives: false,
        consentPersonalData: false,
        consentContact: false,
        personalCollection: false,
        principalRep: '',
        principalAddress: '',
        principalPesel: '',
        attorneyName: '',
        attorneyPesel: '',
        attorneyAddress: '',
        attorneyRelationship: '',
        attorneyDocument: '',
        validUntil: '',
        scopeSubmit: true,
        scopeCollect: true,
        scopeStatements: true,
        conditionalForms: {},
        extraForms: {},
        attachmentFlags: {
          attachmentGoodRepute: false,
          attachmentVehiclesList: false,
          attachmentDriversList: false,
          attachmentDrivingLicense: false,
          attachmentMedical: false,
          attachmentPsychological: false,
          attachmentCriminalRecord: false,
          attachmentRegistrationDocument: false,
          attachmentLegalTitle: false,
          attachmentFeeProof: false,
          attachmentPhoto: false,
        },
      },
      additionalVehicles: [],
      additionalDrivers: [],
      fleetVehicleOptions: [],
      fleetVehiclesLoading: false,
      fleetVehiclesError: '',
      personOptions: [],
      personSearch: '',
      personSearchError: '',
      personLoading: false,
      personDebounce: null,
      cityRegistry: CITY_REGISTRY.map((entry) => ({
        ...entry,
        forms: [],
        procedures: [],
        cityTemplates: [],
        status: 'REVIEW_REQUIRED',
        statusNote: 'Oczekiwanie na zweryfikowany katalog backendu.',
      })),
      officialCatalogVerifiedAt: '',
      catalogLoading: false,
      catalogError: '',
      selectedRegistryCity: null,
      // Co baza licencji odpowiedziała dla wybranego miasta: OK / WYGASLA / BRAK.
      licenseLookup: { status: '', licencja: null },
      // Numer, który TA strona wstawiła automatycznie. Bez tego nie da się
      // odróżnić wartości wpisanej ręcznie przez operatora od numeru
      // podstawionego dla POPRZEDNIO wybranego miasta — a wtedy przy zmianie
      // miasta na wniosku zostaje numer z poprzedniego urzędu.
      licenseAutofilled: '',
      registrySearch: '',
      previewing: false,
      generating: false,
      docs: [],
      docsLoading: false,
      docsError: '',
      docsSearch: '',
      docsTypeFilter: 'ALL',
      docsTypeOptions: [
        { text: 'Wszystkie typy', value: 'ALL' },
        { text: 'Wnioski i wykazy', value: 'APPLICATION' },
        { text: 'Pełnomocnictwa', value: 'PELNOMOCNICTWO' },
        { text: 'Oryginalne druki urzędów', value: 'URZAD_DRUK' },
      ],
      personRequestId: 0,
      docHeaders: [
        { text: 'Data', value: 'createdAt', width: '120px' },
        { text: 'Numer', value: 'number', width: '155px' },
        { text: 'Typ dokumentu', value: 'type', width: '230px' },
        { text: 'Odbiorca', value: 'person', width: '190px' },
        { text: 'Urząd', value: 'office' },
        { text: '', value: 'actions', sortable: false, width: '100px', align: 'end' },
      ],
    };
  },
  computed: {
    operationBusy() {
      return this.generating || this.previewing || this.attorneyGenerating || this.emailSending;
    },
    generationTypes() {
      const types = this.needsWniosek ? (this.cityTemplates.length ? this.cityTemplates.map((t) => t.type) : ['URZAD_WYPIS_WNIOSEK']) : [];
      if (this.isRemoval && !types.includes('URZAD_WYPIS_WNIOSEK')) types.push('URZAD_WYPIS_WNIOSEK');
      if (this.needsPelnomocnictwo) types.push('PELNOMOCNICTWO');
      return [...new Set(types)];
    },
    printableDocs() {
      return this.generatedDocs.filter((doc) => doc.type !== 'URZAD_DRUK');
    },
    attorneyFormValid() {
      const f = this.form;
      return Boolean(String(f.attorneyName || '').trim() && String(f.principalRep || '').trim()
        && /^\d{11}$/.test(String(f.attorneyPesel || '').trim())
        && (f.scopeSubmit || f.scopeCollect || f.scopeStatements));
    },
    isRemoval() {
      return ['REMOVE_DRIVER', 'REMOVE_VEHICLE'].includes(this.wizardIntent);
    },
    showDriverFields() {
      return this.wizardNeedsDriver || this.needsDriverDetails || this.form.hasDrivers || this.form.ownerDrives;
    },
    showVehicleFields() {
      return this.wizardNeedsVehicle || this.cityTemplates.some((t) =>
        (t.requiredDataPaths || []).some((path) => path.startsWith('vehicles[]')));
    },
    automaticAttachmentFlags() {
      return {
        attachmentGoodRepute: this.cityTemplates.some((t) => /REPUTAC/i.test(t.type)),
        attachmentDriversList: this.cityTemplates.some((t) => /KIEROWC|DRIVER/i.test(t.type)),
        attachmentVehiclesList: this.cityTemplates.some((t) => /WYKAZ|POJAZD|VEHICLES/i.test(t.type) && !/KIEROWC|DRIVER/i.test(t.type)),
      };
    },
    supplementalFields() {
      const paths = this.cityTemplates.flatMap((t) => t.requiredDataPaths || []);
      const fields = [
        ['vehiclesCount', 'vehiclesTotalAfterChange', 'Liczba pojazdów po zmianie'],
        ['companyLegalForm', 'companyLegalForm', 'Forma prawna firmy'],
        ['companyProvince', 'companyProvince', 'Województwo firmy'],
        ['companyRepresentationBody', 'companyRepresentationBody', 'Organ reprezentujący firmę'],
        ['companyRepresentationMethod', 'companyRepresentationMethod', 'Sposób reprezentacji firmy'],
        ['principal.name', 'principalRep', 'Mocodawca / reprezentant firmy'],
        ['principal.addressPesel', 'principalAddress', 'Adres mocodawcy'],
        ['principal.addressPesel', 'principalPesel', 'PESEL mocodawcy'],
        ['attorney.name', 'attorneyName', 'Imię i nazwisko pełnomocnika'],
        ['attorney.pesel', 'attorneyPesel', 'PESEL pełnomocnika'],
        ['attorney.addressRelationship', 'attorneyAddress', 'Adres pełnomocnika'],
      ];
      return fields.filter(([path, key]) => paths.includes(path) && (key !== 'vehiclesTotalAfterChange' || (this.isRemoval && this.wizardIntent !== 'REMOVE_VEHICLE')))
        .map(([, key, label]) => ({ key, label }));
    },
    cityRegistryOptions() {
      return this.cityRegistry.map((c) => c.city);
    },
    wizardCityCards() {
      const query = String(this.wizardCitySearch || '').trim().toLocaleLowerCase('pl-PL');
      if (!query) return this.cityRegistry;
      return this.cityRegistry.filter((c) => [c.city, c.office].filter(Boolean).join(' ')
        .toLocaleLowerCase('pl-PL').includes(query));
    },
    wizardNeedsDriver() {
      return ['ADD_DRIVER', 'ADD_BOTH', 'REMOVE_DRIVER'].includes(this.wizardIntent);
    },
    wizardNeedsVehicle() {
      return this.wizardIntent === 'NEW_LICENSE'
        || this.wizardIntent === 'ADD_VEHICLE'
        || this.wizardIntent === 'ADD_BOTH'
        || this.wizardIntent === 'REMOVE_VEHICLE';
    },
    wizardIntentLabel() {
      const option = this.wizardIntentOptions.find((item) => item.value === this.wizardIntent);
      return option ? option.text : 'Dokumenty do urzędu';
    },
    wizardDriversSummary() {
      const names = [];
      if (this.form.person) names.push(this.form.person.text.split(' — ')[0]);
      this.additionalDrivers.forEach((driver) => {
        if (String(driver.name || '').trim()) names.push(driver.name.trim());
      });
      return names.length ? names.join(', ') : 'Nie wpisano';
    },
    wizardVehiclesSummary() {
      const rows = [
        [this.form.vehicleBrand, this.form.vehicleModel, this.form.vehiclePlate].filter(Boolean).join(' '),
        ...this.additionalVehicles.map((vehicle) => [vehicle.brand, vehicle.model, vehicle.plate].filter(Boolean).join(' ')),
      ].filter((row) => row.trim());
      return rows.length ? rows.join(', ') : 'Nie wpisano';
    },
    emailFormValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(this.emailForm.email || '').trim())
        && Boolean(this.emailForm.subject.trim()) && this.emailForm.subject.length <= 200
        && this.emailForm.name.length <= 120 && this.emailForm.message.length <= 4000
        && this.generatedDocs.length > 0;
    },
    builderStepOneReady() {
      return Boolean(this.form.docKind && (this.isOfficeMode || this.form.person));
    },
    builderStepTwoReady() {
      return Boolean(
        String(this.form.officeName || '').trim()
        && String(this.form.officeCity || '').trim(),
      );
    },
    selectedRegistryEntry() {
      return this.cityRegistry.find((c) => c.city === this.selectedRegistryCity) || null;
    },
    // Krótki komunikat pod polem numeru licencji — skąd wzięła się wartość
    // albo dlaczego pole zostało puste.
    licenseHint() {
      const { status, licencja } = this.licenseLookup || {};
      if (status === 'OK' && licencja) {
        const doKiedy = licencja.expiresAt
          ? `ważna do ${new Date(licencja.expiresAt).toLocaleDateString('pl-PL')}`
          : 'bezterminowa';
        return `Podstawione z listy licencji (${doKiedy}).`;
      }
      if (status === 'WYGASLA' && licencja) {
        const koniec = licencja.expiresAt
          ? new Date(licencja.expiresAt).toLocaleDateString('pl-PL')
          : 'nieznana data';
        return `Uwaga: licencja na to miasto jest po terminie (${koniec}). Sprawdź numer przed wysłaniem wniosku.`;
      }
      if (status === 'BRAK') {
        return 'Brak licencji na to miasto w zakładce „Licencje TAXI" — wpisz numer ręcznie.';
      }
      return '';
    },
    licenseHintClass() {
      return this.licenseLookup && this.licenseLookup.status === 'WYGASLA'
        ? 'od-license-hint--alarm'
        : '';
    },
    selectedCityProcedure() {
      const procedures = (this.selectedRegistryEntry && this.selectedRegistryEntry.procedures) || [];
      return procedures.find((procedure) => procedure.value === this.form.officialProcedure) || null;
    },
    // Wszystkie druki wybranej procedury — do listy „Druki w komplecie".
    procedureTemplates() {
      return (this.selectedCityProcedure && this.selectedCityProcedure.templates) || [];
    },
    conditionalTemplates() {
      return ((this.selectedCityProcedure && this.selectedCityProcedure.templates) || [])
        .filter((template) => template.conditional);
    },
    // Miejskie szablony auto-wypełniane (odwzorowanie druku urzędu 1:1).
    isOfficeMode() {
      return this.form.recipientMode === 'OFFICE';
    },
    availableDocKinds() {
      // Pełnomocnictwo jest wystawiane na osobę — w trybie „dla biura" zostaje sam wniosek.
      if (this.isOfficeMode) return this.docKinds.filter((kind) => kind.value === 'WNIOSEK');
      return this.docKinds;
    },
    extraCityForms() {
      const entry = this.selectedRegistryEntry;
      if (!entry || !this.needsWniosek) return [];
      const offeredTypes = new Set(
        ((this.selectedCityProcedure && this.selectedCityProcedure.templates) || [])
          .map((template) => template.type),
      );
      return (entry.forms || [])
        .filter((form) => form.type && !form.fileId && (form.status || 'ACTIVE') === 'ACTIVE')
        .filter((form) => !offeredTypes.has(form.type))
        .map((form) => ({
          type: form.type,
          name: form.name,
          requiredDataPaths: form.requiredDataPaths || [],
          capacities: form.capacities || {},
        }));
    },
    selectedExtraTemplates() {
      return this.extraCityForms.filter((template) => this.form.extraForms[template.type]);
    },
    /**
     * Pola, których naprawdę używają druki wybrane w tym kreatorze.
     *
     * Dzięki temu kreator nie musi znać miast po nazwie: pokazuje pole wtedy,
     * gdy któryś z wybranych formularzy je czyta. Lista przychodzi z katalogu
     * (`mappedDataPaths`), więc nowe miasto włącza pole samo.
     */
    mappedPathsInSelection() {
      const zbior = new Set();
      (this.cityTemplates || []).forEach((template) => {
        (template.mappedDataPaths || []).forEach((path) => zbior.add(path));
      });
      return zbior;
    },
    /**
     * Jelenia Góra pyta o LICZBĘ LAT (2–50) w dwóch kratkach, a nie o przedział
     * jak reszta miast. Wpisanie tam „2-15" dałoby na wniosku „21".
     */
    needsLicenseYears() {
      return this.isNewLicense && this.mappedPathsInSelection.has('licenseYears');
    },
    cityTemplates() {
      if (this.selectedCityProcedure) {
        const templates = this.selectedCityProcedure.templates || [];
        return templates
          .filter((template) => !template.conditional || this.form.conditionalForms[template.type])
          .concat(this.selectedExtraTemplates)
          .filter((template) => !this.isRemoval || !/KIEROWC|DRIVER|VEHICLES|POJAZD|WYKAZ/i.test(template.type));
      }
      const legacy = (this.selectedRegistryEntry && this.selectedRegistryEntry.cityTemplates) || [];
      return legacy.concat(this.selectedExtraTemplates);
    },
    vehicleCapacity() {
      const limits = this.cityTemplates
        .map((template) => Number(template.capacities && template.capacities.vehicles))
        .filter((value) => Number.isInteger(value) && value > 0);
      return limits.length ? Math.min(20, ...limits) : 20;
    },
    driverCapacity() {
      const limits = this.cityTemplates
        .map((template) => Number(template.capacities && template.capacities.drivers))
        .filter((value) => Number.isInteger(value) && value > 0);
      return limits.length ? Math.min(20, ...limits) : 20;
    },
    needsDeclarantDetails() {
      if (!this.needsWniosek || !this.cityTemplates.length) return false;
      return this.cityTemplates.some((template) => {
        const required = template.requiredDataPaths || [];
        return required.some((path) => String(path).split('|').some((part) => part.startsWith('declarant.')))
          || /REPUTAC|OSWIADCZEN|OŚWIADCZEN/i.test(`${template.type || ''} ${template.name || ''}`);
      });
    },
    needsDriverDetails() {
      if (!this.needsWniosek || !this.cityTemplates.length) return false;
      return this.cityTemplates.some((template) => {
        const required = template.requiredDataPaths || [];
        return required.some((path) => String(path).split('|').some((part) => part.startsWith('drivers[]')))
          || /KIEROWC|DRIVER/i.test(`${template.type || ''} ${template.name || ''}`);
      });
    },
    needsOfficialAttorneyDetails() {
      if (!this.needsWniosek || !this.cityTemplates.length) return false;
      return this.cityTemplates.some((template) => {
        const required = template.requiredDataPaths || [];
        return required.some((path) => /^(principal|attorney)\./.test(String(path)))
          || /PEŁNOMOC|PELNOMOC/i.test(`${template.type || ''} ${template.name || ''}`);
      });
    },
    isGrudziadz() {
      return this.selectedRegistryCity === 'Grudziądz';
    },
    isGrudziadzNewLicense() {
      return this.isGrudziadz && this.form.officialProcedure === 'NEW_LICENSE';
    },
    isGrudziadzChange() {
      return this.isGrudziadz && this.isLicenseChange;
    },
    isNewLicense() {
      return this.form.officialProcedure === 'NEW_LICENSE';
    },
    isLicenseChange() {
      return this.form.officialProcedure === 'LICENSE_CHANGE'
        || this.form.officialProcedure === 'CHANGE_LICENSE';
    },
    vehicleSectionTitle() {
      if (this.isNewLicense) return 'Nowa licencja TAXI — pojazd';
      if (this.isLicenseChange) return 'Wniosek o zmianę licencji';
      return 'Wniosek o wypis — pojazd';
    },
    vehicleSectionDescription() {
      if (this.isNewLicense) return 'Dane pojazdu do wniosku i wykazu pojazdów.';
      if (this.isLicenseChange) return 'Opisz zmianę i zdecyduj, czy dołączyć wykaz pojazdów.';
      return 'Dane pojazdu, dla którego urząd ma wydać wypis.';
    },
    needsWniosek() {
      return this.form.docKind === 'BOTH' || this.form.docKind === 'WNIOSEK';
    },
    needsPelnomocnictwo() {
      return this.form.docKind === 'BOTH' || this.form.docKind === 'PELNOMOCNICTWO';
    },
    docsThisMonth() {
      const now = new Date();
      return this.docs.filter((doc) => {
        const date = new Date(doc.createdAt);
        return !Number.isNaN(date.getTime())
          && date.getFullYear() === now.getFullYear()
          && date.getMonth() === now.getMonth();
      }).length;
    },
    registryFormsCount() {
      return this.cityRegistry.reduce((sum, city) => sum + (city.forms || []).length, 0);
    },
    grudziadzVehicleReady() {
      return Boolean(
        String(this.form.vehicleBrand || '').trim()
        && String(this.form.vehiclePlate || '').trim()
        && /^[A-HJ-NPR-Z0-9]{17}$/i.test(String(this.form.vehicleVin || '').trim())
        && String(this.form.vehiclePurpose || '').trim()
        && String(this.form.legalTitle || '').trim(),
      );
    },
    citySpecificReady() {
      if (!this.isGrudziadz || !this.needsWniosek || this.isRemoval) return true;
      if (this.isGrudziadzNewLicense) {
        return this.grudziadzVehicleReady && Boolean(
          String(this.form.licenseArea || '').trim()
          && String(this.form.companyPhone || '').trim()
          && String(this.form.feeAmount || '').trim()
          && String(this.form.declarantName || '').trim()
          && /^\d{11}$/.test(String(this.form.declarantPesel || '').trim())
          && String(this.form.declarantStreet || '').trim()
          && String(this.form.declarantCityLine || '').trim()
          && String(this.form.declarantDocument || '').trim(),
        );
      }
      if (this.isGrudziadzChange) {
        const vehicleReady = !this.form.changeIncludesVehicle || this.grudziadzVehicleReady;
        return vehicleReady && Boolean(
          String(this.form.licenseNumber || '').trim()
          && String(this.form.changeDescription || '').trim()
          && String(this.form.companyPhone || '').trim()
          && String(this.form.feeAmount || '').trim(),
        );
      }
      return false;
    },
    canGenerate() {
      return Boolean(
        this.isPackageReady
        && (!this.catalogLoading || !this.needsWniosek)
        && (!this.catalogError || !this.needsWniosek)
      );
    },
    readinessItems() {
      const f = this.form;
      const items = [];
      const check = (key, label, done) => items.push({ key, label, value: label, done: Boolean(done) });
      const filled = (value) => String(value == null ? '' : value).trim().length > 0;
      check('applicant-details', 'Uzupełnij nazwę firmy, ulicę z numerem i miejscowość.', filled(f.applicantName) && filled(f.applicantAddress) && filled(f.applicantCity));
      check('applicant-nip', 'NIP firmy musi zawierać 10 cyfr.', /^\d{10}$/.test(String(f.applicantNip || '').trim()));
      check('applicant-zip', 'Kod pocztowy firmy musi mieć format 00-000.', /^\d{2}-\d{3}$/.test(String(f.applicantZipCode || '').trim()));
      check('office', 'Podaj nazwę i miasto urzędu.', filled(f.officeName) && filled(f.officeCity));
      if (this.needsWniosek) {
        check('license', this.wizardIntent === 'NEW_LICENSE' ? 'Podaj obszar i okres nowej licencji.' : 'Podaj numer licencji TAXI.',
          this.wizardIntent === 'NEW_LICENSE' ? filled(f.licenseArea) && filled(f.licensePeriod) : filled(f.licenseNumber));
        // Urząd, który chce LICZBĘ LAT (Jelenia Góra), nie przyjmie przedziału
        // — a puste kratki na wniosku o licencję to brak wniosku.
        if (this.needsLicenseYears) {
          check(
            'license-years',
            'Podaj liczbę lat licencji (2–50) — ten urząd nie przyjmuje przedziału.',
            /^\d+$/.test(String(f.licenseYears || '').trim())
              && Number(f.licenseYears) >= 2
              && Number(f.licenseYears) <= 50,
          );
        }
        if (this.isLicenseChange || this.isRemoval) check('change', 'Opisz zmianę w licencji.', filled(f.changeDescription));
        if (this.wizardNeedsVehicle && !this.isRemoval) {
          check('copies', 'Liczba wypisów musi być dodatnią liczbą całkowitą.', /^\d+$/.test(String(f.copiesCount)) && Number(f.copiesCount) > 0);
        }
        this.supplementalFields.forEach((field) => {
          check(`extra-${field.key}`, `Uzupełnij: ${field.label.toLocaleLowerCase('pl-PL')}.`, filled(f[field.key]));
          if (/Pesel$/.test(field.key) && filled(f[field.key])) check(`format-${field.key}`, `${field.label}: wymagane jest 11 cyfr.`, /^\d{11}$/.test(f[field.key]));
        });
        if (this.needsDeclarantDetails && filled(f.declarantPesel)) check('declarant-pesel', 'PESEL osoby składającej oświadczenie musi zawierać 11 cyfr.', /^\d{11}$/.test(f.declarantPesel));
        if (this.isRemoval) {
          const count = this.wizardIntent === 'REMOVE_DRIVER' ? f.driversTotalAfterChange : f.vehiclesTotalAfterChange;
          check('remaining-count', 'Podaj liczbę pozostających kierowców lub pojazdów (0, jeśli wykreślasz ostatni wpis).', /^\d+$/.test(String(count)));
        }
        if (this.showVehicleFields) {
          const rows = [{ brand: f.vehicleBrand, plate: f.vehiclePlate, vin: f.vehicleVin }, ...this.additionalVehicles];
          check('vehicles-capacity', `Wykaz mieści maksymalnie ${this.vehicleCapacity} pojazdów.`, rows.length <= this.vehicleCapacity);
          rows.forEach((row, i) => {
            check(`vehicle-${i}`, `Pojazd ${i + 1}: podaj markę i numer rejestracyjny.`, filled(row.brand) && filled(row.plate));
            check(`vin-${i}`, `Pojazd ${i + 1}: VIN musi mieć 17 znaków, bez I, O i Q.`, /^[A-HJ-NPR-Z0-9]{17}$/i.test(String(row.vin || '').trim()));
          });
        }
        if (this.showDriverFields) {
          const rows = [...(f.person ? [{ name: [f.person.firstName, f.person.lastName].filter(Boolean).join(' '), pesel: f.driverPesel }] : []), ...this.additionalDrivers];
          check('drivers', 'Dodaj kierowcę z systemu lub wpisz jego imię i nazwisko ręcznie.', rows.length > 0);
          check('drivers-capacity', `Wykaz mieści maksymalnie ${this.driverCapacity} kierowców.`, rows.length <= this.driverCapacity);
          rows.forEach((row, i) => {
            check(`driver-${i}`, `Kierowca ${i + 1}: podaj imię i nazwisko.`, filled(row.name));
            if (filled(row.pesel)) check(`pesel-${i}`, `Kierowca ${i + 1}: PESEL musi zawierać 11 cyfr.`, /^\d{11}$/.test(row.pesel));
          });
        }
        this.cityTemplates.forEach((template) => {
          const data = this.normalizeOfficialReadinessData(this.buildData(template.type));
          (template.requiredDataPaths || []).forEach((expression) => {
            check(`${template.type}-${expression}`, `${template.name}: ${this.requiredFieldLabel(expression)}.`,
              String(expression).split('|').some((path) => this.hasRequiredDataValue(data, path)));
          });
        });
        if (this.isGrudziadz) check('grudziadz', 'Uzupełnij dane urzędu, opłatę (także 0 zł) i dane oświadczenia.', this.citySpecificReady);
      }
      if (this.needsPelnomocnictwo) check('attorney', 'Uzupełnij pełnomocnika, PESEL, reprezentanta i zakres pełnomocnictwa.', this.attorneyFormValid);
      return items;
    },
    readinessDone() {
      return this.readinessItems.filter((item) => item.done).length;
    },
    readinessPercent() {
      if (!this.readinessItems.length) return 0;
      return Math.round((this.readinessDone / this.readinessItems.length) * 100);
    },
    pendingReadinessItems() {
      return this.readinessItems.filter((item) => !item.done);
    },
    isPackageReady() {
      return this.readinessItems.length > 0
        && this.pendingReadinessItems.length === 0
        && this.citySpecificReady;
    },
    includedDocuments() {
      const result = [];
      if (this.needsWniosek) {
        if (this.cityTemplates.length) {
          this.cityTemplates.forEach((template) => result.push({
            key: template.type,
            name: template.name,
            hint: 'Formularz miejski uzupełniany automatycznie',
            icon: 'mdi-auto-fix',
            tone: 'green',
          }));
        } else {
          result.push({
            key: 'application',
            name: this.wizardIntentLabel,
            hint: 'Uniwersalne podanie — sprawdź w BIP, czy urząd wymaga własnego druku',
            icon: 'mdi-file-document-edit-outline',
            tone: 'blue',
          });
        }
      }
      if (this.isRemoval && this.cityTemplates.length) result.push({ key: 'removal-list', name: this.wizardIntent === 'REMOVE_DRIVER' ? 'Wykaz kierowców do wykreślenia' : 'Wykaz pojazdów do wykreślenia', hint: 'Załącznik w układzie uniwersalnym z danymi wskazanych wpisów', icon: 'mdi-file-document-outline', tone: 'blue' });
      if (this.needsPelnomocnictwo) {
        result.push({
          key: 'attorney',
          name: 'Pełnomocnictwo do urzędu',
          hint: 'Dokument gotowy do podpisu i złożenia',
          icon: 'mdi-account-tie-outline',
          tone: 'violet',
        });
      }
      const sourceForms = ((this.selectedRegistryEntry && this.selectedRegistryEntry.forms) || [])
        .filter((form) => form.fileId);
      sourceForms.forEach((form) => result.push({
        key: `source-${form.fileId}`,
        name: form.name,
        hint: 'Oryginalny druk urzędu dołączony do kompletu',
        icon: 'mdi-file-certificate-outline',
        tone: 'amber',
      }));
      return result;
    },
    filteredDocs() {
      const query = String(this.docsSearch || '').trim().toLocaleLowerCase('pl-PL');
      return this.docs.filter((doc) => {
        const matchesType = this.docsTypeFilter === 'ALL'
          || (this.docsTypeFilter === 'APPLICATION' && doc.type !== 'PELNOMOCNICTWO' && doc.type !== 'URZAD_DRUK')
          || doc.type === this.docsTypeFilter;
        if (!matchesType) return false;
        if (!query) return true;
        const searchable = [
          doc.number,
          this.documentTypeLabel(doc),
          this.documentPerson(doc),
          this.documentOffice(doc),
        ].filter(Boolean).join(' ').toLocaleLowerCase('pl-PL');
        return searchable.includes(query);
      });
    },
    filteredRegistry() {
      const query = String(this.registrySearch || '').trim().toLocaleLowerCase('pl-PL');
      if (!query) return this.cityRegistry;
      return this.cityRegistry.filter((city) => [city.city, city.office, city.note]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('pl-PL')
        .includes(query));
    },
  },
  watch: {
    'form.person'(person, previous) {
      if ((person && person.id) === (previous && previous.id)) return;
      const defaults = this.initialForm || {};
      Object.keys(defaults).filter((key) => key.startsWith('driver') && key !== 'driversTotalAfterChange')
        .forEach((key) => { this.form[key] = defaults[key]; });
      if (person) {
        this.form.driverPesel = person.pesel || '';
        this.form.driverBirthDate = String(person.dateOfBirth || '').slice(0, 10);
        this.form.driverLicenseNumber = person.licenseNumber || '';
        // Dane do wnioskow zapisane w karcie kierowcy (od 23.09.2026).
        // Nadpisujemy tylko tym, co naprawde jest — reszte operator wpisze sam.
        if (person.drivingLicenseIndefinite) {
          this.form.driverLicenseIndefinite = true;
          this.form.driverLicenseValidTo = '';
        } else if (person.drivingLicenseValidTo) {
          this.form.driverLicenseValidTo = person.drivingLicenseValidTo;
        }
        [['medicalCertificateNumber', 'driverMedicalCertificateNumber'],
          ['medicalValidTo', 'driverMedicalValidTo'],
          ['psychologicalCertificateNumber', 'driverPsychologicalCertificateNumber'],
          ['psychologicalValidTo', 'driverPsychologicalValidTo'],
          ['criminalRecordDate', 'driverCriminalRecordDate']].forEach(([zrodlo, cel]) => {
          if (person[zrodlo]) this.form[cel] = person[zrodlo];
        });
        this.additionalDrivers = this.additionalDrivers.filter((row) => Object.values(row).some((value) => typeof value === 'string' && value.trim()));
      }
    },
    'form.docKind'() {
      this.activeDetailSection = this.needsWniosek ? 'vehicle' : 'attorney';
    },
    // Opłata to wypisy x 22 zł — wpisujemy ją sami, żeby pracownik nie liczył
    // w pamięci. Ręcznie wpisanej kwoty nie ruszamy: nadpisujemy tylko puste pole
    // albo takie, w którym nadal stoi nasza poprzednia podpowiedź.
    suggestedFeeAmount(nowa, poprzednia) {
      const wpisane = String(this.form.feeAmount || '').trim();
      if (!nowa) return;
      if (!wpisane || wpisane === String(poprzednia || '')) {
        this.form.feeAmount = nowa;
      }
    },
    personSearch(val) {
      clearTimeout(this.personDebounce);
      if (!val || val.length < 2) {
        this.personRequestId += 1;
        this.personLoading = false;
        if (!this.form.person) this.personOptions = [];
        return;
      }
      this.personDebounce = setTimeout(() => this.searchPersons(val), 350);
    },
    'form.companyLegalForm'(value) {
      const legalForm = String(value || '').toLocaleLowerCase('pl-PL');
      if (legalForm.includes('jednoosobowa działalność')) {
        this.form.entityType = 'PHYSICAL';
        this.form.companyRegistry = 'CEIDG';
        this.form.companyRepresentationBody = '';
        this.form.declarantPosition = 'Właściciel (działalność gospodarcza)';
      } else if (
        legalForm.includes('spółka jawna')
        || legalForm.includes('spółka komandytowa')
        || legalForm.includes('spółka partnerska')
      ) {
        this.form.entityType = 'LEGAL';
        this.form.companyRegistry = 'KRS';
        this.form.companyRepresentationBody = 'Wspólnicy reprezentujący spółkę';
        this.form.declarantPosition = 'Wspólnik (spółki osobowe)';
      } else {
        if (legalForm !== 'inne') {
          this.form.entityType = 'LEGAL';
          this.form.companyRegistry = 'KRS';
        }
        if (
          !this.form.companyRepresentationBody
          || this.form.companyRepresentationBody === 'Wspólnicy reprezentujący spółkę'
        ) {
          this.form.companyRepresentationBody = 'Zarząd';
        }
        if (
          this.form.declarantPosition === 'Właściciel (działalność gospodarcza)'
          || this.form.declarantPosition === 'Wspólnik (spółki osobowe)'
        ) {
          this.form.declarantPosition = 'Prezes Zarządu';
        }
      }
    },
  },
  created() {
    this.initialForm = JSON.parse(JSON.stringify(this.form));
    this.loadApplicantFromCompany();
    this.loadOfficialCatalog();
    this.loadDocs();
    this.loadFleetVehicles();
  },
  beforeDestroy() {
    clearTimeout(this.personDebounce);
    this.personRequestId += 1;
  },
  methods: {
    // Dane wnioskodawcy byly wpisane na sztywno w data(), wiec po przelaczeniu sie
    // na inna spolke druki i tak wychodzily z danymi BTP. Bierzemy je z aktywnej
    // firmy; wartosci z data() zostaja jako awaryjne, gdyby zapytanie nie przeszlo.
    // addressLine1 i addressLine2 laczymy SPACJA — serwer sklada je przecinkiem
    // i na drukach stalo „Makowa, 13" zamiast „Makowa 13".
    async loadApplicantFromCompany() {
      const id = localStorage.getItem('activeCompanyId');
      if (!id) return;
      let firma;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'company/' + id,
          ...this.getConfig(),
        });
        firma = (res.data && res.data.data) || {};
      } catch (e) {
        console.error(e);
        return;
      }
      const nip = String(firma.taxCode || '').replace(/\D/g, '').slice(0, 10);
      // addressLine1 i addressLine2 sklejamy SPACJĄ — serwer robi to przecinkiem
      // i na drukach stało „Makowa, 13". Dopisujemy też „ul.", bo w bazie zapisana
      // jest sama nazwa ulicy, a na wniosku do urzędu ma być pełny adres.
      // Placów, alej i osiedli nie ruszamy — tam „ul." byłoby błędem.
      const bezPrzedrostka = /^(ul|al|pl|os|ulica|aleja|aleje|plac|osiedle|rynek)(\.|\s|$)/i;
      const ulica = String(firma.addressLine1 || '').trim();
      const numer = String(firma.addressLine2 || '').trim();
      const adres = [ulica && !bezPrzedrostka.test(ulica) ? `ul. ${ulica}` : ulica, numer]
        .filter(Boolean).join(' ');
      // Lustro rekordu firmy: czego firma nie ma, to zostaje PUSTE. Trzymanie
      // wartości z data() podstawiałoby NIP i adres BTP pod dokumenty innej spółki.
      const nowe = {
        applicantName: String(firma.name || '').trim(),
        applicantNip: nip.length === 10 ? nip : '',
        applicantAddress: adres,
        applicantZipCode: String(firma.zipCode || '').trim(),
        applicantCity: String(firma.city || '').trim(),
      };
      Object.keys(nowe).forEach((pole) => {
        // Nie nadpisujemy tego, co operator zdążył już poprawić ręcznie.
        if (this.form[pole] === this.initialForm[pole]) this.form[pole] = nowe[pole];
        // Snapshot też, żeby „Nowy komplet" nie cofał do danych wpisanych w kodzie.
        this.initialForm[pole] = nowe[pole];
      });
    },
    requiredFieldLabel(expression) {
      const labels = {
        licenseNumber: 'numer licencji', copiesCount: 'liczba wypisów', licensePeriod: 'okres licencji',
        changeDescription: 'opis zmiany', matterDescription: 'opis sprawy',
        companyLegalForm: 'forma prawna firmy', companyProvince: 'województwo firmy',
        companyRepresentationBody: 'organ reprezentujący firmę', companyRepresentationMethod: 'sposób reprezentacji firmy',
        'declarant.name': 'imię i nazwisko osoby składającej oświadczenie',
        'declarant.pesel': 'PESEL osoby składającej oświadczenie',
        'declarant.birthDate': 'data urodzenia osoby składającej oświadczenie',
        'declarant.address': 'adres osoby składającej oświadczenie',
        'declarant.streetAddress': 'adres osoby składającej oświadczenie',
        'drivers[].name': 'imię i nazwisko każdego kierowcy',
        'drivers[].drivingLicenseNumber': 'numer prawa jazdy każdego kierowcy',
        'vehicles[].brand': 'marka każdego pojazdu', 'vehicles[].brandModel': 'marka i model pojazdu',
        'vehicles[].model': 'model każdego pojazdu', 'vehicles[].plate': 'numer rejestracyjny każdego pojazdu',
        'vehicles[].vin': 'VIN każdego pojazdu', 'vehicles[].firstRegDate': 'data pierwszej rejestracji każdego pojazdu',
        'vehicles[].legalTitle': 'tytuł prawny do każdego pojazdu', 'vehicles[].legalTitleOfficial': 'tytuł prawny do pojazdu',
        'vehicles[].purpose': 'przeznaczenie pojazdu', vehiclesCount: 'liczba pojazdów',
        'flags.vehicleTechnicalInspection': 'potwierdź ważne badanie techniczne w sekcji załączników',
        'flags.vehicleHasTaximeter': 'zaznacz taksometr', 'flags.vehicleUsesApp': 'zaznacz aplikację do rozliczania przejazdów',
        'principal.name': 'imię i nazwisko mocodawcy', 'principal.addressPesel': 'adres i PESEL mocodawcy',
        'attorney.name': 'imię i nazwisko pełnomocnika', 'attorney.pesel': 'PESEL pełnomocnika',
        'attorney.addressRelationship': 'adres pełnomocnika',
      };
      return String(expression).split('|').map((path) => labels[path] || 'uzupełnij dane sprawy').join(' lub ');
    },
    getConfig() {
      return {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token'),
          'x-current-page': 'official-documents',
        },
      };
    },
    async loadFleetVehicles() {
      this.fleetVehiclesLoading = true;
      this.fleetVehiclesError = '';
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'cars/for-documents',
          ...this.getConfig(),
        });
        // `cars/for-documents` oddaje flote firmowa ORAZ auta prywatne kierowcow.
        // Zwykly `GET /cars` ma na sztywno `isCompanyCar: true` i pokazywal 19 aut
        // z 414 — aut kierowcow nie dalo sie wybrac na wniosku.
        const rows = (res.data && res.data.data) || [];
        this.fleetVehicleOptions = rows.map((car) => {
          const nazwa = [car.make, car.model].map((v) => String(v || '').trim()).filter(Boolean).join(' ');
          return {
            id: car.id,
            isCompanyCar: car.isCompanyCar !== false,
            ownerName: String(car.ownerName || '').trim(),
            // Przy aucie kierowcy nazwisko wlasciciela jest wazniejsze niz marka —
            // po nim operator szuka, bo wniosek dotyczy konkretnej osoby.
            text: car.isCompanyCar === false && car.ownerName
              ? `${String(car.plate || '').toUpperCase()} — ${nazwa} · ${String(car.ownerName).trim()}`
              : `${String(car.plate || '').toUpperCase()} — ${nazwa}`,
            brand: String(car.make || '').trim(),
            model: String(car.model || '').trim(),
            plate: String(car.plate || '').toUpperCase(),
            vin: String(car.vin || '').toUpperCase(),
            year: car.productionYear || '',
          };
        }).sort((a, b) => a.text.localeCompare(b.text, 'pl'));
      } catch (e) {
        console.error(e);
        this.fleetVehicleOptions = [];
        this.fleetVehiclesError = 'Nie udało się pobrać listy aut.';
      } finally {
        this.fleetVehiclesLoading = false;
      }
    },
    // Jedna lista z serwera, dwa widoki: „Nasza flota" i „Auto kierowcy".
    opcjeAut(zrodlo) {
      if (zrodlo === 'DRIVER') return this.fleetVehicleOptions.filter((a) => !a.isCompanyCar);
      return this.fleetVehicleOptions.filter((a) => a.isCompanyCar);
    },
    applyFleetVehicle(vehicleId, target = null) {
      if (!vehicleId) return;
      const selected = this.fleetVehicleOptions.find((vehicle) => vehicle.id === vehicleId);
      if (!selected) return;
      if (target) {
        Object.assign(target, {
          brand: selected.brand,
          model: selected.model,
          plate: selected.plate,
          vin: selected.vin,
          year: selected.year,
        });
        return;
      }
      Object.assign(this.form, {
        vehicleBrand: selected.brand,
        vehicleModel: selected.model,
        vehiclePlate: selected.plate,
        vehicleVin: selected.vin,
        vehicleYear: selected.year,
      });
    },
    setWorkspace(workspace) {
      if (!['generator', 'history', 'registry'].includes(workspace)) return;
      this.activeWorkspace = workspace;
    },
    startWizard() {
      // Kreator zawsze generuje komplet do historii (tryb biurowy); jeśli
      // w kroku "Dane" zostanie wybrany kierowca z systemu, komplet trafi
      // dodatkowo do jego aplikacji.
      this.form.recipientMode = 'OFFICE';
      this.form.docKind = 'WNIOSEK';
      this.wizardStep = 1;
      this.wizardMaxStep = Math.max(this.wizardMaxStep, 1);
    },
    goWizardStep(step) {
      if (this.operationBusy || step < 1 || step > 5 || step > this.wizardMaxStep + 1) return;
      if (step === 4 && !this.wizardIntent) return;
      this.wizardStep = step;
      this.wizardMaxStep = Math.max(this.wizardMaxStep, step);
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
    },
    resetWizard(step) {
      if (this.operationBusy) return;
      this.form = JSON.parse(JSON.stringify(this.initialForm));
      this.licenseAutofilled = '';
      this.licenseLookup = { status: '', licencja: null };
      this.personRequestId += 1;
      this.personLoading = false;
      this.personOptions = [];
      this.personSearch = '';
      this.wizardCitySearch = '';
      this.emailDialog = false;
      this.emailForm = { email: '', name: '', subject: '', message: '' };
      this.additionalVehicles = [];
      this.additionalDrivers = [];
      this.generatedDocs = [];
      this.wizardIntent = '';
      this.wizardManualCity = false;
      this.attorneyOpen = false;
      this.selectedRegistryCity = null;
      this.wizardStep = step;
      this.wizardMaxStep = step;
      if (step === 1) this.startWizard();
    },
    selectWizardCity(city) {
      if (this.operationBusy) return;
      this.wizardIntent = '';
      this.wizardMaxStep = 1;
      this.form.changeDescription = '';
      this.wizardManualCity = false;
      this.selectedRegistryCity = city;
      this.applyRegistryCity();
      this.goWizardStep(2);
    },
    confirmManualOffice() {
      if (!this.builderStepTwoReady) return;
      this.wizardIntent = '';
      this.wizardMaxStep = 1;
      this.selectedRegistryCity = null;
      this.form.officialProcedure = '';
      this.form.conditionalForms = {};
      this.form.extraForms = {};
      this.goWizardStep(2);
    },
    selectWizardIntent(intent) {
      if (this.operationBusy) return;
      this.wizardMaxStep = 2;
      this.wizardIntent = intent;
      const f = this.form;
      const available = ((this.selectedRegistryEntry && this.selectedRegistryEntry.procedures) || [])
        .map((procedure) => procedure.value);
      const pick = (...preferred) => preferred.find((value) => available.includes(value)) || '';
      Object.keys(f.changeFlags).forEach((key) => { f.changeFlags[key] = false; });
      Object.keys(f.attachmentFlags).forEach((key) => { f.attachmentFlags[key] = false; });
      f.hasDrivers = false;
      f.ownerDrives = false;
      f.vehicleIsReplacement = false;
      if (intent === 'NEW_LICENSE') {
        f.changeIncludesVehicle = true;
        f.changeDescription = '';
        if (!f.licenseArea) f.licenseArea = this.selectedRegistryCity || f.officeCity;
        this.selectOfficialProcedure(pick('NEW_LICENSE'), intent);
      } else if (intent === 'ADD_VEHICLE') {
        f.changeIncludesVehicle = true;
        // Wykaz pojazdów jest obowiązkowym załącznikiem zgłoszenia pojazdu —
        // wcześniej kratka przy „wykaz pojazdów samochodowych TAXI zgłaszanych
        // do licencji (załącznik nr 3)" zostawała pusta (uwagi z testów 01.09.2026).
        f.attachmentFlags.attachmentVehiclesList = true;
        f.changeDescription = 'Dopisanie pojazdu do licencji i wydanie wypisu.';
        this.selectOfficialProcedure(pick('ADD_VEHICLE', 'LICENSE_CHANGE', 'CHANGE_LICENSE'), intent);
      } else if (intent === 'ADD_DRIVER') {
        f.hasDrivers = true;
        f.changeIncludesVehicle = false;
        f.changeFlags.changeDriver = true;
        f.attachmentFlags.attachmentDriversList = true;
        f.changeDescription = 'Zgłoszenie kierowcy do licencji.';
        this.selectOfficialProcedure(pick('LICENSE_CHANGE', 'CHANGE_LICENSE'), intent);
      } else if (intent === 'ADD_BOTH') {
        f.hasDrivers = true;
        f.changeIncludesVehicle = true;
        f.changeFlags.changeDriver = true;
        f.attachmentFlags.attachmentDriversList = true;
        f.attachmentFlags.attachmentVehiclesList = true;
        f.changeDescription = 'Dopisanie pojazdu i zgłoszenie kierowcy do licencji.';
        this.selectOfficialProcedure(pick('LICENSE_CHANGE', 'CHANGE_LICENSE', 'ADD_VEHICLE'), intent);
      } else if (intent === 'REMOVE_DRIVER') {
        f.hasDrivers = true;
        f.changeIncludesVehicle = false;
        f.changeFlags.changeDriver = true;
        f.attachmentFlags.attachmentDriversList = false;
        f.changeDescription = 'Wykreślenie wskazanych osób lub pojazdów z licencji — dane w załączonym wykazie.';
        this.selectOfficialProcedure(pick('LICENSE_CHANGE', 'CHANGE_LICENSE'), intent);
      } else if (intent === 'REMOVE_VEHICLE') {
        f.changeIncludesVehicle = true;
        f.vehicleIsReplacement = false;
        f.attachmentFlags.attachmentVehiclesList = false;
        f.changeDescription = 'Wykreślenie wskazanych osób lub pojazdów z licencji — dane w załączonym wykazie.';
        this.selectOfficialProcedure(pick('LICENSE_CHANGE', 'CHANGE_LICENSE'), intent);
      }
      if (this.needsDriverDetails) f.hasDrivers = true;
      if (this.showDriverFields && !f.person && !this.additionalDrivers.length) this.addDriverRow();
      this.goWizardStep(3);
    },
    async generateAttorney() {
      const f = this.form;
      if (this.attorneyGenerating || !this.attorneyFormValid) return;
      this.attorneyGenerating = true;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'document-generator/batch',
          data: {
            documents: [{
              type: 'PELNOMOCNICTWO',
              caseType: 'URZAD',
              ...(f.person ? { driverId: f.person.id } : {}),
              data: this.buildData('PELNOMOCNICTWO'),
              formSnapshot: JSON.parse(JSON.stringify(this.form)),
            }],
          },
          ...this.getConfig(),
        });
        const created = res.data?.data || res.data;
        if (Array.isArray(created) && created.length) {
          this.generatedDocs = [...this.generatedDocs, ...created];
        } else {
          await this.loadDocs();
          throw new Error('Sprawdź historię — serwer nie zwrócił identyfikatora pełnomocnictwa.');
        }
        this.attorneyOpen = false;
        Swal.fire({ icon: 'success', title: 'Pełnomocnictwo dodane do kompletu', timer: 1800, showConfirmButton: false });
        this.loadDocs();
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', e.response?.data?.message || String(e), 'error');
      } finally {
        this.attorneyGenerating = false;
      }
    },
    async printDoc(doc) {
      if (this.printingId) return;
      this.printingId = doc.id;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'document-generator/' + doc.id + '/preview',
          responseType: 'blob',
          ...this.getConfig(),
        });
        const url = URL.createObjectURL(res.data);
        const frame = document.createElement('iframe');
        frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        frame.src = url;
        frame.onload = () => {
          try {
            frame.contentWindow.focus();
            frame.contentWindow.print();
          } catch (err) {
            window.open(url, '_blank');
          }
          window.setTimeout(() => {
            frame.remove();
            URL.revokeObjectURL(url);
          }, 60000);
        };
        document.body.appendChild(frame);
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', 'Nie udało się przygotować wydruku', 'error');
      } finally {
        this.printingId = null;
      }
    },
    async printAll() {
      if (this.printingId || !this.printableDocs.length) return;
      const popup = window.open('about:blank', '_blank');
      this.printingId = 'ALL';
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'document-generator/print-package',
          data: { documentIds: this.printableDocs.map((doc) => doc.id) },
          responseType: 'blob', ...this.getConfig(),
        });
        this.openBlobInNewTab(res.data, popup);
      } catch (e) {
        if (popup && !popup.closed) popup.close();
        Swal.fire('Nie udało się otworzyć kompletu', 'Spróbuj ponownie. Możesz też otworzyć i wydrukować pojedyncze dokumenty z listy.', 'error');
      } finally {
        this.printingId = null;
      }
    },
    openEmailDialog() {
      if (!this.generatedDocs.length) return;
      this.emailForm.email = '';
      this.emailForm.name = this.form.attorneyName || '';
      this.emailForm.subject = `Dokumenty do urzędu — ${this.form.officeName || this.form.officeCity}`.slice(0, 200);
      this.emailForm.message = 'Dzień dobry,\n\nw załącznikach komplet dokumentów do złożenia w urzędzie:\n'
        + this.generatedDocs.map((doc) => `• ${this.documentTypeLabel(doc)}`).join('\n')
        + `\n\nUrząd: ${this.form.officeName}${this.form.officeAddress ? ', ' + this.form.officeAddress : ''}, ${this.form.officeCity}.`
        + '\n\nPozdrawiamy,\nBTP Partner';
      this.emailDialog = true;
    },
    async sendPackageEmail() {
      if (this.emailSending || !this.emailFormValid) return;
      this.emailSending = true;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'document-generator/send-email',
          data: {
            documentIds: this.generatedDocs.map((doc) => doc.id),
            email: this.emailForm.email.trim(),
            recipientName: this.emailForm.name.trim() || undefined,
            subject: this.emailForm.subject.trim(),
            message: this.emailForm.message,
          },
          ...this.getConfig(),
        });
        this.emailDialog = false;
        Swal.fire({ icon: 'success', title: 'Komplet wysłany', text: this.emailForm.email, timer: 2500, showConfirmButton: false });
      } catch (e) {
        console.error(e);
        const status = e.response?.status;
        Swal.fire(
          'Błąd wysyłki',
          status === 404 || status === 405
            ? 'Wysyłka kompletu jest chwilowo niedostępna. Pobierz dokumenty lub spróbuj ponownie później.'
            : e.response?.data?.message || String(e),
          'error',
        );
      } finally {
        this.emailSending = false;
      }
    },
    setBuilderStep(step) {
      if (![1, 2, 3].includes(step)) return;
      this.builderStep = this.builderStep === step ? 0 : step;
    },
    toggleDetailSection(section) {
      this.activeDetailSection = this.activeDetailSection === section ? '' : section;
    },
    valuesAtDataPath(current, parts) {
      if (!parts.length) return [current];
      if (current === null || current === undefined || typeof current !== 'object') return [];
      const [part, ...rest] = parts;
      if (part.endsWith('[]')) {
        const rows = current[part.slice(0, -2)];
        if (!Array.isArray(rows)) return [];
        return rows.flatMap((row) => {
          const resolved = this.valuesAtDataPath(row, rest);
          return resolved.length ? resolved : [undefined];
        });
      }
      return this.valuesAtDataPath(current[part], rest);
    },
    hasRequiredDataValue(data, valuePath) {
      const serverProvidedPaths = new Set([
        'companyName',
        'companyAddress',
        'companyNip',
        'companyAddressLine1',
        'companyAddressLine2',
        'companyStreet',
        'companyBuildingNumber',
        'companyUnitNumber',
        'companyZipCode',
        'companyCity',
      ]);
      if (serverProvidedPaths.has(String(valuePath))) return true;
      const values = this.valuesAtDataPath(data, String(valuePath).split('.'));
      const isPresent = (value) => (typeof value === 'boolean'
        ? value
        : value !== undefined && value !== null && String(value).trim() !== '');
      return String(valuePath).includes('[]')
        ? values.length > 0 && values.every(isPresent)
        : values.some(isPresent);
    },
    normalizeOfficialReadinessData(input) {
      const data = input || {};
      const vehicles = Array.isArray(data.vehicles)
        ? data.vehicles.map((item) => {
          const vehicle = item || {};
          const brand = String(vehicle.brand || '').trim();
          const model = String(vehicle.model || '').trim();
          const legalTitle = String(vehicle.legalTitle || '').trim();
          const legalTitleOfficial = {
            'umowa najmu': 'najem',
            'umowa użyczenia': 'użyczenie',
            współwłasność: 'współwłasność',
            własność: 'własność',
            leasing: 'leasing',
            dzierżawa: 'dzierżawa',
          }[legalTitle.toLocaleLowerCase('pl-PL')] || legalTitle;
          return {
            ...vehicle,
            brand,
            model,
            brandModel: [brand, model].filter(Boolean).join(' '),
            legalTitleOfficial,
          };
        })
        : data.vehicles;
      const drivers = Array.isArray(data.drivers)
        ? data.drivers.map((item) => {
          const driver = item || {};
          const suppliedName = String(driver.name || '').trim();
          const nameParts = suppliedName.split(/\s+/).filter(Boolean);
          const firstName = String(driver.firstName || '').trim() || nameParts[0] || '';
          const lastName = String(driver.lastName || '').trim() || nameParts.slice(1).join(' ');
          return {
            ...driver,
            firstName,
            lastName,
            name: suppliedName || [firstName, lastName].filter(Boolean).join(' '),
          };
        })
        : data.drivers;
      const declarantInput = data.declarant || {};
      const declarantAddress = String(declarantInput.address || '').trim()
        || [declarantInput.street, declarantInput.cityLine]
          .map((value) => String(value || '').trim())
          .filter(Boolean)
          .join(', ');
      return {
        ...data,
        declarant: {
          ...declarantInput,
          address: declarantAddress,
          streetAddress: String(declarantInput.streetAddress || '').trim() || declarantAddress,
        },
        vehicles,
        drivers,
        vehiclesCount: data.vehiclesCount ?? (Array.isArray(vehicles) ? vehicles.length : 0),
        driversCount: data.driversCount ?? (Array.isArray(drivers) ? drivers.length : 0),
        driversTotalAfterChange: data.driversTotalAfterChange ?? '',
        vehiclesTotalAfterChange: data.vehiclesTotalAfterChange ?? '',
      };
    },
    officialTemplateReady(template) {
      const requirements = (template && template.requiredDataPaths) || [];
      if (!requirements.length) return true;
      const data = this.normalizeOfficialReadinessData(this.buildData(template.type));
      return requirements.every((expression) => String(expression)
        .split('|')
        .some((valuePath) => this.hasRequiredDataValue(data, valuePath)));
    },
    addVehicleRow() {
      if (this.additionalVehicles.length >= this.vehicleCapacity - 1) return;
      this.additionalVehicles.push({
        source: 'FLEET',
        fleetVehicleId: null,
        brand: '',
        model: '',
        plate: '',
        vin: '',
        year: '',
        firstRegDate: '',
        seats: '',
        dmc: '',
        legalTitle: this.form.legalTitle || 'umowa najmu',
        purpose: this.form.vehiclePurpose || 'samochód osobowy / TAXI',
      });
    },
    removeVehicleRow(index) {
      this.additionalVehicles.splice(index, 1);
    },
    addDriverRow() {
      if (this.additionalDrivers.length >= this.driverCapacity - (this.form.person ? 1 : 0)) return;
      this.additionalDrivers.push({
        source: 'FLEET',
        driverId: null,
        options: [],
        loading: false,
        search: '',
        searchTimer: null,
        name: '',
        pesel: '',
        birthDate: '',
        drivingLicenseNumber: '',
        drivingLicenseValidTo: '',
        drivingLicenseIndefinite: false,
        medicalValidTo: '',
        medicalCertificateNumber: '',
        psychologicalValidTo: '',
        psychologicalCertificateNumber: '',
        criminalRecordDate: '',
        isEuCitizen: true,
      });
    },
    removeDriverRow(index) {
      clearTimeout((this.additionalDrivers[index] || {}).searchTimer);
      this.additionalDrivers.splice(index, 1);
    },
    // Kierowcow jest ponad 150, a lista z serwera potrafi isc poltorej sekundy,
    // wiec nie wciagamy calej kartoteki jak przy autach — szukamy po nazwisku,
    // tak samo jak dziala wybor pierwszego kierowcy.
    searchDriverRow(row, text) {
      clearTimeout(row.searchTimer);
      const fraza = String(text || '').trim();
      if (fraza.length < 2) { row.options = []; return; }
      row.searchTimer = setTimeout(async () => {
        row.loading = true;
        try {
          const res = await axios({
            method: 'get',
            url: `${process.env.VUE_APP_ROOT_API}drivers?skip=1&take=15&search=${encodeURIComponent(fraza)}`,
            ...this.getConfig(),
          });
          row.options = ((res.data && res.data.data && res.data.data.result) || []).map((d) => ({
            id: d.id,
            text: `${d.firstName} ${d.lastName}${d.city ? ' — ' + d.city : ''}`,
            name: `${d.firstName || ''} ${d.lastName || ''}`.trim(),
            pesel: d.pesel || '',
            birthDate: String(d.dateOfBirth || '').slice(0, 10),
            drivingLicenseNumber: d.licenseNumber || '',
            drivingLicenseValidTo: String(d.drivingLicenseValidTo || '').slice(0, 10),
            drivingLicenseIndefinite: !!d.drivingLicenseIndefinite,
            medicalCertificateNumber: d.medicalCertificateNumber || '',
            medicalValidTo: String(d.medicalValidTo || '').slice(0, 10),
            psychologicalCertificateNumber: d.psychologicalCertificateNumber || '',
            psychologicalValidTo: String(d.psychologicalValidTo || '').slice(0, 10),
            criminalRecordDate: String(d.criminalRecordDate || '').slice(0, 10),
          }));
        } catch (e) {
          console.error(e);
          row.options = [];
        } finally {
          row.loading = false;
        }
      }, 350);
    },
    applyFleetDriver(driverId, row) {
      if (!driverId) return;
      const wybrany = (row.options || []).find((d) => d.id === driverId);
      if (!wybrany) return;
      // W kartotece zwykle brakuje PESEL-u i numeru prawa jazdy (23.09.2026:
      // PESEL ma 8 osob na 155, numer prawa jazdy 1 na 155), wiec nadpisujemy
      // WYLACZNIE tym, co naprawde jest. Inaczej wybor kierowcy kasowalby to,
      // co operator zdazyl juz wpisac recznie.
      ['name', 'pesel', 'birthDate', 'drivingLicenseNumber', 'drivingLicenseValidTo',
        'medicalCertificateNumber', 'medicalValidTo', 'psychologicalCertificateNumber',
        'psychologicalValidTo', 'criminalRecordDate'].forEach((pole) => {
        if (wybrany[pole]) row[pole] = wybrany[pole];
      });
      // Bezterminowe prawo jazdy przepisujemy zawsze — brak daty waznosci jest
      // informacja, a nie brakiem danych.
      if (wybrany.drivingLicenseIndefinite) {
        row.drivingLicenseIndefinite = true;
        row.drivingLicenseValidTo = '';
      }
    },
    handleVehicleChangeToggle(enabled) {
      const defaultDescription = 'Dopisanie pojazdu do licencji i wydanie wypisu.';
      if (!enabled && this.form.changeDescription === defaultDescription) {
        this.form.changeDescription = '';
      } else if (enabled && !String(this.form.changeDescription || '').trim()) {
        this.form.changeDescription = defaultDescription;
      }
      if (this.isGrudziadzChange) {
        this.setConditionalForm('GRUDZIADZ_TAXI_WYKAZ', enabled);
      }
    },
    setRecipientMode(mode) {
      if (this.form.recipientMode === mode) return;
      this.form.recipientMode = mode;
      if (mode === 'OFFICE') {
        this.form.person = null;
        this.form.docKind = 'WNIOSEK';
      }
    },
    setExtraForm(type, enabled) {
      this.$set(this.form.extraForms, type, Boolean(enabled));
    },
    selectOfficialProcedure(value, intent = null) {
      this.form.officialProcedure = value;
      this.form.conditionalForms = {};
      this.form.extraForms = {};
      const procedure = ((this.selectedRegistryEntry && this.selectedRegistryEntry.procedures) || [])
        .find((item) => item.value === value);
      const templates = (procedure && procedure.templates) || [];
      templates
        .filter((template) => template.conditional && template.defaultIncluded)
        .forEach((template) => this.$set(this.form.conditionalForms, template.type, true));
      // Wykazy z katalogu są oznaczone jako `conditional` i prawie nigdzie nie
      // mają defaultIncluded — więc bez tego kroku kreator NIGDY ich nie
      // generował (wykaz kierowców, wykaz pojazdów, zgłoszenie kierowcy).
      // Sprawa, którą wybrał użytkownik, mówi wprost, które wykazy są potrzebne.
      const wantsDrivers = ['ADD_DRIVER', 'ADD_BOTH', 'NEW_LICENSE', 'REMOVE_DRIVER'].includes(intent);
      const wantsVehicles = ['ADD_VEHICLE', 'ADD_BOTH', 'NEW_LICENSE', 'REMOVE_VEHICLE'].includes(intent);
      const isDriversList = (tpl) => /kierowc|drivers|identyfikator/i.test(`${tpl.type} ${tpl.name || ''}`);
      const isVehiclesList = (tpl) => /pojazd|vehicle|wykaz.*auto/i.test(`${tpl.type} ${tpl.name || ''}`);
      templates
        .filter((template) => template.conditional)
        .forEach((template) => {
          if (wantsDrivers && isDriversList(template)) this.$set(this.form.conditionalForms, template.type, true);
          if (wantsVehicles && isVehiclesList(template)) this.$set(this.form.conditionalForms, template.type, true);
        });
      if (this.isGrudziadzChange) {
        this.$set(this.form.conditionalForms, 'GRUDZIADZ_TAXI_WYKAZ', this.form.changeIncludesVehicle);
      }
    },
    setConditionalForm(type, enabled) {
      const included = Boolean(enabled);
      this.$set(this.form.conditionalForms, type, included);
      if (
        type === 'GRUDZIADZ_TAXI_WYKAZ'
        && this.isGrudziadzChange
        && this.form.changeIncludesVehicle !== included
      ) {
        this.form.changeIncludesVehicle = included;
        const defaultDescription = 'Dopisanie pojazdu do licencji i wydanie wypisu.';
        if (!included && this.form.changeDescription === defaultDescription) {
          this.form.changeDescription = '';
        } else if (included && !String(this.form.changeDescription || '').trim()) {
          this.form.changeDescription = defaultDescription;
        }
      }
    },
    applyRegistryCity() {
      const entry = this.selectedRegistryEntry;
      if (!entry) return;
      this.form.officeName = entry.office;
      this.form.officeCity = entry.city;
      const procedures = entry.procedures || [];
      if (procedures.length && !procedures.some((item) => item.value === this.form.officialProcedure)) {
        this.form.officialProcedure = procedures[0].value;
      }
      if (!procedures.length) this.form.officialProcedure = '';
      this.form.conditionalForms = {};
      this.form.extraForms = {};
      const selectedProcedure = procedures.find((item) => item.value === this.form.officialProcedure);
      ((selectedProcedure && selectedProcedure.templates) || [])
        .filter((template) => template.conditional && template.defaultIncluded)
        .forEach((template) => this.$set(this.form.conditionalForms, template.type, true));
      if (entry.city === 'Grudziądz' && this.isLicenseChange) {
        this.$set(this.form.conditionalForms, 'GRUDZIADZ_TAXI_WYKAZ', this.form.changeIncludesVehicle);
      }
      this.form.officeAddress = entry.address || (entry.city === 'Grudziądz'
        ? 'ul. Piłsudskiego 51, 86-300 Grudziądz'
        : '');
      this.form.licenseArea = entry.city === 'Grudziądz'
        ? 'Gmina - miasto Grudziądz'
        : entry.city;
      this.podstawLicencje(entry.city);
    },
    /**
     * Numer licencji z listy „Licencje TAXI" zamiast przepisywania go
     * ręcznie przy każdym wniosku. Nie nadpisuje tego, co operator już wpisał
     * — poprawka zrobiona świadomie ma pierwszeństwo nad bazą.
     *
     * Świadomie NIE blokujemy generowania, gdy licencji brak albo jest po
     * terminie: wniosek bywa składany właśnie po to, żeby licencję odnowić.
     * Ostrzegamy i zostawiamy decyzję operatorowi.
     */
    async podstawLicencje(city) {
      this.licenseLookup = { status: '', licencja: null };
      if (!city) return;
      try {
        const res = await axios({
          method: 'GET',
          url: `${process.env.VUE_APP_ROOT_API}company-licenses/for-city`,
          params: { city },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'x-current-page': 'official-documents',
          },
        });
        const wynik = (res.data && res.data.data) || {};
        this.licenseLookup = {
          status: wynik.status || '',
          licencja: wynik.licencja || null,
        };
        const obecny = String(this.form.licenseNumber || '').trim();
        // Nadpisujemy tylko wtedy, gdy pole jest puste albo trzyma numer,
        // który sami wstawiliśmy dla wcześniej wybranego miasta. Ręczną
        // poprawkę operatora zostawiamy nietkniętą.
        const nasze = obecny && obecny === this.licenseAutofilled;
        if (wynik.licencja && (!obecny || nasze)) {
          this.form.licenseNumber = wynik.licencja.licenseNumber;
          this.licenseAutofilled = wynik.licencja.licenseNumber;
        } else if (!wynik.licencja && nasze) {
          // Nowe miasto nie ma licencji — zostawienie numeru z poprzedniego
          // urzędu byłoby gorsze niż puste pole.
          this.form.licenseNumber = '';
          this.licenseAutofilled = '';
        }
      } catch (e) {
        // Brak dostępu albo błąd sieci nie może zatrzymać kreatora —
        // operator wpisze numer ręcznie, tak jak przed tą zmianą.
        this.licenseLookup = { status: '', licencja: null };
      }
    },
    async loadOfficialCatalog() {
      this.catalogLoading = true;
      this.catalogError = '';
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'document-generator/official-forms/catalog',
          ...this.getConfig(),
        });
        const offices = res.data && res.data.offices;
        if (!Array.isArray(offices) || !offices.length) throw new Error('Katalog urzędów jest pusty');
        this.cityRegistry = offices;
        this.officialCatalogVerifiedAt = res.data.verifiedAt || '';
      } catch (e) {
        console.error(e);
        this.catalogError = 'Nie udało się odświeżyć katalogu — pokazuję lokalną listę awaryjną.';
      } finally {
        this.catalogLoading = false;
      }
    },
    useRegistryCity(city) {
      if (this.wizardStep === 5) this.resetWizard(1);
      if (this.wizardStep === 0) this.startWizard();
      this.activeWorkspace = 'generator';
      this.selectWizardCity(city);
    },
    scrollToRegistry() {
      this.activeWorkspace = 'registry';
      this.$nextTick(() => {
        const registry = this.$refs.officeRegistry;
        if (registry && typeof registry.focus === 'function') registry.focus({ preventScroll: true });
        if (registry) registry.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    formatDateTime(d) {
      if (!d) return '—';
      return new Date(d).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
    },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    formatTime(d) {
      if (!d) return '';
      return new Date(d).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    },
    documentTypeLabel(item) {
      if (!item) return 'Dokument';
      if (item.type === 'PELNOMOCNICTWO') return 'Pełnomocnictwo';
      if (item.type === 'URZAD_DRUK') return 'Druk urzędu';
      if (item.data && item.data.displayName) return item.data.displayName;
      if (item.type === 'BYDGOSZCZ_TAXI_ZMIANY') return 'Zgłoszenie zmian do licencji';
      if (item.type === 'BYDGOSZCZ_TAXI_WYKAZ') return 'Wykaz pojazdów';
      if (item.type === 'GRUDZIADZ_TAXI_WNIOSEK') return 'Wniosek o licencję TAXI';
      if (item.type === 'GRUDZIADZ_TAXI_REPUTACJA') return 'Oświadczenie o dobrej reputacji';
      if (item.type === 'GRUDZIADZ_TAXI_WYKAZ') return 'Wykaz pojazdów';
      if (item.type === 'GRUDZIADZ_TAXI_ZMIANA') return 'Zmiana licencji TAXI';
      return 'Wniosek o wypis';
    },
    documentTypeIcon(type) {
      if (type === 'PELNOMOCNICTWO') return 'mdi-account-tie-outline';
      if (type === 'URZAD_DRUK') return 'mdi-file-certificate-outline';
      if (type === 'BYDGOSZCZ_TAXI_WYKAZ') return 'mdi-format-list-bulleted-square';
      if (type === 'GRUDZIADZ_TAXI_REPUTACJA') return 'mdi-shield-account-outline';
      if (type === 'GRUDZIADZ_TAXI_WYKAZ') return 'mdi-format-list-bulleted-square';
      if (type === 'GRUDZIADZ_TAXI_ZMIANA') return 'mdi-file-document-refresh-outline';
      return 'mdi-file-document-edit-outline';
    },
    documentTone(type) {
      if (type === 'PELNOMOCNICTWO') return 'violet';
      if (type === 'URZAD_DRUK') return 'amber';
      if (type === 'BYDGOSZCZ_TAXI_ZMIANY' || type === 'BYDGOSZCZ_TAXI_WYKAZ') return 'green';
      if (String(type || '').startsWith('GRUDZIADZ_TAXI_')) return 'green';
      return 'blue';
    },
    documentPerson(item) {
      return (item && item.data && (item.data.attorneyName || item.data.personName))
        || (item && !item.driverId ? 'Biuro BTP (dokument firmowy)' : 'Nie wskazano');
    },
    documentOffice(item) {
      if (!item || !item.data) return 'Nie wskazano';
      const name = item.data.officeName || '';
      const city = item.data.officeCity || '';
      return [name, city && name.indexOf(city) === -1 ? city : ''].filter(Boolean).join(' · ') || 'Nie wskazano';
    },
    personInitials(name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return '?';
      return `${parts[0].charAt(0)}${parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''}`.toUpperCase();
    },
    cityInitials(city) {
      return String(city || '').trim().slice(0, 2).toUpperCase();
    },
    async searchPersons(search) {
      const requestId = ++this.personRequestId;
      this.personLoading = true;
      this.personSearchError = '';
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'drivers?skip=1&take=15&search=' + encodeURIComponent(search),
          ...this.getConfig(),
        });
        if (requestId !== this.personRequestId) return;
        const rows = res.data?.data?.result || [];
        this.personOptions = rows.map((d) => ({
          pesel: d.pesel, dateOfBirth: d.dateOfBirth, licenseNumber: d.licenseNumber,
          drivingLicenseValidTo: String(d.drivingLicenseValidTo || '').slice(0, 10),
          drivingLicenseIndefinite: !!d.drivingLicenseIndefinite,
          medicalCertificateNumber: d.medicalCertificateNumber || '',
          medicalValidTo: String(d.medicalValidTo || '').slice(0, 10),
          psychologicalCertificateNumber: d.psychologicalCertificateNumber || '',
          psychologicalValidTo: String(d.psychologicalValidTo || '').slice(0, 10),
          criminalRecordDate: String(d.criminalRecordDate || '').slice(0, 10),
          text: `${d.firstName} ${d.lastName}${d.accountType === 'SUBFLEET_OWNER' ? ' (właściciel aut)' : ''}${d.city ? ' — ' + d.city : ''}`,
          id: d.id,
          firstName: d.firstName,
          lastName: d.lastName,
        }));
      } catch (e) {
        console.error(e);
        if (requestId === this.personRequestId) this.personSearchError = 'Nie udało się wyszukać kierowców. Spróbuj ponownie lub wpisz dane ręcznie.';
      } finally {
        if (requestId === this.personRequestId) this.personLoading = false;
      }
    },
    // Wspólne pola obu dokumentów, budowane z formularza.
    buildData(type) {
      const f = this.form;
      const person = f.person || {};
      const personName = `${person.firstName || ''} ${person.lastName || ''}`.trim();
      const today = new Date().toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const primaryVehicle = {
        brand: f.vehicleBrand,
        model: f.vehicleModel || '',
        plate: String(f.vehiclePlate || '').trim().toUpperCase(),
        vin: String(f.vehicleVin || '').trim().toUpperCase(),
        year: f.vehicleYear,
        purpose: f.vehiclePurpose,
        legalTitle: f.legalTitle,
        firstRegDate: f.firstRegDate,
        seats: f.vehicleSeats || '',
        dmc: f.vehicleDmc || '',
      };
      const vehicleRows = [primaryVehicle, ...this.additionalVehicles]
        .filter((vehicle, index) => index === 0 || [
          vehicle.brand, vehicle.model, vehicle.plate, vehicle.vin,
        ].some((value) => String(value || '').trim()));
      const primaryDriver = {
        name: personName,
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        pesel: f.driverPesel || person.pesel || '',
        birthDate: f.driverBirthDate || person.dateOfBirth || '',
        drivingLicenseNumber: f.driverLicenseNumber || person.licenseNumber || '',
        drivingLicenseValidTo: f.driverLicenseValidTo,
        drivingLicenseIndefinite: f.driverLicenseIndefinite,
        medicalValidTo: f.driverMedicalValidTo,
        medicalCertificateNumber: f.driverMedicalCertificateNumber,
        psychologicalValidTo: f.driverPsychologicalValidTo,
        psychologicalCertificateNumber: f.driverPsychologicalCertificateNumber,
        criminalRecordDate: f.driverCriminalRecordDate,
        isEuCitizen: f.driverIsEuCitizen,
      };
      // Dla biura nie ma osoby-odbiorcy — kierowców podaje się wyłącznie wierszami.
      vehicleRows.forEach((vehicle) => { vehicle.plate = String(vehicle.plate || '').trim().toUpperCase(); vehicle.vin = String(vehicle.vin || '').trim().toUpperCase(); });
      const driverPool = personName
        ? [primaryDriver, ...this.additionalDrivers]
        : this.additionalDrivers;
      const driverRows = this.showDriverFields
        ? driverPool.filter(
          (driver, index) => (personName && index === 0) || String(driver.name || '').trim(),
        )
        : [];
      const base = {
        officialApplicant: {
          name: String(f.applicantName || '').trim(),
          nip: String(f.applicantNip || '').trim(),
          address: String(f.applicantAddress || '').trim(),
          zipCode: String(f.applicantZipCode || '').trim(),
          city: String(f.applicantCity || '').trim(),
        },
        documentDate: today,
        city: f.officeCity || '',
        officeName: f.officeName,
        officeAddress: f.officeAddress,
        officeCity: f.officeCity,
        licenseNumber: f.licenseNumber,
        personName: personName || (this.isOfficeMode ? 'Biuro BTP (dokument firmowy)' : ''),
        driversTotalAfterChange: f.driversTotalAfterChange,
        vehiclesTotalAfterChange: f.vehiclesTotalAfterChange,
      };
      if (type === 'PELNOMOCNICTWO') {
        return {
          ...base,
          principalRep: f.principalRep,
          attorneyName: f.attorneyName || personName,
          attorneyAddress: f.attorneyAddress,
          attorneyPesel: f.attorneyPesel,
          attorneyDocument: f.attorneyDocument,
          scopeSubmit: f.scopeSubmit,
          scopeCollect: f.scopeCollect,
          scopeStatements: f.scopeStatements,
          validUntil: f.validUntil,
        };
      }
      if (type === 'BYDGOSZCZ_TAXI_ZMIANY') {
        // Druk ma osobne kratki dla wykazu kierowców i wykazu pojazdów. Wcześniej
        // szło tu na sztywno „1 pojazd", więc zgłoszenie samych kierowców wychodziło
        // z urzędu jako wniosek o pojazd, a liczba kierowców w ogóle się nie wpisywała.
        const zKierowcami = this.wizardNeedsDriver && driverRows.length > 0;
        const zPojazdami = this.wizardNeedsVehicle && vehicleRows.length > 0;
        const ileZ = (pole, pole2) => driverRows.filter(
          (kierowca) => String(kierowca[pole] || '').trim() || String(kierowca[pole2] || '').trim(),
        ).length;
        return {
          ...base,
          displayName: 'Bydgoszcz — zgłoszenie zmian do licencji (wypełnione)',
          phone: '52 516 11 12',
          vehiclesAdd: zPojazdami ? vehicleRows.length : 0,
          wypisyAdd: zPojazdami ? (f.copiesCount || vehicleRows.length) : 0,
          vehiclesAfter: f.vehiclesTotalAfterChange || f.vehiclesAfter,
          driversAdd: zKierowcami ? driverRows.length : 0,
          driversAfter: f.driversTotalAfterChange,
          medicalAdd: zKierowcami ? ileZ('medicalCertificateNumber', 'medicalValidTo') : 0,
          psychologicalAdd: zKierowcami ? ileZ('psychologicalCertificateNumber', 'psychologicalValidTo') : 0,
          drivingLicenseAdd: zKierowcami && f.attachmentDrivingLicense ? driverRows.length : 0,
          feeAmount: f.feeAmount || (zPojazdami ? this.suggestedFeeAmount : ''),
          documentDate: today,
        };
      }
      if (type === 'BYDGOSZCZ_TAXI_WYKAZ') {
        return {
          ...base,
          displayName: 'Bydgoszcz — wykaz pojazdów, zał. 3 (wypełniony)',
          documentDate: today,
          vehicles: vehicleRows,
        };
      }
      if (String(type || '').startsWith('GRUDZIADZ_TAXI_')) {
        const displayNames = {
          GRUDZIADZ_TAXI_WNIOSEK: 'Grudziądz — wniosek o licencję TAXI',
          GRUDZIADZ_TAXI_REPUTACJA: 'Grudziądz — oświadczenie o dobrej reputacji',
          GRUDZIADZ_TAXI_WYKAZ: 'Grudziądz — wykaz pojazdów',
          GRUDZIADZ_TAXI_ZMIANA: 'Grudziądz — zmiana licencji TAXI',
        };
        return {
          ...base,
          displayName: displayNames[type] || 'Grudziądz — formularz TAXI',
          companyPhone: f.companyPhone,
          companyKrs: f.companyKrs,
          licenseArea: f.licenseArea,
          licensePeriod: f.licensePeriod,
          copiesCount: f.copiesCount,
          criminalRecordCertificatesCount: f.criminalRecordCertificatesCount,
          feeAmount: f.feeAmount,
          declarantName: f.declarantName,
          declarantPesel: f.declarantPesel,
          declarantStreet: f.declarantStreet,
          declarantCityLine: f.declarantCityLine,
          declarantDocument: f.declarantDocument,
          changeDescription: f.changeDescription,
          vehicles: this.isGrudziadzChange && !f.changeIncludesVehicle
            ? []
            : vehicleRows,
        };
      }
      const selectedOfficeForms = (this.selectedRegistryEntry && this.selectedRegistryEntry.forms) || [];
      const officialForm = selectedOfficeForms.find((item) => item.type === type);
      if (officialForm) {
        const legalForm = String(f.companyLegalForm || '').toLocaleLowerCase('pl-PL');
        const isSoleTrader = f.entityType === 'PHYSICAL';
        const isLegalEntity = f.entityType === 'LEGAL';
        const isRegisteredInKrs = f.companyRegistry === 'KRS';
        const isPartnership = legalForm.includes('spółka jawna')
          || legalForm.includes('spółka komandytowa')
          || legalForm.includes('spółka komandytowo-akcyjna')
          || legalForm.includes('spółka partnerska');
        const formRequiresLicenseNumber = (officialForm.requiredDataPaths || [])
          .includes('licenseNumber');
        // Opis liczony RAZ: trafia i do rubryki „inne", i do flagi, która tę
        // rubrykę zaznacza. Liczony osobno w dwóch miejscach rozjechałby się
        // przy pierwszej zmianie — wtedy na druku byłby tekst bez krzyżyka
        // albo krzyżyk bez tekstu.
        const opisInnejZmiany = OPISY_PODSTAWIANE_PRZEZ_KREATOR
          .includes(String(f.changeDescription || '').trim())
          ? ''
          : String(f.changeDescription || '').trim();
        const matterDescription = {
          NEW_LICENSE: 'Uzyskanie licencji na wykonywanie przewozu osób taksówką.',
          LICENSE_CHANGE: f.changeDescription,
          ADD_VEHICLE: `Dopisanie pojazdu do licencji nr ${f.licenseNumber || '—'} i wydanie wypisu.`,
          ADDITIONAL_EXTRACT: `Wydanie dodatkowego wypisu z licencji nr ${f.licenseNumber || '—'}.`,
        }[f.officialProcedure] || f.changeDescription;
        return {
          ...base,
          displayName: officialForm.name,
          officialProcedure: f.officialProcedure,
          licenseArea: f.licenseArea,
          licenseNumber: !this.isNewLicense || formRequiresLicenseNumber
            ? f.licenseNumber
            : '',
          licensePeriod: this.isNewLicense ? f.licensePeriod : '',
          // Część urzędów (Jelenia Góra) chce LICZBĘ LAT, a nie przedział.
          // Pole pokazuje się tylko tam, gdzie druk naprawdę je czyta.
          licenseYears: this.isNewLicense ? String(f.licenseYears || '').trim() : '',
          copiesCount: this.isRemoval && type === 'BYDGOSZCZ_TAXI_LICENSE_CHANGE' ? '' : f.copiesCount,
          feeAmount: f.feeAmount,
          changeDescription: this.isLicenseChange ? f.changeDescription : '',
          // Rubryka „inne" na druku bydgoskim czyta to pole, nie changeDescription.
          otherChangeDescription: opisInnejZmiany,
          companyPhone: f.companyPhone,
          companyKrs: f.companyKrs,
          companyRegon: f.companyRegon,
          companyEmail: f.companyEmail,
          companyLegalForm: f.companyLegalForm,
          companyProvince: f.companyProvince,
          companyRepresentationBody: f.companyRepresentationBody,
          companyRepresentationMethod: f.companyRepresentationMethod,
          declarantName: f.declarantName,
          declarantPesel: f.declarantPesel,
          declarantStreet: f.declarantStreet,
          declarantCityLine: f.declarantCityLine,
          declarantDocument: f.declarantDocument,
          declarant: {
            name: f.declarantName,
            firstName: String(f.declarantName || '').trim().split(/\s+/)[0] || '',
            lastName: String(f.declarantName || '').trim().split(/\s+/).slice(1).join(' '),
            pesel: f.declarantPesel,
            birthDate: f.declarantBirthDate,
            birthPlace: f.declarantBirthPlace,
            street: f.declarantStreet,
            cityLine: f.declarantCityLine,
            address: [f.declarantStreet, f.declarantCityLine].filter(Boolean).join(', '),
            document: f.declarantDocument,
            position: f.declarantPosition,
            email: f.declarantEmail,
          },
          principal: {
            name: f.principalRep || f.declarantName,
            address: f.principalAddress || f.declarantStreet,
            pesel: f.principalPesel || f.declarantPesel,
            addressPesel: [
              f.principalAddress || f.declarantStreet,
              `PESEL: ${f.principalPesel || f.declarantPesel || ''}`,
            ].filter(Boolean).join(', '),
          },
          attorney: {
            name: f.attorneyName || personName,
            address: f.attorneyAddress,
            pesel: f.attorneyPesel,
            relationship: f.attorneyRelationship,
            addressRelationship: [
              f.attorneyAddress,
              f.attorneyRelationship ? `pokrewieństwo: ${f.attorneyRelationship}` : '',
            ].filter(Boolean).join(', '),
          },
          matterDescription,
          validUntil: f.validUntil,
          vehicles: this.showVehicleFields ? vehicleRows : [],
          drivers: driverRows,
          driversCount: type === 'BYDGOSZCZ_TAXI_LICENSE_CHANGE' && (this.wizardIntent === 'REMOVE_DRIVER' || !this.showDriverFields) ? '' : driverRows.length,
          vehiclesCount: this.isRemoval ? (type === 'BYDGOSZCZ_TAXI_LICENSE_CHANGE' ? '' : f.vehiclesTotalAfterChange) : (this.showVehicleFields ? vehicleRows.length : 0),
          driversRemovedCount: this.wizardIntent === 'REMOVE_DRIVER' ? driverRows.length : '',
          vehiclesRemovedCount: this.wizardIntent === 'REMOVE_VEHICLE' ? vehicleRows.length : '',
          flags: {
            application: true,
            taxi: true,
            legalEntity: isLegalEntity,
            newLicense: this.isNewLicense,
            licenseChange: this.isLicenseChange,
            addVehicle: !this.isRemoval && (f.officialProcedure === 'ADD_VEHICLE' || (this.isLicenseChange && f.changeIncludesVehicle)),
            additionalExtract: f.officialProcedure === 'ADDITIONAL_EXTRACT',
            procedureNewLicense: this.isNewLicense,
            procedureLicenseChange: this.isLicenseChange,
            // Zgłoszenie pojazdu w ramach zmiany licencji też jest aktualizacją
            // wykazu pojazdów — bez tego przy „dodaj kierowcę i auto" kratka
            // przy pkt III zostawała pusta (uwagi z testów 01.09.2026).
            driversRemoved: this.wizardIntent === 'REMOVE_DRIVER',
            vehiclesRemoved: this.wizardIntent === 'REMOVE_VEHICLE',
            procedureAddVehicle: !this.isRemoval && (f.officialProcedure === 'ADD_VEHICLE'
              || (this.isLicenseChange && f.changeIncludesVehicle))
              && !f.vehicleIsReplacement,
            procedureAdditionalExtract: f.officialProcedure === 'ADDITIONAL_EXTRACT',
            procedureChangeVehicle: f.officialProcedure === 'ADD_VEHICLE' && f.vehicleIsReplacement,
            procedureChangedExtract: f.procedureChangedExtract,
            procedureDuplicateExtract: f.procedureDuplicateExtract,
            licenseNumberProvided: (!this.isNewLicense || formRequiresLicenseNumber)
              && Boolean(String(f.licenseNumber || '').trim()),
            requestAdditionalExtract: f.officialProcedure === 'ADD_VEHICLE'
              || f.officialProcedure === 'ADDITIONAL_EXTRACT',
            registryCeidg: !isRegisteredInKrs,
            registryKrs: isRegisteredInKrs,
            krs: isRegisteredInKrs,
            roleSoleTrader: isSoleTrader,
            roleLegalPersonBoard: !isSoleTrader && !isPartnership,
            rolePartnershipManager: isPartnership,
            hasDrivers: f.hasDrivers && this.wizardIntent !== 'REMOVE_DRIVER',
            employedDrivers: f.hasDrivers,
            ownerDrives: f.ownerDrives,
            driverRegistration: !this.isRemoval && this.needsDriverDetails && (f.hasDrivers || f.ownerDrives),
            consent: f.consentPersonalData,
            consentContact: f.consentContact,
            euDriver: f.driverIsEuCitizen,
            driverDocuments: Boolean(
              f.attachmentFlags.attachmentDrivingLicense
              && f.attachmentFlags.attachmentMedical
              && f.attachmentFlags.attachmentPsychological
              && f.attachmentFlags.attachmentCriminalRecord
            ),
            goodReputation: f.attachmentFlags.attachmentGoodRepute,
            vehicleDocuments: Boolean(
              f.attachmentFlags.attachmentRegistrationDocument
              && f.attachmentFlags.attachmentLegalTitle
            ),
            passengerCar: true,
            transportTaxi: true,
            transportPassengerCar: false,
            transportVehicle7to9: false,
            vehiclePassengerCar: true,
            vehicleOther: false,
            vehicleTechnicalInspection: f.vehicleTechnicalInspection,
            vehicleHasTaximeter: f.vehicleHasTaximeter,
            vehicleUsesApp: f.vehicleUsesApp,
            // Kratka „inne" na wniosku o zmianę (Jelenia Góra) — zaznacza się
            // dokładnie wtedy, gdy jest co w tej rubryce wydrukować.
            changeOther: Boolean(opisInnejZmiany),
            period2To15: this.isNewLicense && f.licensePeriod === '2-15',
            period16To30: this.isNewLicense && f.licensePeriod === '16-30',
            period31To50: this.isNewLicense && f.licensePeriod === '31-50',
            changeAddress: f.changeFlags.changeCompanyAddress,
            personalCollection: f.personalCollection,
            ...f.changeFlags,
            ...f.attachmentFlags,
            attachmentGoodRepute: f.attachmentFlags.attachmentGoodRepute || this.automaticAttachmentFlags.attachmentGoodRepute,
            attachmentDriversList: this.wizardIntent !== 'REMOVE_DRIVER' && (f.attachmentFlags.attachmentDriversList || this.automaticAttachmentFlags.attachmentDriversList),
            attachmentVehiclesList: this.wizardIntent !== 'REMOVE_VEHICLE' && (f.attachmentFlags.attachmentVehiclesList || this.automaticAttachmentFlags.attachmentVehiclesList),
            viaAttorney: this.form.docKind === 'BOTH',
          },
        };
      }
      return {
        ...base,
        displayName: this.isRemoval && this.cityTemplates.length ? (this.wizardIntent === 'REMOVE_DRIVER' ? 'Wykaz kierowców do wykreślenia' : 'Wykaz pojazdów do wykreślenia') : this.wizardIntentLabel,
        asRemovalAttachment: this.isRemoval && this.cityTemplates.length > 0,
        wizardIntent: this.wizardIntent,
        changeDescription: f.changeDescription,
        drivers: driverRows,
        licenseArea: f.licenseArea,
        licensePeriod: f.licensePeriod,
        copiesCount: f.copiesCount,
        viaAttorney: this.form.docKind === 'BOTH',
        vehicles: this.showVehicleFields ? vehicleRows : [],
      };
    },
    validateCommon() {
      if (this.catalogLoading && this.needsWniosek) {
        Swal.fire('Jeszcze chwila', 'Trwa pobieranie katalogu formularzy urzędowych.', 'info');
        return false;
      }
      if (this.catalogError && this.needsWniosek) {
        Swal.fire('Katalog niedostępny', 'Odśwież katalog formularzy przed generowaniem dokumentów.', 'error');
        return false;
      }
      if (this.pendingReadinessItems.length) {
        Swal.fire('Uzupełnij dane', this.pendingReadinessItems.map((item) => item.value).join('\n'), 'info');
        return false;
      }
      return this.isPackageReady;
    },
    async preview() {
      if (this.generating || this.previewing || !this.validateCommon()) return;
      // Podgląd: dla miast z miejskim szablonem pokazujemy wypełniony druk.
      const type = this.needsWniosek && this.cityTemplates.length
        ? this.cityTemplates[0].type
        : this.needsPelnomocnictwo ? 'PELNOMOCNICTWO' : 'URZAD_WYPIS_WNIOSEK';
      const popup = window.open('about:blank', '_blank');
      this.previewing = true;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'document-generator/preview',
          data: { type, data: this.buildData(type) },
          responseType: 'blob',
          ...this.getConfig(),
        });
        this.openBlobInNewTab(res.data, popup);
      } catch (e) {
        if (popup && !popup.closed) popup.close();
        console.error(e);
        Swal.fire('Błąd', 'Nie udało się wygenerować podglądu', 'error');
      } finally {
        this.previewing = false;
      }
    },
    async generate() {
      if (this.generating || this.previewing || !this.validateCommon()) return;
      this.generating = true;
      try {
        const types = this.generationTypes;
        // Komplet zawsze ląduje w historii; driverId tylko gdy wybrano
        // kierowcę z systemu — wtedy dokumenty trafiają też do jego aplikacji.
        const recipientId = this.form.person ? this.form.person.id : null;
        // Migawka formularza jedzie z każdym dokumentem — dzięki temu można
        // wrócić do gotowego wniosku i dopisać jedną rzecz, zamiast wypełniać
        // wszystko od nowa. Kopiujemy, żeby późniejsza edycja formularza nie
        // zmieniła tego, co już poszło na serwer.
        const snapshot = JSON.parse(JSON.stringify(this.form));
        const documents = types.map((type) => ({
          type,
          caseType: 'URZAD',
          ...(recipientId ? { driverId: recipientId } : {}),
          data: this.buildData(type),
          formSnapshot: snapshot,
        }));
        // Oryginalne druki wybranego urzędu — dołączamy do kompletu.
        const cityForms = ((this.selectedRegistryEntry && this.selectedRegistryEntry.forms) || [])
          .filter((form) => form.fileId);
        for (const f of cityForms) {
          documents.push({
            type: 'URZAD_DRUK',
            caseType: 'URZAD',
            ...(recipientId ? { driverId: recipientId } : {}),
            data: {
              sourceFileId: f.fileId,
              formName: f.name,
              personName: recipientId ? this.form.person.text : 'Biuro BTP (dokument firmowy)',
              officeName: this.form.officeName,
              officeCity: this.form.officeCity,
            },
          });
        }
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'document-generator/batch',
          data: { documents },
          ...this.getConfig(),
        });
        const created = res.data?.data || res.data;
        if (Array.isArray(created) && created.length) {
          this.generatedDocs = created;
          this.loadDocs();
        } else {
          await this.loadDocs();
          throw new Error('Serwer nie zwrócił identyfikatorów kompletu. Sprawdź historię przed ponownym generowaniem.');
        }
        this.wizardStep = 5;
        this.wizardMaxStep = 5;
        Swal.fire({
          icon: 'success',
          title: 'Dokumenty wygenerowane',
          text: recipientId
            ? `${this.form.person.text} dostał(a) też powiadomienie w aplikacji BTP.`
            : 'Komplet zapisany — możesz go wydrukować albo wysłać e-mailem.',
          timer: 2600,
          showConfirmButton: false,
        });
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', e.response?.data?.message || String(e), 'error');
      } finally {
        this.generating = false;
      }
    },
    /**
     * Czy da się wrócić do tego wniosku. Dokumenty sprzed 29.09.2026 nie mają
     * migawki formularza, a druk urzędu to gotowy plik — tam nie ma czego edytować.
     */
    mozeEdytowac(item) {
      return !!(item && item.formSnapshot && item.type !== 'URZAD_DRUK');
    },

    /**
     * Wczytanie gotowego wniosku z powrotem do kreatora.
     *
     * Odtwarzamy FORMULARZ z migawki, a nie dane szablonu — `buildData()` jest
     * jednokierunkowe i odtwarzanie z niego rozjechałoby się przy pierwszej
     * zmianie mapowania. Stary dokument ZOSTAJE w historii: mógł już pójść do
     * urzędu, więc podmiana pod nim byłaby gorsza niż druga pozycja na liście.
     */
    popraw(item) {
      if (!this.mozeEdytowac(item)) return;
      this.form = JSON.parse(JSON.stringify(item.formSnapshot));
      this.generatedDocs = [];
      this.activeWorkspace = 'generator';
      this.wizardStep = 4;
      this.wizardMaxStep = 4;
      this.$nextTick(() => {
        const el = this.$refs.officeRegistry;
        if (el && el.scrollIntoView) window.scrollTo({ top: 0, behavior: 'smooth' });
      });
      Swal.fire({
        icon: 'info',
        title: 'Wniosek wczytany',
        html:
          `Formularz wypełniony danymi z <b>${item.number || 'dokumentu'}</b>. ` +
          'Popraw, co trzeba, i wygeneruj ponownie.<br><br>' +
          '<small>Poprzednia wersja zostaje w historii — mogła już trafić do urzędu.</small>',
      });
    },

    async loadDocs() {
      this.docsLoading = true;
      this.docsError = '';
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'document-generator?caseType=URZAD',
          ...this.getConfig(),
        });
        this.docs = res.data?.data || res.data || [];
      } catch (e) {
        console.error(e);
        this.docsError = e.response?.data?.message || 'Sprawdź połączenie z API i spróbuj ponownie.';
      } finally {
        this.docsLoading = false;
      }
    },
    async openPdf(id) {
      let popup = null;
      try {
        const doc = [...this.generatedDocs, ...this.docs].find((d) => d.id === id);
        // Druk urzędu = zapisany plik (PDF/DOC) — pobieramy prosto z S3.
        if (doc && doc.type === 'URZAD_DRUK' && doc.fileId) {
          return this.downloadStoredForm({ fileId: doc.fileId, name: (doc.data && doc.data.formName) || 'druk' });
        }
        popup = window.open('about:blank', '_blank');
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'document-generator/' + id + '/preview',
          responseType: 'blob',
          ...this.getConfig(),
        });
        this.openBlobInNewTab(res.data, popup);
      } catch (e) {
        if (popup && !popup.closed) popup.close();
        console.error(e);
        Swal.fire('Błąd', 'Nie udało się otworzyć PDF', 'error');
      }
    },
    openBlobInNewTab(blob, popup) {
      const url = URL.createObjectURL(blob);
      if (popup && !popup.closed) popup.location.href = url;
      else {
        const link = document.createElement('a');
        link.href = url;
        link.download = 'dokumenty.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
        Swal.fire({ icon: 'info', title: 'PDF pobrany', text: 'Przeglądarka zablokowała nową kartę. Otwórz plik z pobranych.', timer: 3500, showConfirmButton: false });
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    },
    async downloadStoredForm(f) {
      try {
        const officialTemplate = Boolean(f.type);
        const res = await axios({
          method: 'get',
          url: officialTemplate
            ? process.env.VUE_APP_ROOT_API + 'document-generator/official-forms/' + encodeURIComponent(f.type) + '/source'
            : process.env.VUE_APP_ROOT_API + 'document-generator/official-source-files/' + encodeURIComponent(f.fileId),
          responseType: 'blob',
          ...this.getConfig(),
        });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        const extension = String(f.name || '').match(/\((PDF|DOCX|DOC)\)$/i);
        const cleanName = String(f.name || 'druk-urzedu').replace(/\s*\((PDF|DOCX|DOC)\)$/i, '');
        a.download = f.fileName || `${cleanName}.${(extension && extension[1].toLowerCase()) || 'pdf'}`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', 'Nie udało się pobrać druku', 'error');
      }
    },
  },
};
</script>

<style scoped>
/* ===== Kreator (nowy liniowy przepływ) ===== */
.odw { display: flex; flex-direction: column; gap: 14px; }
.odw-start {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 52px 24px;
  text-align: center;
}
.odw-start__icon {
  width: 68px;
  height: 68px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(111, 139, 255, 0.28);
  border-radius: 20px;
  background: rgba(111, 139, 255, 0.12);
}
.odw-start__icon .v-icon { color: #8ea7ff !important; }
.odw-start h2 { margin: 0; color: #f3f5fb; font-size: 24px; }
.odw-start p { max-width: 560px; margin: 0; color: #a9b3c6; font-size: 13.5px; line-height: 1.6; }
.odw-start__actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 8px; }
.odw-start__cta { min-height: 48px; padding: 12px 26px; font-size: 15px; }

.odw-progress {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
}
.odw-progress button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
  color: #97a2b8;
  font-family: inherit;
  font-size: 12.5px;
  cursor: pointer;
}
.odw-progress button:disabled { opacity: 0.45; cursor: not-allowed; }
.odw-progress button span {
  width: 21px;
  height: 21px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  font-size: 11px;
  font-weight: 700;
}
.odw-progress button.is-active { border-color: rgba(111, 139, 255, 0.5); background: rgba(111, 139, 255, 0.14); color: #cdd8ff; }
.odw-progress button.is-active span { background: #6f8bff; color: #fff; }
.odw-progress button.is-done { color: #82e7ae; }
.odw-progress button.is-done span { background: rgba(80, 216, 144, 0.2); color: #82e7ae; }
.odw-progress__cancel { margin-left: auto; }
.odw-progress__cancel .v-icon { color: inherit !important; }

.odw-step { display: flex; flex-direction: column; gap: 16px; padding: 22px; }
.odw-step__head h2 { margin: 0 0 4px; color: #f3f5fb; font-size: 20px; }
.odw-step__head p { margin: 0; color: #98a3b9; font-size: 13px; }
.odw-step__head--done { text-align: center; }
.odw-done-icon {
  width: 54px;
  height: 54px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  border-radius: 50%;
  background: rgba(80, 216, 144, 0.14);
}
.odw-done-icon .v-icon { color: #6ee7a7 !important; }

.odw-city-search { max-width: 340px; }
.odw-city-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 10px;
}
.odw-city-grid > button {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.025);
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.odw-city-grid > button:hover { border-color: rgba(111, 139, 255, 0.4); background: rgba(111, 139, 255, 0.07); }
.odw-city-grid > button.is-active { border-color: #6f8bff; background: rgba(111, 139, 255, 0.13); }
.odw-city-grid > button > span {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(111, 139, 255, 0.14);
  color: #9ab0ff;
  font-size: 13px;
  font-weight: 700;
}
.odw-city-grid > button strong { color: #eef1f8; font-size: 14.5px; }
.odw-city-grid > button small {
  display: -webkit-box;
  overflow: hidden;
  color: #8b96ab;
  font-size: 11px;
  line-height: 1.45;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.odw-city-grid > button em {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #7fdfa8;
  font-size: 10.5px;
  font-style: normal;
}
.odw-city-grid > button em .v-icon { color: #7fdfa8 !important; }

.odw-manual { border-top: 1px dashed rgba(255, 255, 255, 0.09); padding-top: 12px; }
.odw-manual__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  color: #97a2b8;
  font-family: inherit;
  font-size: 12.5px;
  cursor: pointer;
}
.odw-manual__toggle:hover { color: #c8d0df; }
.odw-manual__toggle .v-icon { color: inherit !important; }
.odw-manual__grid { margin-top: 12px; }
.odw-manual__go { display: flex; align-items: flex-end; }

.odw-intent-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}
.odw-intent-grid > button {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 9px;
  min-height: 140px;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.025);
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
}
.odw-intent-grid > button:hover { border-color: rgba(111, 139, 255, 0.45); transform: translateY(-2px); }
.odw-intent-grid > button.is-active { border-color: #6f8bff; background: rgba(111, 139, 255, 0.12); }
.odw-intent-grid > button > span {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 13px;
}
.odw-intent-grid > button > span.is-violet { background: rgba(166, 141, 255, 0.14); }
.odw-intent-grid > button > span.is-violet .v-icon { color: #b9a5ff !important; }
.odw-intent-grid > button > span.is-blue { background: rgba(111, 139, 255, 0.14); }
.odw-intent-grid > button > span.is-blue .v-icon { color: #8ea7ff !important; }
.odw-intent-grid > button > span.is-green { background: rgba(80, 216, 144, 0.13); }
.odw-intent-grid > button > span.is-green .v-icon { color: #6ee7a7 !important; }
.odw-intent-grid > button > span.is-amber { background: rgba(255, 189, 102, 0.13); }
.odw-intent-grid > button > span.is-amber .v-icon { color: #ffd08f !important; }
.odw-intent-grid > button > span.is-red { background: rgba(255, 120, 120, 0.13); }
.odw-intent-grid > button > span.is-red .v-icon { color: #ff9a9a !important; }
.odw-intent-grid > button strong { color: #eef1f8; font-size: 16px; }
.odw-intent-grid > button small { color: #8b96ab; font-size: 12px; line-height: 1.5; }

.odw-block { display: flex; flex-direction: column; gap: 12px; }
.odw-block--framed {
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.018);
}
.odw-forms-list { display: flex; flex-direction: column; gap: 6px; margin: 10px 0 14px; }
.odw-forms-list__row { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #d7deea; }
.odw-forms-list__lock { flex: 0 0 auto; }
.odw-forms-list__name { flex: 1 1 auto; min-width: 0; }
.odw-forms-list__tag { color: #929db0; font-size: 11.5px; }
.odw-block__head { display: flex; align-items: flex-start; gap: 11px; }
.odw-block__head--toggle { cursor: pointer; }
.odw-block__head--toggle > .v-icon { margin-left: auto; color: #8b96ab !important; }
.odw-block__head > span {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
}
.odw-block__head > span.is-blue { background: rgba(111, 139, 255, 0.13); }
.odw-block__head > span.is-blue .v-icon { color: #8ea7ff !important; }
.odw-block__head > span.is-green { background: rgba(80, 216, 144, 0.12); }
.odw-block__head > span.is-green .v-icon { color: #6ee7a7 !important; }
.odw-block__head > span.is-amber { background: rgba(255, 189, 102, 0.12); }
.odw-block__head > span.is-amber .v-icon { color: #ffd08f !important; }
.odw-block__head > span.is-violet { background: rgba(166, 141, 255, 0.13); }
.odw-block__head > span.is-violet .v-icon { color: #b9a5ff !important; }
.odw-block__head strong { display: block; color: #e8ecf4; font-size: 14px; }
.odw-block__head small { color: #8b96ab; font-size: 11.5px; line-height: 1.45; }
.odw-declarant { border-top: 1px dashed rgba(255, 255, 255, 0.09); padding-top: 12px; }
.odw-declarant__title { display: block; margin-bottom: 10px; color: #d9deea; font-size: 12.5px; }

.odw-step__nav {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  justify-content: flex-end;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 14px;
}
.odw-step__nav .od-button:first-child { margin-right: auto; }

.odw-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}
.odw-summary-fact {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
}
.odw-summary-fact small { color: #8b96ab; font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; }
.odw-summary-fact strong { color: #eef1f8; font-size: 13.5px; }
.odw-summary-fact span { color: #a9b3c6; font-size: 12px; }
.odw-pending li .v-icon { color: #ffbd66 !important; }

.odw-done-list { display: flex; flex-direction: column; gap: 8px; }
.odw-done-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 14px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
}
.odw-done-item > span {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(111, 139, 255, 0.12);
}
.odw-done-item > span .v-icon { color: #8ea7ff !important; }
.odw-done-item > span.is-violet { background: rgba(166, 141, 255, 0.13); }
.odw-done-item > span.is-violet .v-icon { color: #b9a5ff !important; }
.odw-done-item > span.is-green { background: rgba(80, 216, 144, 0.12); }
.odw-done-item > span.is-green .v-icon { color: #6ee7a7 !important; }
.odw-done-item > span.is-amber { background: rgba(255, 189, 102, 0.12); }
.odw-done-item > span.is-amber .v-icon { color: #ffd08f !important; }
.odw-done-item > div { flex: 1; min-width: 0; }
.odw-done-item strong { display: block; color: #e8ecf4; font-size: 13px; }
.odw-done-item small { color: #8b96ab; font-size: 11px; }
.odw-done-item > button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 11px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 9px;
  background: transparent;
  color: #c3ccdd;
  font-family: inherit;
  font-size: 11.5px;
  cursor: pointer;
}
.odw-done-item > button:hover:not(:disabled) { border-color: rgba(111, 139, 255, 0.45); color: #cdd8ff; }
.odw-done-item > button .v-icon { color: inherit !important; }
.odw-attorney { display: flex; flex-direction: column; gap: 13px; }
.odw-attorney > .od-button { align-self: flex-start; }
.odw-final-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 15px;
}

.odw-dialog { border-radius: 18px; }
.odw-dialog__card {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 18px;
  background: #202635;
  color: #e8ecf4;
}
.odw-dialog__card header {
  display: flex;
  align-items: center;
  gap: 11px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  padding: 16px 18px;
}
.odw-dialog__card header > span {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: rgba(111, 139, 255, 0.13);
}
.odw-dialog__card header > span .v-icon { color: #8ea7ff !important; }
.odw-dialog__card header small { color: #8b96ab; font-size: 10.5px; letter-spacing: 0.05em; text-transform: uppercase; }
.odw-dialog__card header h3 { margin: 0; color: #f3f5fb; font-size: 16px; }
.odw-dialog__card header > button {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #8b96ab;
  cursor: pointer;
}
.odw-dialog__card header > button .v-icon { color: inherit !important; }
.odw-dialog__body { display: flex; flex-direction: column; gap: 12px; padding: 16px 18px; }
.odw-dialog__hint { display: inline-flex; align-items: center; gap: 5px; color: #8b96ab; font-size: 11.5px; }
.odw-dialog__hint .v-icon { color: #8b96ab !important; }
.odw-dialog__card footer {
  display: flex;
  gap: 9px;
  justify-content: flex-end;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  padding: 14px 18px;
}

@media (max-width: 760px) {
  .odw-step { padding: 15px; }
  .odw-progress__cancel { order: -1; width: 100%; justify-content: center; margin-left: 0; }
  .odw-step__nav .od-button { flex: 1 1 auto; justify-content: center; }
  .odw-final-actions .od-button { flex: 1 1 100%; justify-content: center; }
}

.official-docs-page {
  --od-bg: #171b27;
  --od-panel: #202635;
  --od-panel-soft: #252c3d;
  --od-input: #191e2b;
  --od-border: rgba(255, 255, 255, 0.078);
  --od-text: #f5f7fc;
  --od-muted: #9ca8bd;
  --od-primary: #5b7cff;
  --od-primary-soft: rgba(91, 124, 255, 0.12);
  --od-green: #53d994;
  --od-amber: #ffbd66;
  --od-violet: #ad91ff;
  min-height: calc(100vh - 64px);
  margin: -24px;
  padding: 20px 24px 48px;
  color: var(--od-text);
  background:
    radial-gradient(circle at 82% -5%, rgba(91, 124, 255, 0.11), transparent 31%),
    radial-gradient(circle at 8% 55%, rgba(49, 180, 255, 0.045), transparent 27%),
    var(--od-bg);
}

.official-docs-page button,
.official-docs-page a { font: inherit; }
.official-docs-page button { border: 0; }

.od-hero,
.od-workspace-tabs,
.od-builder-layout,
.od-history,
.od-registry {
  width: 100%;
  max-width: 1440px;
  margin-right: auto;
  margin-left: auto;
}

.od-hero {
  position: relative;
  overflow: hidden;
  margin-bottom: 11px;
  padding: 18px 20px;
  border: 1px solid var(--od-border);
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(37, 44, 61, 0.98), rgba(27, 33, 47, 0.98));
  box-shadow: 0 20px 55px rgba(5, 8, 18, 0.2);
}
.od-hero::after {
  position: absolute;
  top: -160px;
  right: -80px;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: rgba(91, 124, 255, 0.13);
  content: '';
  pointer-events: none;
}
.od-hero__top,
.od-hero__identity,
.od-metrics article,
.od-section-head,
.od-selected-office__top,
.od-subsection__head,
.od-summary__card > header,
.od-package__item,
.od-inline-state,
.od-registry__head,
.od-registry-grid article > header,
.od-registry-grid footer,
.od-doc-card header,
.od-doc-facts span,
.od-person-cell,
.od-office-cell {
  display: flex;
  align-items: center;
}
.od-hero__top { position: relative; z-index: 1; justify-content: space-between; gap: 24px; }
.od-hero__identity { align-items: flex-start; gap: 15px; }
.od-hero__icon {
  display: grid;
  place-items: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(119, 147, 255, 0.24);
  border-radius: 14px;
  color: #91a7ff;
  background: linear-gradient(145deg, rgba(91, 124, 255, 0.2), rgba(91, 124, 255, 0.07));
  box-shadow: inset 0 1px rgba(255, 255, 255, 0.08);
}
.od-hero__icon >>> .v-icon,
.od-metrics article > span >>> .v-icon,
.od-kind-card > span >>> .v-icon,
.od-kind-check,
.od-selected-office__top > span >>> .v-icon,
.od-subsection__head > span >>> .v-icon,
.od-summary__card > header > span >>> .v-icon,
.od-package__item > span >>> .v-icon,
.od-section-icon >>> .v-icon,
.od-inline-state > span >>> .v-icon,
.od-empty-state > span >>> .v-icon,
.od-registry-grid article > header > span + div + .v-icon { color: inherit !important; }
.od-eyebrow,
.od-section-eyebrow {
  display: block;
  color: #90a6ff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}
.od-hero h1 { margin: 3px 0 0; font-size: clamp(26px, 2.6vw, 31px); line-height: 1.08; letter-spacing: -0.025em; }
.od-hero p { max-width: 760px; margin: 6px 0 0; color: var(--od-muted); font-size: 13px; line-height: 1.45; }
.od-catalog-alert { position: relative; z-index: 1; display: flex; align-items: center; gap: 9px; margin-top: 15px; padding: 10px 12px; border: 1px solid rgba(255, 143, 143, 0.2); border-radius: 11px; color: #ffaaa8; background: rgba(255, 93, 93, 0.07); }
.od-catalog-alert >>> .v-icon { flex: 0 0 auto; color: inherit !important; }
.od-catalog-alert > span { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.od-catalog-alert strong { font-size: 11px; }
.od-catalog-alert small { margin-top: 2px; color: #c99a9d; font-size: 9.5px; }
.od-catalog-alert button { padding: 6px 9px; border: 1px solid rgba(255, 170, 168, 0.2) !important; border-radius: 8px; color: #ffd2d1; background: rgba(255, 255, 255, 0.035); cursor: pointer; font-size: 10px !important; font-weight: 750 !important; }

.od-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  gap: 8px;
  padding: 0 16px;
  border: 1px solid transparent !important;
  border-radius: 11px;
  cursor: pointer;
  font-size: 13px !important;
  font-weight: 750 !important;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, opacity 0.18s ease;
}
.od-button:hover:not(:disabled) { transform: translateY(-1px); }
.od-button:disabled { cursor: not-allowed; opacity: 0.45; }
.od-button >>> .v-icon { color: inherit !important; }
.od-button--ghost { border-color: var(--od-border) !important; color: #dce2ed; background: rgba(255, 255, 255, 0.035); }
.od-button--ghost:hover:not(:disabled) { border-color: rgba(126, 151, 255, 0.34) !important; background: rgba(91, 124, 255, 0.09); }
.od-button--primary { color: #fff; background: linear-gradient(135deg, #6685ff, #4f70f5); box-shadow: 0 10px 24px rgba(79, 112, 245, 0.24); }

.od-section-icon {
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  color: #91a7ff;
  background: rgba(91, 124, 255, 0.12);
}
.od-section-icon.is-green { color: #6fe2a4; background: rgba(83, 217, 148, 0.11); }
.od-section-icon.is-violet { color: #bea9ff; background: rgba(173, 145, 255, 0.11); }

.od-workspace-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 5px;
  margin-bottom: 12px;
  padding: 5px;
  border: 1px solid var(--od-border);
  border-radius: 16px;
  background: rgba(31, 37, 51, 0.92);
  box-shadow: 0 14px 36px rgba(5, 8, 18, 0.12);
}
.od-workspace-tabs > button {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  align-items: center;
  min-width: 0;
  min-height: 54px;
  gap: 10px;
  padding: 7px 11px;
  border-radius: 12px;
  color: #929db0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;
}
.od-workspace-tabs > button > span:first-child {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  color: #8fa4f4;
  background: rgba(91, 124, 255, 0.08);
}
.od-workspace-tabs > button > span:first-child >>> .v-icon { color: inherit !important; }
.od-workspace-tab__copy { display: flex; min-width: 0; flex-direction: column; }
.od-workspace-tabs strong { color: #d8deea; font-size: 13px; font-weight: 800; }
.od-workspace-tabs small { margin-top: 2px; overflow: hidden; color: #7f8a9e; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.od-workspace-tabs em {
  min-width: 30px;
  padding: 5px 8px;
  border-radius: 999px;
  color: #aebcf4;
  background: rgba(91, 124, 255, 0.09);
  font-size: 10px;
  font-style: normal;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  text-align: center;
}
.od-workspace-tabs > button.is-active {
  color: #cbd5ff;
  background: rgba(91, 124, 255, 0.12);
  box-shadow: inset 0 0 0 1px rgba(126, 151, 255, 0.15);
}
.od-workspace-tabs > button.is-active > span:first-child { color: #b6c3ff; background: rgba(91, 124, 255, 0.16); }
.od-workspace-tabs > button.is-active strong { color: #f4f6ff; }
.od-workspace-tabs > button:focus-visible,
.od-section-head--toggle:focus-visible,
.od-panel-next > button:focus-visible,
.od-refresh-action:focus-visible,
.od-registry-jump:focus-visible,
.od-kind-card:focus-visible,
.od-subsection__head:focus-visible,
.od-use-office:focus-visible,
.od-download-form:focus-visible {
  outline: 2px solid #91a7ff;
  outline-offset: 2px;
}

.od-builder-layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; align-items: start; gap: 12px; }
.od-builder { display: grid; min-width: 0; gap: 9px; scroll-margin-top: 14px; }
.od-panel {
  overflow: hidden;
  border: 1px solid var(--od-border);
  border-radius: 16px;
  background: rgba(31, 37, 51, 0.94);
  box-shadow: 0 18px 45px rgba(5, 8, 18, 0.13);
}
.od-builder > .od-panel { padding: 0; }
.od-section-head { align-items: flex-start; gap: 12px; margin-bottom: 16px; }
.od-section-head--toggle {
  width: 100%;
  min-height: 68px;
  margin: 0;
  padding: 13px 15px;
  color: inherit;
  background: transparent;
  cursor: pointer;
  text-align: left;
}
.od-step-panel.is-active > .od-section-head--toggle {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
  background: rgba(91, 124, 255, 0.035);
}
.od-section-copy { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.od-section-state {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 7px;
  align-self: center;
  color: #7f8ba0;
}
.od-section-state small { max-width: 190px; overflow: hidden; font-size: 10.5px; font-weight: 750; text-overflow: ellipsis; white-space: nowrap; }
.od-section-state >>> .v-icon { color: inherit !important; }
.od-step-panel.is-done .od-section-state { color: #65d99b; }
.od-step-panel.is-done .od-step { border-color: rgba(83, 217, 148, 0.25); color: #72e0a7; background: rgba(83, 217, 148, 0.09); }
.od-panel__content { padding: 15px 16px 16px; }
.od-panel-next {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding-top: 13px;
  border-top: 1px solid rgba(255, 255, 255, 0.055);
}
.od-panel-next > span { display: inline-flex; align-items: center; min-width: 0; gap: 6px; overflow: hidden; color: #8f9aaf; font-size: 11.5px; text-overflow: ellipsis; white-space: nowrap; }
.od-panel-next > span >>> .v-icon { flex: 0 0 auto; color: #8799dc !important; }
.od-panel-next > button { display: inline-flex; align-items: center; flex: 0 0 auto; min-height: 40px; gap: 7px; padding: 0 13px; border-radius: 11px; color: #e8edff; background: rgba(91, 124, 255, 0.15); cursor: pointer; font-size: 11.5px !important; font-weight: 800 !important; }
.od-panel-next > button:disabled { color: #737d91; background: rgba(255, 255, 255, 0.035); cursor: not-allowed; }
.od-panel-next > button >>> .v-icon { color: inherit !important; }
.od-step {
  display: grid;
  place-items: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(111, 139, 255, 0.24);
  border-radius: 11px;
  color: #9cafef;
  background: rgba(91, 124, 255, 0.09);
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.08em;
}
.od-section-head h2,
.od-section-title { margin: 2px 0 0; color: var(--od-text); font-size: 18px; font-weight: 700; letter-spacing: -0.015em; line-height: 1.25; }
.od-section-head p,
.od-section-description { margin: 4px 0 0; color: var(--od-muted); font-size: 12.5px; line-height: 1.45; }
.od-section-head--plain { margin: 0; }

.od-kind-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-bottom: 14px; }
.od-kind-grid.od-kind-grid--recipient { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.od-kind-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  min-width: 0;
  gap: 10px;
  padding: 13px;
  border: 1px solid rgba(255, 255, 255, 0.065) !important;
  border-radius: 13px;
  color: #adb7c9;
  background: rgba(11, 15, 25, 0.22);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
}
.od-kind-card:hover { transform: translateY(-1px); border-color: rgba(111, 139, 255, 0.26) !important; }
.od-kind-card.is-active { border-color: rgba(111, 139, 255, 0.42) !important; color: #b9c7ff; background: rgba(91, 124, 255, 0.11); box-shadow: inset 0 0 0 1px rgba(111, 139, 255, 0.05); }
.od-kind-card > span { display: grid; place-items: center; width: 34px; height: 34px; border-radius: 10px; color: inherit; background: rgba(255, 255, 255, 0.045); }
.od-kind-card > div { display: flex; min-width: 0; flex-direction: column; }
.od-kind-card strong { color: #e8ecf4; font-size: 13px; }
.od-kind-card small { margin-top: 2px; color: #929db0; font-size: 11.5px; line-height: 1.35; }
.od-kind-card.is-active small { color: #99a8c6; }
.od-kind-check { color: inherit !important; }

.od-field-block { min-width: 0; }
.od-field-label { display: block; margin: 0 0 6px 2px; color: #b4bdcc; font-size: 12px; font-weight: 700; }
.od-field-label b { color: #ff8f8f; }
.od-field-help { display: flex; align-items: center; gap: 5px; margin: 7px 0 0 3px; color: #929db0; font-size: 11.5px; }
.od-field-help >>> .v-icon { color: #8194d9 !important; }
.official-docs-page >>> .od-control .v-input__slot {
  min-height: 43px !important;
  border-radius: 11px !important;
  background: rgba(12, 16, 27, 0.3) !important;
  box-shadow: none !important;
}
.official-docs-page >>> .od-control fieldset { border-color: rgba(255, 255, 255, 0.09) !important; }
.official-docs-page >>> .od-control.v-input--is-focused fieldset { border-color: rgba(111, 139, 255, 0.72) !important; }
.official-docs-page >>> .od-control input,
.official-docs-page >>> .od-control textarea,
.official-docs-page >>> .od-control .v-select__selection { color: var(--od-text) !important; font-size: 13.5px; }
.official-docs-page >>> .od-control input::placeholder { color: #6f7a8f !important; opacity: 1; }
.official-docs-page >>> .od-control .v-icon { color: #7f8ca4 !important; }
.official-docs-page >>> .od-control--uppercase input { text-transform: uppercase; }

.od-office-picker { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: end; gap: 10px; }
.od-registry-jump {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 43px;
  gap: 6px;
  padding: 0 13px;
  border: 1px solid var(--od-border) !important;
  border-radius: 11px;
  color: #a8b4c8;
  background: rgba(255, 255, 255, 0.025);
  cursor: pointer;
  font-size: 11.5px !important;
  font-weight: 700 !important;
}
.od-registry-jump >>> .v-icon { color: #8298e8 !important; }
.od-selected-office { margin-top: 13px; padding: 14px; border: 1px solid rgba(83, 217, 148, 0.16); border-radius: 14px; background: rgba(83, 217, 148, 0.05); }
.od-selected-office__top { gap: 10px; }
.od-selected-office__top > span { display: grid; place-items: center; width: 34px; height: 34px; border-radius: 10px; color: #70e1a5; background: rgba(83, 217, 148, 0.11); }
.od-selected-office__top > div { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.od-selected-office__top small { color: #8daf9e; font-size: 10.5px; font-weight: 750; letter-spacing: 0.06em; text-transform: uppercase; }
.od-selected-office__top strong { margin-top: 2px; color: #e9f3ee; font-size: 13px; line-height: 1.35; }
.od-selected-office__top a { display: inline-flex; align-items: center; gap: 5px; color: #83e4af; font-size: 11px; font-weight: 750; text-decoration: none; }
.od-selected-office__top a >>> .v-icon { color: inherit !important; }
.od-selected-office > p { margin: 10px 0 0 44px; color: #aab4c5; font-size: 12px; line-height: 1.45; }
.od-source-warning { display: flex; align-items: flex-start; gap: 8px; margin: 10px 0 0 44px; padding: 9px 10px; border: 1px solid rgba(255, 189, 102, 0.22); border-radius: 10px; color: #ffd08a; background: rgba(255, 189, 102, 0.07); }
.od-source-warning >>> .v-icon { flex: 0 0 auto; color: inherit !important; }
.od-source-warning > span { display: flex; flex-direction: column; }
.od-source-warning strong { font-size: 10.5px; }
.od-source-warning small { margin-top: 2px; color: #c2ad8e; font-size: 9.5px; line-height: 1.4; }
.od-procedure-picker { margin: 12px 0 0 44px; }
.od-procedure-picker > .od-source-forms__label { margin-bottom: 7px; }
.od-procedure-picker > div { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; }
.od-procedure-picker button { display: flex; align-items: center; min-width: 0; gap: 8px; padding: 9px 10px; border: 1px solid rgba(255, 255, 255, 0.065) !important; border-radius: 10px; color: #8996aa; background: rgba(11, 15, 25, 0.2); cursor: pointer; text-align: left; }
.od-procedure-picker button.is-active { border-color: rgba(83, 217, 148, 0.24) !important; color: #73dfa7; background: rgba(83, 217, 148, 0.075); }
.od-procedure-picker button >>> .v-icon { flex: 0 0 auto; color: inherit !important; }
.od-procedure-picker button > span { display: flex; min-width: 0; flex-direction: column; }
.od-procedure-picker button strong { color: #e4eaf2; font-size: 12px; }
.od-procedure-picker button small { margin-top: 2px; color: #929db0; font-size: 10.5px; line-height: 1.35; }
.od-conditional-forms { display: grid; gap: 7px; margin: 12px 0 0 44px; }
.od-conditional-forms > label { display: flex; align-items: center; gap: 5px; padding: 8px 10px; border: 1px solid rgba(255, 255, 255, 0.065); border-radius: 10px; background: rgba(11, 15, 25, 0.2); cursor: pointer; }
.od-conditional-forms > label.is-active { border-color: rgba(83, 217, 148, 0.24); background: rgba(83, 217, 148, 0.075); }
.od-conditional-forms > label >>> .v-input { flex: 0 0 auto; margin: 0; padding: 0; }
.od-conditional-forms > label > span { display: flex; min-width: 0; flex-direction: column; }
.od-conditional-forms strong { color: #e4eaf2; font-size: 10.5px; }
.od-conditional-forms small { margin-top: 2px; color: #7d899d; font-size: 9px; }
.od-attachment-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 7px; }
.od-attachment-grid > label { display: flex; align-items: center; min-width: 0; gap: 3px; padding: 7px 9px; border: 1px solid rgba(255, 255, 255, 0.065); border-radius: 10px; color: #8996aa; background: rgba(11, 15, 25, 0.2); cursor: pointer; }
.od-attachment-grid > label.is-active { border-color: rgba(83, 217, 148, 0.22); color: #cfe9da; background: rgba(83, 217, 148, 0.065); }
.od-attachment-grid > label >>> .v-input { flex: 0 0 auto; margin: 0; padding: 0; }
.od-attachment-grid > label > span { overflow: hidden; font-size: 10.5px; text-overflow: ellipsis; white-space: nowrap; }
.od-vehicle-flags { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 10px; }
.od-vehicle-flags > label { display: flex; align-items: center; gap: 3px; padding: 6px 9px; border: 1px solid rgba(255, 255, 255, 0.065); border-radius: 9px; color: #8996aa; background: rgba(11, 15, 25, 0.2); cursor: pointer; }
.od-vehicle-flags > label.is-active { border-color: rgba(83, 217, 148, 0.22); color: #cfe9da; background: rgba(83, 217, 148, 0.065); }
.od-vehicle-flags > label >>> .v-input { margin: 0; padding: 0; }
.od-vehicle-flags > label > span { font-size: 10px; }
.od-source-forms { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; margin: 11px 0 0 44px; }
.od-source-forms__label { display: inline-flex; align-items: center; gap: 5px; color: #bdc7d6; font-size: 10.5px; font-weight: 750; }
.od-source-forms__label >>> .v-icon { color: #ffd08a !important; }
.od-source-forms button { display: inline-flex; align-items: center; gap: 5px; max-width: 320px; padding: 6px 9px; overflow: hidden; border: 1px solid rgba(255, 189, 102, 0.15) !important; border-radius: 8px; color: #ffd08a; background: rgba(255, 189, 102, 0.07); cursor: pointer; font-size: 10px !important; font-weight: 700 !important; text-overflow: ellipsis; white-space: nowrap; }
.od-source-forms button >>> .v-icon { color: inherit !important; }
.od-source-forms > small { flex-basis: 100%; color: #78859a; font-size: 10px; }

.od-form-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 11px 10px; margin-top: 14px; }
.od-field-block--wide { grid-column: span 2; }
.od-field-block--full { grid-column: 1 / -1; }
.od-change-vehicle-toggle { display: flex; align-items: center; gap: 12px; margin-top: 14px; padding: 9px 12px; border: 1px solid rgba(83, 217, 148, 0.14); border-radius: 11px; background: rgba(83, 217, 148, 0.045); }
.od-change-vehicle-toggle >>> .v-input { flex: 0 0 auto; margin: 0; padding: 0; }
.od-change-vehicle-toggle >>> .v-label { color: #dce6e1 !important; font-size: 11px; font-weight: 700; }
.od-change-vehicle-toggle > small { color: #849389; font-size: 9.5px; line-height: 1.4; }
.od-vehicle-source-box { display: grid; grid-template-columns: minmax(190px, .7fr) auto minmax(260px, 1.3fr); align-items: center; gap: 12px; margin: 14px 0; padding: 13px; border: 1px solid rgba(83, 217, 148, 0.18); border-radius: 12px; background: rgba(83, 217, 148, 0.055); }
.od-vehicle-source-box__head { display: flex; flex-direction: column; gap: 2px; }
.od-vehicle-source-box__head strong { color: #eef6f1; font-size: 11.5px; }
.od-vehicle-source-box__head small,
.od-vehicle-source-note { color: #8fa29a; font-size: 9.5px; line-height: 1.4; }
.od-vehicle-source-select { min-width: 0; }
.od-vehicle-source-toggle { flex: 0 0 auto; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 9px !important; background: rgba(8, 13, 23, 0.55) !important; }
.od-vehicle-source-toggle >>> .v-btn { min-height: 40px; padding: 0 11px !important; color: #9cabbc !important; background: transparent !important; font-size: 10px; font-weight: 750; letter-spacing: 0; text-transform: none; }
.od-vehicle-source-toggle >>> .v-btn--active { color: #b9f2d5 !important; background: rgba(83, 217, 148, 0.14) !important; }
.od-vehicle-source-note { grid-column: 3; margin-top: -7px; }
.od-inline-retry { grid-column: 3; justify-self: start; color: #ffbd66; cursor: pointer; font-size: 9.5px; text-align: left; }
.od-additional-vehicle-source { display: grid; grid-template-columns: auto minmax(220px, 1fr); align-items: center; gap: 10px; margin: 8px 0 13px; }
.od-subsection { overflow: hidden; padding: 0; border: 1px solid rgba(255, 255, 255, 0.058); border-radius: 15px; background: rgba(10, 14, 24, 0.17); }
.od-subsection.is-open { border-color: rgba(111, 139, 255, 0.14); }
.od-subsection + .od-subsection { margin-top: 12px; }
.od-subsection__head { align-items: center; width: 100%; min-height: 62px; gap: 10px; padding: 11px 13px; color: inherit; background: transparent; cursor: pointer; text-align: left; }
.od-subsection.is-open > .od-subsection__head { border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important; background: rgba(91, 124, 255, 0.025); }
.od-subsection__head > span:first-child { display: grid; place-items: center; flex: 0 0 34px; width: 34px; height: 34px; border-radius: 10px; }
.od-subsection__head > span:first-child.is-blue { color: #8fa6ff; background: rgba(91, 124, 255, 0.11); }
.od-subsection__head > span:first-child.is-green { color: #73dfa7; background: rgba(83, 217, 148, 0.1); }
.od-subsection__head > span:first-child.is-violet { color: #bea9ff; background: rgba(173, 145, 255, 0.11); }
.od-subsection__copy { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.od-subsection__head strong { color: #e9edf5; font-size: 13.5px; }
.od-subsection__head small { margin-top: 2px; color: #9aa5b7; font-size: 11.5px; line-height: 1.4; }
.od-subsection__chevron { flex: 0 0 auto; color: #7e8ba1 !important; }
.od-subsection__content { padding: 14px; }
.od-subsection__content > :first-child { margin-top: 0; }
.od-auto-badge { display: inline-flex; align-items: center; gap: 5px; max-width: 300px; padding: 5px 8px; border-radius: 999px; color: #73dfa7; background: rgba(83, 217, 148, 0.09); font-size: 9.5px; font-weight: 750; }
.od-subsection__head > .od-auto-badge { width: auto; height: auto; }
.od-auto-badge >>> .v-icon { color: inherit !important; }
.od-repeat-section { margin-top: 13px; padding-top: 13px; border-top: 1px solid rgba(255, 255, 255, 0.055); }
.od-repeat-section > header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.od-repeat-section > header > div { display: flex; flex-direction: column; }
.od-repeat-section > header strong { color: #dce2ec; font-size: 11px; }
.od-repeat-section > header small { margin-top: 2px; color: #788499; font-size: 9.5px; }
.od-repeat-add,
.od-repeat-card__title button { display: inline-flex; align-items: center; gap: 5px; padding: 7px 9px; border-radius: 8px; color: #aebdf5; background: rgba(91, 124, 255, 0.1); cursor: pointer; font-size: 9.5px !important; font-weight: 750 !important; }
.od-repeat-add:disabled { opacity: 0.45; cursor: default; }
.od-repeat-add >>> .v-icon,
.od-repeat-card__title button >>> .v-icon { color: inherit !important; }
.od-repeat-card { margin-top: 9px; padding: 11px; border: 1px solid rgba(111, 139, 255, 0.12); border-radius: 12px; background: rgba(91, 124, 255, 0.025); }
.od-repeat-card__title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.od-repeat-card__title > strong { color: #cfd7e6; font-size: 10.5px; }
.od-repeat-card__title button { color: #ffaaa8; background: rgba(255, 111, 105, 0.08); }
.od-driver-status { margin-bottom: 13px; }
.od-form-grid--city-extra { padding-top: 13px; border-top: 1px solid rgba(255, 255, 255, 0.055); }
.od-scope-block { margin-top: 14px; }
.od-scope-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-top: 14px; }
.od-scope-grid > label { display: flex; align-items: center; min-width: 0; gap: 4px; padding: 9px 10px; border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 11px; background: rgba(255, 255, 255, 0.018); cursor: pointer; }
.od-scope-grid > label.is-active { border-color: rgba(111, 139, 255, 0.24); background: rgba(91, 124, 255, 0.075); }
.od-scope-grid >>> .v-input { flex: 0 0 auto; margin: 0; padding: 0; }
.od-scope-grid > label > span { display: flex; min-width: 0; flex-direction: column; }
.od-scope-grid strong { color: #dce2ec; font-size: 11px; }
.od-scope-grid small { overflow: hidden; margin-top: 1px; color: #788499; font-size: 9.5px; text-overflow: ellipsis; white-space: nowrap; }

.od-summary { position: relative; min-width: 0; }
.od-summary__card { position: sticky; top: 14px; overflow: hidden; padding: 18px; border: 1px solid var(--od-border); border-radius: 19px; background: linear-gradient(155deg, rgba(37, 44, 61, 0.98), rgba(28, 34, 48, 0.98)); box-shadow: 0 18px 45px rgba(5, 8, 18, 0.17); }
.od-summary__card > header { gap: 10px; padding-bottom: 14px; border-bottom: 1px solid rgba(255, 255, 255, 0.065); }
.od-summary__card > header > span { display: grid; place-items: center; width: 36px; height: 36px; border-radius: 11px; color: #91a7ff; background: rgba(91, 124, 255, 0.12); }
.od-summary__card > header > div { display: flex; flex-direction: column; }
.od-summary__card > header small { color: #8692a6; font-size: 10px; font-weight: 750; letter-spacing: 0.08em; text-transform: uppercase; }
.od-summary__card > header h2 { margin: 1px 0 0; color: #f4f6fb; font-size: 18px; }
.od-progress { margin-top: 15px; }
.od-progress > div { display: flex; align-items: center; justify-content: space-between; color: #8e9aaf; font-size: 11.5px; }
.od-progress strong { color: #d8deea; }
.od-progress > i { display: block; height: 5px; margin-top: 7px; overflow: hidden; border-radius: 999px; background: rgba(255, 255, 255, 0.065); }
.od-progress > i > b { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #5b7cff, #7f9aff); transition: width 0.25s ease; }
.od-readiness-list { display: grid; gap: 7px; margin: 14px 0 0; padding: 0; list-style: none; }
.od-readiness-list li { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 8px; }
.od-readiness-list li > span { display: grid; place-items: center; width: 24px; height: 24px; border-radius: 8px; color: #7d8799; background: rgba(255, 255, 255, 0.045); }
.od-readiness-list li.is-done > span { color: #69dfa0; background: rgba(83, 217, 148, 0.1); }
.od-readiness-list li > span >>> .v-icon { color: inherit !important; }
.od-readiness-list li > div { display: flex; min-width: 0; flex-direction: column; }
.od-readiness-list strong { color: #cfd6e2; font-size: 11.5px; }
.od-readiness-list small { overflow: hidden; color: #778398; font-size: 10.5px; text-overflow: ellipsis; white-space: nowrap; }
.od-readiness-ready { display: grid; grid-template-columns: 32px minmax(0, 1fr); align-items: center; gap: 9px; margin-top: 14px; padding: 10px; border: 1px solid rgba(83, 217, 148, 0.15); border-radius: 11px; background: rgba(83, 217, 148, 0.055); }
.od-readiness-ready > span { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 10px; color: #70e1a5; background: rgba(83, 217, 148, 0.11); }
.od-readiness-ready > span >>> .v-icon { color: inherit !important; }
.od-readiness-ready > div { display: flex; min-width: 0; flex-direction: column; }
.od-readiness-ready strong { color: #ddefe5; font-size: 12px; }
.od-readiness-ready small { margin-top: 2px; color: #8eaa9b; font-size: 10.5px; line-height: 1.35; }
.od-package { margin-top: 17px; padding-top: 15px; border-top: 1px solid rgba(255, 255, 255, 0.065); }
.od-summary-label { display: block; margin-bottom: 9px; color: #8490a4; font-size: 9.5px; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase; }
.od-package__item { gap: 8px; padding: 7px 0; }
.od-package__item > span { display: grid; place-items: center; width: 29px; height: 29px; border-radius: 9px; }
.od-package__item > span.is-blue { color: #91a7ff; background: rgba(91, 124, 255, 0.1); }
.od-package__item > span.is-green { color: #72e0a7; background: rgba(83, 217, 148, 0.1); }
.od-package__item > span.is-violet { color: #bea9ff; background: rgba(173, 145, 255, 0.1); }
.od-package__item > span.is-amber { color: #ffd08a; background: rgba(255, 189, 102, 0.1); }
.od-package__item > div { display: flex; min-width: 0; flex-direction: column; }
.od-package__item strong { color: #dce2ec; font-size: 11.5px; }
.od-package__item small { overflow: hidden; margin-top: 1px; color: #778398; font-size: 10.5px; text-overflow: ellipsis; white-space: nowrap; }
.od-fee-note { display: flex; align-items: flex-start; gap: 8px; margin-top: 14px; padding: 10px; border: 1px solid rgba(255, 189, 102, 0.12); border-radius: 11px; color: #ffd08a; background: rgba(255, 189, 102, 0.055); }
.od-fee-note >>> .v-icon { flex: 0 0 auto; color: inherit !important; }
.od-fee-note p { margin: 0; color: #a89f8e; font-size: 10.5px; line-height: 1.45; }
.od-summary__actions { display: grid; grid-template-columns: 1fr; gap: 8px; margin-top: 15px; }
.od-summary__actions > small { display: flex; align-items: flex-start; gap: 5px; color: #8b96a9; font-size: 10.5px; line-height: 1.4; }
.od-summary__actions > small >>> .v-icon { flex: 0 0 auto; color: #ffbd66 !important; }

.od-history { margin-top: 0; }
.od-history__head,
.od-registry__head { display: flex; align-items: center; justify-content: space-between; gap: 22px; padding: 18px 20px; border-bottom: 1px solid var(--od-border); }
.od-history__filters { display: flex; align-items: center; gap: 8px; width: min(660px, 56%); }
.od-history-search { flex: 1 1 360px; }
.od-history-type { flex: 0 1 210px; }
.od-refresh-action { display: grid; place-items: center; flex: 0 0 43px; width: 43px; height: 43px; border: 1px solid var(--od-border) !important; border-radius: 11px; color: #9aacec; background: rgba(91, 124, 255, 0.07); cursor: pointer; }
.od-refresh-action:disabled { cursor: default; opacity: 0.5; }
.od-refresh-action >>> .v-icon { color: inherit !important; }
.od-refresh-action > span { display: none; }
.od-inline-state { gap: 12px; margin: 16px; padding: 14px; border-radius: 13px; }
.od-inline-state > span { display: grid; place-items: center; width: 38px; height: 38px; border-radius: 11px; }
.od-inline-state > div { min-width: 0; flex: 1; }
.od-inline-state strong { color: #edf0f7; font-size: 12.5px; }
.od-inline-state p { margin: 2px 0 0; color: #9aa5b8; font-size: 11px; }
.od-inline-state button { padding: 7px 10px; border-radius: 8px; cursor: pointer; font-size: 10.5px !important; font-weight: 750 !important; }
.od-inline-state--error { border: 1px solid rgba(255, 115, 115, 0.17); color: #ff9696; background: rgba(255, 115, 115, 0.055); }
.od-inline-state--error > span { background: rgba(255, 115, 115, 0.1); }
.od-inline-state--error button { color: #ffb0b0; background: rgba(255, 115, 115, 0.1); }
.od-empty-state { display: flex; align-items: center; justify-content: center; gap: 14px; min-height: 180px; padding: 32px; text-align: left; }
.od-empty-state > span { display: grid; place-items: center; width: 58px; height: 58px; border: 1px solid rgba(111, 139, 255, 0.15); border-radius: 17px; color: #8fa6ff; background: rgba(91, 124, 255, 0.065); }
.od-empty-state > div { display: flex; flex-direction: column; }
.od-empty-state strong { color: #e9edf5; font-size: 14px; }
.od-empty-state p { margin: 4px 0 0; color: #8793a7; font-size: 11.5px; }
.official-docs-page >>> .od-data-table { background: transparent !important; color: var(--od-text) !important; }
.official-docs-page >>> .od-data-table thead tr th { height: 40px; border-bottom: 1px solid var(--od-border) !important; color: #7f8ba0 !important; background: rgba(12, 16, 27, 0.16) !important; font-size: 10px !important; font-weight: 800 !important; letter-spacing: 0.06em; text-transform: uppercase; }
.official-docs-page >>> .od-data-table tbody tr { background: transparent !important; }
.official-docs-page >>> .od-data-table tbody tr:hover { background: rgba(91, 124, 255, 0.045) !important; }
.official-docs-page >>> .od-data-table tbody td { height: 58px; border-bottom: 1px solid rgba(255, 255, 255, 0.048) !important; color: #d9dfea !important; font-size: 12px; }
.official-docs-page >>> .od-data-table .v-data-footer { border-top: 1px solid var(--od-border) !important; color: #8f9aaf !important; }
.official-docs-page >>> .od-data-table .v-data-footer .v-icon { color: #8f9aaf !important; }
.od-date-cell { display: flex; flex-direction: column; }
.od-date-cell strong { color: #dbe1eb; font-size: 11.5px; }
.od-date-cell small { margin-top: 2px; color: #758196; font-size: 10.5px; }
.od-doc-type { display: inline-flex; align-items: center; max-width: 220px; gap: 5px; padding: 5px 8px; overflow: hidden; border-radius: 999px; font-size: 10.5px; font-weight: 750; text-overflow: ellipsis; white-space: nowrap; }
.od-doc-type >>> .v-icon { color: inherit !important; }
.od-doc-type.is-blue { color: #9eb1ff; background: rgba(91, 124, 255, 0.1); }
.od-doc-type.is-green { color: #76e3aa; background: rgba(83, 217, 148, 0.1); }
.od-doc-type.is-violet { color: #c0adff; background: rgba(173, 145, 255, 0.1); }
.od-doc-type.is-amber { color: #ffd08a; background: rgba(255, 189, 102, 0.1); }
.od-person-cell { gap: 7px; }
.od-person-cell > span { display: grid; place-items: center; width: 27px; height: 27px; border-radius: 8px; color: #aab9f2; background: rgba(91, 124, 255, 0.09); font-size: 9px; font-weight: 800; }
.od-person-cell strong { overflow: hidden; max-width: 150px; color: #dbe1eb; font-size: 11.5px; text-overflow: ellipsis; white-space: nowrap; }
.od-office-cell { gap: 5px; max-width: 360px; color: #aeb8c9; line-height: 1.35; }
.od-office-cell >>> .v-icon { flex: 0 0 auto; color: #7e91d7 !important; }
.od-table-action { display: inline-flex; align-items: center; gap: 5px; padding: 7px 9px; border: 1px solid rgba(111, 139, 255, 0.2) !important; border-radius: 8px; color: #a8b8f4; background: rgba(91, 124, 255, 0.07); cursor: pointer; font-size: 10px !important; font-weight: 750 !important; }
.od-table-action >>> .v-icon { color: inherit !important; }
.od-history-mobile { display: grid; gap: 8px; padding: 10px; }
.od-doc-card { padding: 13px; border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 13px; background: rgba(10, 14, 24, 0.17); }
.od-doc-card header { justify-content: space-between; gap: 10px; }
.od-doc-card time { color: #79859a; font-size: 9.5px; }
.od-doc-number { display: block; margin-top: 10px; color: #eef1f7; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; }
.od-doc-facts { display: grid; gap: 5px; margin-top: 9px; }
.od-doc-facts span { gap: 6px; color: #9ca7b9; font-size: 10.5px; }
.od-doc-facts span >>> .v-icon { color: #7f91d4 !important; }
.od-doc-card > button { display: inline-flex; align-items: center; justify-content: center; width: 100%; min-height: 38px; gap: 6px; margin-top: 11px; border-radius: 9px; color: #dce4ff; background: rgba(91, 124, 255, 0.13); cursor: pointer; font-size: 11px !important; font-weight: 750 !important; }
.od-doc-card > button >>> .v-icon { color: inherit !important; }

.od-registry { margin-top: 0; scroll-margin-top: 14px; }
.od-registry-search { width: min(390px, 42%); }
.od-registry-note { display: flex; align-items: flex-start; gap: 8px; margin: 14px 18px 0; padding: 10px 12px; border: 1px solid rgba(111, 139, 255, 0.12); border-radius: 11px; color: #92a7f5; background: rgba(91, 124, 255, 0.045); }
.od-registry-note >>> .v-icon { flex: 0 0 auto; color: inherit !important; }
.od-registry-note p { margin: 0; color: #8f9caf; font-size: 10.5px; line-height: 1.45; }
.od-registry-note strong { color: #b3c0ef; font-weight: 750; }
.od-registry-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 7px; padding: 12px 16px 16px; }
.od-registry-grid article { display: grid; grid-template-columns: 175px minmax(260px, 1.25fr) minmax(180px, 0.75fr) auto; grid-template-areas: "city office meta actions" "city note meta actions"; align-items: center; min-width: 0; min-height: 76px; gap: 2px 14px; padding: 10px 12px; border: 1px solid rgba(255, 255, 255, 0.058); border-radius: 13px; background: rgba(10, 14, 24, 0.17); transition: border-color 0.18s ease, transform 0.18s ease, background 0.18s ease; }
.od-registry-grid article:hover { transform: translateY(-1px); border-color: rgba(111, 139, 255, 0.17); }
.od-registry-grid article.is-selected { border-color: rgba(83, 217, 148, 0.25); background: rgba(83, 217, 148, 0.045); }
.od-registry-grid article > header { grid-area: city; gap: 8px; }
.od-registry-grid article > header > span { display: grid; place-items: center; width: 34px; height: 34px; border-radius: 10px; color: #9eb1f5; background: rgba(91, 124, 255, 0.09); font-size: 10px; font-weight: 850; }
.od-registry-grid article.is-selected > header > span { color: #73e1a8; background: rgba(83, 217, 148, 0.09); }
.od-registry-grid article > header > div { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.od-registry-grid article > header small { color: #707d91; font-size: 8.5px; font-weight: 750; letter-spacing: 0.07em; text-transform: uppercase; }
.od-registry-grid article > header h3 { margin: 1px 0 0; color: #eef1f6; font-size: 14px; }
.od-registry-grid article > header > .v-icon { color: #68dda0 !important; }
.od-registry-grid article > strong { grid-area: office; align-self: end; margin: 0; color: #c8d0dc; font-size: 12px; line-height: 1.35; }
.od-registry-grid article > p { grid-area: note; align-self: start; margin: 3px 0 0; overflow: hidden; color: #929db0; font-size: 10.5px; line-height: 1.35; text-overflow: ellipsis; white-space: nowrap; }
.od-registry-card__meta { grid-area: meta; display: flex; align-items: center; flex-wrap: wrap; gap: 5px; margin: 0; }
.od-registry-card__meta span { display: inline-flex; align-items: center; gap: 4px; padding: 4px 6px; border-radius: 7px; color: #8f9caf; background: rgba(255, 255, 255, 0.035); font-size: 10px; font-weight: 700; }
.od-registry-card__meta span >>> .v-icon { color: #7e91d8 !important; }
.od-registry-grid footer { grid-area: actions; justify-content: flex-end; flex-wrap: wrap; max-width: 270px; gap: 5px; margin: 0; padding: 0; border-top: 0; }
.od-registry-grid footer button,
.od-registry-grid footer a { display: inline-flex; align-items: center; min-height: 36px; gap: 4px; padding: 0 9px; border-radius: 9px; cursor: pointer; font-size: 10px !important; font-weight: 750 !important; text-decoration: none; }
.od-registry-grid footer button >>> .v-icon,
.od-registry-grid footer a >>> .v-icon { color: inherit !important; }
.od-use-office { color: #aebdf5; background: rgba(91, 124, 255, 0.1); }
.od-registry-grid article.is-selected .od-use-office { color: #76e2aa; background: rgba(83, 217, 148, 0.1); }
.od-registry-grid footer a { color: #aab4c6; background: rgba(255, 255, 255, 0.045); }
.od-download-form { color: #ffd08a; background: rgba(255, 189, 102, 0.08); }
.od-empty-state--compact { min-height: 150px; }

.is-spinning { animation: od-spin 0.85s linear infinite; }
@keyframes od-spin { to { transform: rotate(360deg); } }

@media (max-width: 1320px) {
  .od-builder-layout { grid-template-columns: minmax(0, 1fr) 300px; }
  .od-kind-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .od-kind-card { padding: 11px 12px; }
  .od-form-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .od-form-grid--office .od-field-block--wide { grid-column: span 2; }
}

@media (max-width: 1080px) {
  .od-builder-layout { grid-template-columns: 1fr; }
  .od-summary { order: -1; }
  .od-summary__card { position: static; }
  .od-summary__card { display: grid; grid-template-columns: minmax(0, 1fr) minmax(280px, 0.75fr); gap: 16px; }
  .od-summary__card > header,
  .od-progress,
  .od-readiness-list,
  .od-readiness-ready { grid-column: 1; }
  .od-package,
  .od-fee-note,
  .od-summary__actions { grid-column: 2; }
  .od-package { grid-row: 1 / span 3; margin-top: 0; padding-top: 0; padding-left: 16px; border-top: 0; border-left: 1px solid rgba(255, 255, 255, 0.065); }
  .od-fee-note { grid-row: 4; }
  .od-summary__actions { grid-row: 5; }
  .od-kind-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (max-width: 900px) {
  .od-form-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .od-field-block--wide { grid-column: span 2; }
  .od-scope-grid { grid-template-columns: 1fr; }
  .od-history__head,
  .od-registry__head { align-items: stretch; flex-direction: column; gap: 13px; }
  .od-history__filters,
  .od-registry-search { width: 100%; }
  .od-registry-grid article {
    grid-template-columns: minmax(170px, 0.55fr) minmax(0, 1fr);
    grid-template-areas: "city office" "note note" "meta actions";
    gap: 7px 14px;
  }
  .od-registry-grid article > strong { align-self: center; }
  .od-registry-grid article > p { margin-top: 0; white-space: normal; }
  .od-registry-grid footer { max-width: none; }
}

@media (max-width: 700px) {
  .official-docs-page { min-height: calc(100vh - 56px); margin: -16px; padding: 12px 10px 34px; }
  .od-hero { padding: 17px 15px; border-radius: 17px; }
  .od-hero__top { align-items: stretch; flex-direction: column; }
  .od-hero__identity { gap: 11px; }
  .od-hero__icon { flex-basis: 42px; width: 42px; height: 42px; }
  .od-hero h1 { font-size: 25px; }
  .od-hero p { font-size: 12.5px; }
  .od-hero__top > .od-button { align-self: flex-start; }
  .od-workspace-tabs { gap: 3px; padding: 4px; border-radius: 14px; }
  .od-workspace-tabs > button { grid-template-columns: 32px minmax(0, 1fr); min-height: 51px; gap: 7px; padding: 6px 7px; }
  .od-workspace-tabs > button > span:first-child { width: 32px; height: 32px; }
  .od-workspace-tabs em { grid-column: 1 / -1; display: none; }
  .od-workspace-tabs strong { font-size: 11.5px; }
  .od-workspace-tabs small { display: none; }
  .od-summary { order: 0; }
  .od-summary__card { display: block; padding: 15px; border-radius: 16px; }
  .od-package { margin-top: 15px; padding-top: 13px; padding-left: 0; border-top: 1px solid rgba(255, 255, 255, 0.065); border-left: 0; }
  .od-builder > .od-panel { padding: 0; border-radius: 15px; }
  .od-panel__content { padding: 12px; }
  .od-section-head--toggle { min-height: 62px; padding: 11px 12px; }
  .od-section-state small { display: none; }
  .od-panel-next { align-items: stretch; flex-direction: column; }
  .od-panel-next > span { white-space: normal; }
  .od-panel-next > button { justify-content: center; width: 100%; min-height: 43px; }
  .od-section-head { gap: 10px; }
  .od-step { flex-basis: 34px; width: 34px; height: 34px; }
  .od-section-head h2,
  .od-section-title { font-size: 16px; }
  .od-section-head p,
  .od-section-description { font-size: 11.5px; }
  .od-kind-grid,
  .od-kind-grid.od-kind-grid--recipient { grid-template-columns: 1fr; }
  .od-kind-card small { font-size: 10px; }
  .od-office-picker { grid-template-columns: 1fr; }
  .od-registry-jump { justify-self: start; min-height: 38px; }
  .od-selected-office__top { align-items: flex-start; flex-wrap: wrap; }
  .od-selected-office__top > div { flex-basis: calc(100% - 46px); }
  .od-selected-office__top a { margin-left: 44px; }
  .od-selected-office > p,
  .od-source-warning,
  .od-procedure-picker,
  .od-conditional-forms,
  .od-source-forms { margin-left: 0; }
  .od-procedure-picker > div { grid-template-columns: 1fr; }
  .od-attachment-grid { grid-template-columns: 1fr; }
  .od-change-vehicle-toggle { align-items: flex-start; flex-direction: column; }
  .od-vehicle-source-box { grid-template-columns: 1fr; }
  .od-vehicle-source-note,
  .od-inline-retry { grid-column: 1; margin-top: 0; }
  .od-vehicle-source-toggle { width: 100%; }
  .od-vehicle-source-toggle >>> .v-btn { flex: 1 1 50%; }
  .od-additional-vehicle-source { grid-template-columns: 1fr; }
  .od-form-grid { grid-template-columns: 1fr; }
  .od-field-block--wide,
  .od-form-grid--office .od-field-block--wide { grid-column: auto; }
  .od-subsection { padding: 0; }
  .od-subsection__head { align-items: center; padding: 10px 11px; }
  .od-subsection__head > .od-auto-badge { display: none; }
  .od-subsection__content { padding: 11px; }
  .od-repeat-section > header { align-items: flex-start; flex-direction: column; }
  .od-history__head,
  .od-registry__head { padding: 15px; }
  .od-history__filters { flex-direction: column; }
  .od-history-search,
  .od-history-type { width: 100%; flex: none; }
  .od-refresh-action { display: flex; justify-content: center; width: 100%; min-height: 43px; gap: 7px; }
  .od-refresh-action > span { display: inline; font-size: 11.5px; font-weight: 750; }
  .od-registry-grid { grid-template-columns: 1fr; padding: 11px; }
  .od-registry-note { margin: 11px 11px 0; }
  .od-registry-grid article { display: flex; min-height: 0; flex-direction: column; align-items: stretch; padding: 13px; }
  .od-registry-grid article > header { width: 100%; }
  .od-registry-grid article > strong { margin-top: 10px; }
  .od-registry-grid article > p { margin-top: 6px; white-space: normal; }
  .od-registry-card__meta { margin-top: 10px; }
  .od-registry-grid footer { justify-content: flex-start; max-width: none; margin-top: 11px; padding-top: 10px; border-top: 1px solid rgba(255, 255, 255, 0.05); }
}

@media (max-width: 390px) {
  .official-docs-page { padding-right: 8px; padding-left: 8px; }
  .od-hero__icon { display: none; }
  .od-workspace-tabs > button { grid-template-columns: 1fr; justify-items: center; text-align: center; }
  .od-workspace-tab__copy { align-items: center; }
  .od-section-head p,
  .od-section-description { display: none; }
  .od-selected-office { padding: 11px; }
  .od-source-forms button { max-width: 100%; }
  .od-summary__actions .od-button { width: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .od-button,
  .od-kind-card,
  .od-workspace-tabs > button,
  .od-registry-grid article,
  .od-progress > i > b { transition: none; }
  .is-spinning { animation-duration: 1.7s; }
}

/* Keep the wizard usable inside the office panel, including a narrow sidebar layout. */
.official-docs-page { min-width: 0; }
.odw, .odw-step, .odw-block, .odw-block__head > div, .od-hero__identity > div { min-width: 0; }
.odw-city-grid { grid-template-columns: repeat(auto-fit, minmax(min(100%, 230px), 1fr)); }
.odw-intent-grid { grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr)); }
.odw-summary-grid { grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr)); }
.odw .od-form-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.odw .od-field-block--wide, .odw .od-field-block--full { grid-column: 1 / -1; }
.odw .od-form-grid--vehicle { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.odw .od-form-grid--vehicle .od-field-block--wide { grid-column: span 2; }
.odw .od-control { width: 100%; min-width: 0; }
.odw .od-control >>> .v-input__slot { height: auto; }
.odw .od-control >>> .v-input__append-inner { flex-shrink: 0; }
.odw .od-control >>> .v-select__selections { min-width: 0; }
.odw .od-control >>> .v-select__selection { max-width: 100%; }
.odw .od-control >>> input { min-width: 0; }
.odw-help { padding: 14px 16px; border: 1px solid rgba(255,189,102,.25); border-radius: 12px; background: rgba(255,189,102,.06); color: #e3c69f; font-size: 13px; line-height: 1.6; }
.odw-help ul { margin: 6px 0 0; padding-left: 20px; }
.odw-pending small, .odw-package small { white-space: normal; overflow: visible; }
.odw-summary-fact, .odw-done-item strong, .odw-forms-list__name, .odw-help { overflow-wrap: anywhere; }
.odw-done-item { display: grid; grid-template-columns: 36px minmax(0, 1fr) auto auto; }
.odw-done-item > button { min-height: 40px; justify-content: center; }
.odw-forms-list__row > .v-input { flex: 0 0 28px; }
.odw-forms-list__tag { flex: 0 1 200px; }
.odw-block summary { cursor: pointer; color: #dce4f5; padding: 4px 0; }
.odw-block details[open] .od-attachment-grid { margin-top: 12px; }
.official-docs-page button:focus-visible, .odw-dialog__card button:focus-visible, .odw-block summary:focus-visible { outline: 2px solid #9eb1ff; outline-offset: 3px; }
.official-docs-page button:disabled { cursor: not-allowed; opacity: .5; }
.odw-dialog__card header > span { flex: 0 0 38px; }
.odw-dialog__card header > div { min-width: 0; }
.odw-dialog__card header > button { display: grid; place-items: center; flex: 0 0 40px; width: 40px; height: 40px; }
.odw-dialog__card .od-control >>> input, .odw-dialog__card .od-control >>> textarea { color: #e8ecf4; }
.od-registry-grid article { grid-template-columns: minmax(130px,.6fr) minmax(0,1.5fr) minmax(0,1fr); grid-template-areas: "city office meta" "city note meta" "actions actions actions"; }
.od-registry-grid footer { justify-content: flex-start; max-width: none; margin-top: 8px; }
.od-registry-grid footer button { max-width: 100%; height: auto; padding: 8px 10px; text-align: left; overflow-wrap: anywhere; }
.od-history__filters { min-width: 0; }
.od-history__filters .v-input { min-width: 0; }
@media (max-width: 1000px) {
  .odw .od-form-grid--vehicle { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .od-history__head { align-items: stretch; flex-direction: column; }
  .od-history__filters { width: 100%; }
}
@media (max-width: 700px) {
  .odw .od-form-grid, .odw .od-form-grid--vehicle { grid-template-columns: minmax(0, 1fr); }
  .odw .od-field-block--wide, .odw .od-field-block--full, .odw .od-form-grid--vehicle .od-field-block--wide { grid-column: auto; }
  .odw-step { padding: 14px; }
  .odw-block--framed { padding: 12px; }
  .odw-done-item { grid-template-columns: 36px minmax(0,1fr); gap: 9px; }
  .odw-done-item > button { grid-column: 1 / -1; }
  .odw-progress { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); }
  .odw-progress button { min-height: 44px; padding: 8px 10px; }
  .odw-progress__cancel { grid-column: 1 / -1; order: 1; }
  .od-workspace-tabs > button { grid-template-columns: 1fr; justify-items: center; text-align: center; }
  .od-workspace-tabs strong { overflow-wrap: anywhere; }
  .odw-forms-list__row { flex-wrap: wrap; }
  .odw-forms-list__name { flex: 1 1 calc(100% - 40px); }
  .odw-forms-list__tag { margin-left: 36px; flex-basis: 100%; }
  .odw-dialog__card header { padding: 12px; gap: 8px; }
  .odw-dialog__card header h3 { font-size: 15px; }
  .odw-dialog__card footer { flex-wrap: wrap; }
  .odw-dialog__card footer .od-button { flex: 1; }
}


.od-driver-details { border-top: 1px solid rgba(255,255,255,.08); padding-top: 10px; }
.od-driver-details summary { color: #acbad4; font-size: 12px; cursor: pointer; }
.od-driver-details[open] > .od-form-grid { margin-top: 16px; }

/* The dialog has its own scrolling body so its close/send controls stay visible. */
.odw-dialog__card { max-height: calc(100vh - 48px); overflow: hidden; }
.odw-dialog__card > header, .odw-dialog__card > footer { flex-shrink: 0; }
.odw-dialog__body { overflow-y: auto; min-height: 0; overscroll-behavior: contain; }
.odw-dialog__body >>> textarea { max-height: 280px; overflow-y: auto !important; }

.odw-help-text { color: #9aa9c0; font-size: 12px; line-height: 1.5; margin: 12px 0 0; }

/* Podpowiedź pod numerem licencji: skąd wartość / czemu pusto. */
.od-license-hint { margin: 6px 0 0; font-size: 12px; line-height: 1.35; color: #8E97B3; }
.od-license-hint--alarm { color: #FF8A80; font-weight: 600; }
</style>
